import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import api, { tradingAPI } from '../../services/api';
import UserGuide from './UserGuide';
import ChangesReport, { copyHtml } from './ChangesReport';
import { htmlToPngBlob, withPngDpi, copyPngToClipboard } from './pngClipboard';
import './TradingApp.css';

const fmt = (v, dp = 2) => (v == null || isNaN(v) ? '—' : Number(v).toFixed(dp));

// Shared "effective trade date" pill. A future effective date means the approved targets
// are HELD — they do NOT appear in the Dashboard, Portfolio Viewer or Lab until that day.
// Once the date is reached the targets go live everywhere. Renders nothing when unset
// (blank effective date = live as soon as approved).
function EffectiveDateBadge({ date, size }) {
  if (!date) return null;
  const today = new Date().toISOString().slice(0, 10);
  const held = date > today;
  const title = held
    ? `Trades held until ${date}. These targets will NOT appear in the Dashboard, Portfolio Viewer or Lab until ${date} — the strategy shows no approved target in the meantime.`
    : `Effective from ${date}. These targets are live in the Dashboard, Portfolio Viewer and Lab.`;
  return (
    <span className={`eff-badge ${held ? 'held' : 'live'}${size === 'sm' ? ' sm' : ''}`} title={title}>
      <span className="eff-badge-icon">{held ? '⏳' : '✓'}</span>
      <span className="eff-badge-label">{held ? 'Effective' : 'Live since'}</span>
      <strong>{date}</strong>
      {held && <span className="eff-badge-note">held until then</span>}
    </span>
  );
}

// Excel-style keyboard navigation for grid inputs: ↑/↓ move within the column,
// ←/→ across the row's inputs, Enter moves down. Also stops number inputs
// incrementing their value on ↑/↓.
function gridNav(e) {
  const DIR = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1], Enter: [1, 0] };
  const d = DIR[e.key];
  if (!d) return;
  const input = e.currentTarget;
  const td = input.closest('td');
  const tr = td && td.closest('tr');
  const table = tr && tr.closest('table');
  if (!table) return;
  e.preventDefault();
  const [dr, dc] = d;
  if (dc !== 0) {
    const rowInputs = Array.from(tr.querySelectorAll('input'));
    const next = rowInputs[rowInputs.indexOf(input) + dc];
    if (next) { next.focus(); if (next.select) next.select(); }
    return;
  }
  const colIndex = td.cellIndex;
  const rows = Array.from(table.querySelectorAll('tbody tr'));
  let r = rows.indexOf(tr);
  for (;;) {
    r += dr;
    if (r < 0 || r >= rows.length) return;
    const cell = rows[r].cells[colIndex];
    const target = cell && cell.querySelector('input');
    if (target) { target.focus(); if (target.select) target.select(); return; }
  }
}

const ASSET_CLASS_COLORS = {
  'Australian Equities': '#011E41', 'International Equities': '#5E819C', 'Global Equities': '#5E819C',
  'Real Estate': '#A6C6C1', 'Infrastructure': '#337B91', 'Cash': '#C9A227',
  'Government Bonds': '#F3BD48', 'Corporate Debt': '#AA8090', 'Private Debt': '#874564',
  'Private Equity': '#6C757D', 'Alternatives': '#9B59B6', 'Emerging Markets': '#34495E',
};
const acColour = (ac, i) => ASSET_CLASS_COLORS[ac]
  || ['#072040', '#f0a500', '#599BE3', '#91CF9B', '#874564', '#127080', '#9B59B6', '#E67E22'][i % 8];

// Canonical asset-class order (matches portfolio/api.py ASSET_CLASS_ORDER used by Portfolio Lab)
const AC_ORDER = [
  'Australian Equities', 'International Equities', 'EM Equities', 'Real Estate', 'Infrastructure',
  'Global Alpha - G', 'Global Alpha', 'Private Equity', 'Cash', 'Government Bonds', 'Corporate Debt',
  'Private Debt', 'Private Infrastructure', 'Global Alpha - D',
];
const acRank = (ac) => { const i = AC_ORDER.indexOf(ac); return i === -1 ? 999 : i; };

// Group a fund list by asset class → ordered array [[ac, rows], …] in canonical order
function groupByAssetClass(fundList) {
  const groups = {};
  fundList.forEach((r) => (groups[r.asset_class_group || 'Other'] = groups[r.asset_class_group || 'Other'] || []).push(r));
  Object.values(groups).forEach((g) => g.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
  return Object.entries(groups).sort((a, b) => {
    const ra = acRank(a[0]); const rb = acRank(b[0]);
    return ra !== rb ? ra - rb : a[0].localeCompare(b[0]);
  });
}

// Excel-style multi-cell paste: given a paste event on a grid input, map the clipboard's
// lines onto the same-column inputs walking DOWN the table (mirrors gridNav's vertical
// walk). Returns [{pc, key, val}] or null when it's a plain single-value paste.
function gridPasteTargets(e) {
  const text = (e.clipboardData && e.clipboardData.getData('text')) || '';
  const values = text.split(/\r?\n/).map((s) => s.split('\t')[0].trim()).filter((s) => s !== '');
  if (values.length < 2) return null;
  const input = e.currentTarget;
  const td = input.closest('td');
  const tr = td && td.closest('tr');
  const table = tr && tr.closest('table');
  if (!table) return null;
  e.preventDefault();
  const colIndex = td.cellIndex;
  const bodyRows = Array.from(table.querySelectorAll('tbody tr'));
  let r = bodyRows.indexOf(tr);
  const out = [];
  for (const val of values) {
    let target = null;
    while (r < bodyRows.length && !target) {
      const cell = bodyRows[r].cells[colIndex];
      target = cell ? cell.querySelector('input[data-pc]') : null;
      r += 1;
    }
    if (!target) break;
    out.push({ pc: target.dataset.pc, key: target.dataset.key, val });
  }
  return out;
}

// Synthetic tilt-row key for the Currency (FX) overlay — an exposure that cuts across
// asset classes, shown as its own tilt row rather than under a single asset class.
const CURRENCY_TILT_KEY = '__currency__';

/* ─────── Implied SAA tilt bar: diverging horizontal bar (proposed weight − SAA neutral).
   Bar colour = asset-class colour; direction (right/left of centre) = over/under. ─────── */
function TiltBar({ value, max, color, suffix = '% vs SAA' }) {
  const v = Number(value) || 0;
  const w = Math.min(50, (Math.abs(v) / (max || 1)) * 50);
  const over = v >= 0;
  const near = Math.abs(v) < 0.05;
  return (
    <div className="tiltbar" title={`${v >= 0 ? '+' : ''}${v.toFixed(1)}${suffix}`}>
      <span className="tiltbar-track">
        {!near && (
          <span className="tiltbar-fill"
            style={{ background: color, ...(over ? { left: '50%', width: `${w}%` } : { right: '50%', width: `${w}%` }) }} />
        )}
      </span>
      <span className={`tiltbar-num ${near ? 'flat' : ''}`}>
        {near ? '·' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}`}
      </span>
    </div>
  );
}

/* ─────────── Add-fund panel (screener-backed + manual instrument registry) ─────────── */
const EMPTY_REG = { code: '', name: '', instrument_type: 'fund', platform: 'All',
                   asset_class_group: '', sub_asset_class: '', mer_pct: '', notes: '' };

function AddFundPanel({ onAdd, existingKeys, onClose }) {
  const [assetClasses, setAssetClasses] = useState([]);
  const [ac, setAc] = useState('');
  const [text, setText] = useState('');
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customData, setCustomData] = useState({ instruments: [], types: [], platforms: [], sacs: [], group_order: {} });
  const [showReg, setShowReg] = useState(false);
  const [reg, setReg] = useState(EMPTY_REG);
  const [regMsg, setRegMsg] = useState(null);

  const loadCustom = useCallback(() => {
    tradingAPI.listCustomInstruments().then(setCustomData).catch(() => {});
  }, []);
  useEffect(() => {
    api.get('/api/screener/filters').then((r) => setAssetClasses(r.data?.asset_class || [])).catch(() => {});
    loadCustom();
  }, [loadCustom]);
  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (ac) p.append('asset_class', ac);
    // APIR is the source of truth for the trading universe: include APL funds
    // that have no Refinitiv TR Code / performance data (e.g. CFS Accelerate
    // mirror funds) so they can be selected as trading targets.
    p.append('apir_universe', 'true');
    api.get(`/api/screener/funds?${p.toString()}`)
      .then((r) => setAll(r.data?.funds || [])).catch(() => setAll([])).finally(() => setLoading(false));
  }, [ac]);

  const results = useMemo(() => {
    const t = text.trim().toLowerCase();
    // registered instruments surface first (they're deliberate additions)
    const customRows = (customData.instruments || []).filter((i) => i.active)
      .filter((i) => !ac || i.asset_class_group === ac)
      .filter((i) => !t || i.code.toLowerCase().includes(t) || (i.name || '').toLowerCase().includes(t))
      .map((i) => ({ apir_code: i.code, fund_name: i.name, asset_class: i.asset_class_group,
                     asset_class_group: i.asset_class_group, sub_asset_class: i.sub_asset_class,
                     growth_factor: i.growth_factor, mer: i.mer_pct,
                     custom: true, custom_platform: i.platform, custom_type: i.instrument_type }));
    const list = !t ? all : all.filter((f) =>
      (f.fund_name || '').toLowerCase().includes(t) || (f.apir_code || '').toLowerCase().includes(t) ||
      (f.tr_code || '').toLowerCase().includes(t));
    // A registered instrument is a deliberate override and wins over any AP List
    // row for the same code (e.g. an Accelerate fund registered before it surfaced
    // natively via apir_universe), so drop the duplicate APL row.
    const customCodes = new Set(customRows.map((c) => (c.apir_code || '').toUpperCase()));
    const dedupedList = list.filter((f) => !customCodes.has((f.apir_code || '').toUpperCase()));
    return [...customRows, ...dedupedList].slice(0, 200);
  }, [all, text, ac, customData]);

  const groupNames = useMemo(() => Object.entries(customData.group_order || {})
    .sort((a, b) => a[1] - b[1]).map(([g]) => g), [customData]);

  // SAC → growth factor, so AP List funds (whose growth factor isn't on the screener
  // row) land on the grid with the right Growth/Defensive contribution immediately.
  const sacGrowth = useMemo(() => Object.fromEntries(
    (customData.sacs || []).map((s) => [s.code, s.growth_factor])), [customData]);

  const register = async () => {
    setRegMsg(null);
    try {
      const res = await tradingAPI.createCustomInstrument({
        ...reg, mer_pct: reg.mer_pct === '' ? null : Number(reg.mer_pct) });
      setRegMsg({ ok: true, text: `${reg.code} registered${res.reactivated ? ' (reactivated)' : ''} — searchable above` });
      setText(reg.code);
      setReg(EMPTY_REG); setShowReg(false); loadCustom();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setRegMsg({ ok: false, text: typeof d === 'string' ? d : 'Registration failed' });
    }
  };
  const regReady = ['code', 'name', 'instrument_type', 'platform', 'asset_class_group', 'sub_asset_class']
    .every((f) => String(reg[f] || '').trim());

  return (
    <div className="trading-addfund">
      <div className="trading-addfund-head"><strong>Add fund</strong>
        <button className="trading-link reg-toggle" onClick={() => setShowReg((v) => !v)}>
          {showReg ? '× close register' : '+ Register instrument not in AP List'}
        </button>
        <button className="trading-x" onClick={onClose}>×</button>
      </div>

      {showReg && (
        <div className="reg-form">
          <div className="reg-grid">
            <input placeholder="Code (e.g. DNRHCSUB3) *" value={reg.code} onChange={(e) => setReg({ ...reg, code: e.target.value })} />
            <input placeholder="Name *" value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} />
            <select value={reg.instrument_type} onChange={(e) => setReg({ ...reg, instrument_type: e.target.value })}>
              {(customData.types || []).map((t) => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
            </select>
            <select value={reg.platform} onChange={(e) => setReg({ ...reg, platform: e.target.value })} title="Platform this instrument is valid on">
              {(customData.platforms || []).map((p) => <option key={p} value={p}>{p === 'All' ? 'All platforms' : p}</option>)}
            </select>
            <select value={reg.asset_class_group} onChange={(e) => setReg({ ...reg, asset_class_group: e.target.value })}>
              <option value="">Asset class group *…</option>
              {groupNames.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
            <select value={reg.sub_asset_class} onChange={(e) => setReg({ ...reg, sub_asset_class: e.target.value })}>
              <option value="">Sub-asset class *…</option>
              {(customData.sacs || []).map((s) => <option key={s.code} value={s.code}>{s.name} ({s.code})</option>)}
            </select>
            <input type="number" step="0.01" placeholder="Mgmt Fees & Costs % (optional)" value={reg.mer_pct} onChange={(e) => setReg({ ...reg, mer_pct: e.target.value })} />
            <input placeholder="Notes" value={reg.notes} onChange={(e) => setReg({ ...reg, notes: e.target.value })} />
          </div>
          <div className="reg-actions">
            <span className="trading-muted">All * fields required — platform matters: sheets flag instruments emitted onto a different platform.</span>
            <button className="trading-btn primary sm" disabled={!regReady} onClick={register}>Register</button>
          </div>
        </div>
      )}
      {regMsg && <div className={`trading-save-msg ${regMsg.ok ? 'ok' : 'err'}`}>{regMsg.text}</div>}

      <div className="trading-addfund-filters">
        <select value={ac} onChange={(e) => setAc(e.target.value)}>
          <option value="">All asset classes</option>
          {assetClasses.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <input placeholder="Filter by name / APIR / code…" value={text} onChange={(e) => setText(e.target.value)} />
      </div>
      <div className="trading-addfund-results">
        {loading ? <div className="trading-muted">Searching…</div> : results.map((f) => {
          const key = f.apir_code || f.tr_code;
          const added = existingKeys.has(key);
          return (
            <div key={(f.tr_code || f.apir_code) + (f.custom ? ':c' : '')} className="trading-addfund-row">
              <button className="trading-add-btn" disabled={added}
                onClick={() => onAdd({ position_key: key, apir_code: f.apir_code, fund_name: f.fund_name,
                  asset_class_group: f.asset_class_group || f.asset_class, asset_class: f.asset_class,
                  sub_asset_class: f.sub_asset_class,
                  growth_factor: f.growth_factor ?? sacGrowth[f.sub_asset_class] ?? 0,
                  mer: f.mer, sort_order: 500 })}>
                {added ? '✓' : '+'}
              </button>
              <span className="trading-af-name">{f.fund_name}</span>
              <span className="trading-af-apir">{f.apir_code || f.tr_code}</span>
              {f.custom
                ? <span className="reg-tag" title={`Registered instrument (${f.custom_type}) · ${f.custom_platform}`}>◈ {f.custom_platform}</span>
                : <span className="trading-af-ac">{f.asset_class}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────── Divergence review + capture persistent overrides ─────────── */
const STATUS_LABEL = { reweight: 'Reweight', substitution: 'Substitution', model_only: 'Missing from vehicle', vehicle_only: 'Extra in vehicle' };
const OVERRIDE_TYPES = [
  { v: 'fund_substitution', l: 'Fund substitution' }, { v: 'reweight', l: 'Reweight' },
  { v: 'exclude', l: 'Exclude' }, { v: 'add', l: 'Add' },
];
function defaultOverride(it, pc) {
  const base = { portfolio_code: pc, reason_note: '', apir_code: null, target_pct: null };
  if (it.status === 'reweight') return { ...base, position_key: it.model_apir, override_type: 'reweight', target_pct: it.vehicle_weight };
  if (it.status === 'substitution') return { ...base, position_key: it.model_apir, override_type: 'fund_substitution', apir_code: it.vehicle_apir };
  if (it.status === 'model_only') return { ...base, position_key: it.model_apir, override_type: 'exclude' };
  return { ...base, position_key: it.vehicle_apir, override_type: 'add', apir_code: it.vehicle_apir, target_pct: it.vehicle_weight };
}
function DivergencePanel({ vehicles, onSave }) {
  const [openKey, setOpenKey] = useState(null);
  const [form, setForm] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!(form.reason_note || '').trim()) return;
    setSaving(true);
    try { await onSave([form]); setOpenKey(null); setForm(null); } finally { setSaving(false); }
  };
  return (
    <div className="trading-div-panel">
      <label className="trading-div-showall">
        <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} /> show explained too
      </label>
      {vehicles.filter((v) => !v.no_data).map((v) => {
        const items = (v.items || []).filter((it) => showAll || !it.explained);
        if (!items.length) return null;
        return (
          <div key={v.portfolio_code} className="trading-div-veh">
            <div className="trading-div-vhead">
              <span className="trading-code">{v.portfolio_code}</span>
              <span className="trading-muted">{v.platform}</span>
              <span className={`trading-badge ${v.unexplained ? 'draft' : 'unique'}`}>
                {v.unexplained ? `${v.unexplained} unexplained` : 'all explained'}
              </span>
            </div>
            {items.map((it, i) => {
              const key = `${v.portfolio_code}:${it.model_apir || ''}:${it.vehicle_apir || ''}:${i}`;
              return (
                <div key={key} className={`trading-div-item ${it.explained ? 'expl' : 'unexpl'}`}>
                  <span className={`div-tag ${it.status}`}>{STATUS_LABEL[it.status]}</span>
                  <span className="div-side">{it.model_apir ? <><b>{it.model_name || it.model_apir}</b> {it.model_weight != null ? `${fmt(it.model_weight, 1)}%` : ''}</> : <span className="trading-muted">—</span>}</span>
                  <span className="div-arrow">→</span>
                  <span className="div-side">{it.vehicle_apir ? <><b>{it.vehicle_name || it.vehicle_apir}</b> {it.vehicle_weight != null ? `${fmt(it.vehicle_weight, 1)}%` : ''}</> : <span className="trading-muted">—</span>}</span>
                  {it.explained ? (
                    <span className="div-ok" title={it.reason_note || ''}>✓ {it.equivalent ? 'unit-class' : (it.override_type || 'override')}</span>
                  ) : openKey === key ? (
                    <span className="div-form">
                      <select value={form.override_type} onChange={(e) => setForm({ ...form, override_type: e.target.value })}>
                        {OVERRIDE_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                      </select>
                      <input className="div-reason" placeholder="Reason (required)…" value={form.reason_note}
                        onChange={(e) => setForm({ ...form, reason_note: e.target.value })} />
                      <button className="trading-btn primary sm" disabled={saving || !form.reason_note.trim()} onClick={save}>Save</button>
                      <button className="trading-btn ghost sm" onClick={() => { setOpenKey(null); setForm(null); }}>×</button>
                    </span>
                  ) : (
                    <button className="div-resolve" onClick={() => { setOpenKey(key); setForm(defaultOverride(it, v.portfolio_code)); }}>Record override</button>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

/* ─────────── Sub-portfolio validation modal: gate before save/submit ───────────
   Every sub exception must be explicitly confirmed (or re-weighted). Totals ≠ 100%
   and sub-asset-class tilt differences vs the master are flagged. */
function ValidationModal({ validation, onClose, onConfirm, effectiveDate, onEffectiveDateChange }) {
  const [ticks, setTicks] = useState({});
  const [weights, setWeights] = useState({});
  if (!validation) return null;
  const { action, cols } = validation;
  const isReview = action === 'review';   // standalone manage view — no confirm gate
  const isSubmit = action === 'submit' || action === 'submitAll';

  // derived platform unit-class swaps show for information but don't need ticking
  const exKey = (col, s, o) => `${col.blender_code}:${o.id ?? `${s.portfolio_code}:${o.position_key}`}`;
  const exKeys = [];
  cols.forEach(({ col, data }) => (data.subs || []).forEach((s) =>
    s.exceptions.filter((o) => o.source !== 'platform')
      .forEach((o) => exKeys.push(exKey(col, s, o)))));
  const allTicked = exKeys.every((k) => ticks[k]);

  const confirm = () => {
    const updates = [];
    cols.forEach(({ col, data }) => (data.subs || []).forEach((s) =>
      s.exceptions.forEach((o) => {
        const wk = exKey(col, s, o);
        const nv = weights[wk];
        if (nv !== undefined && nv !== '' && Number(nv) !== o.target_pct) {
          updates.push({ bc: col.blender_code, payload: {
            portfolio_code: s.portfolio_code, position_key: o.position_key,
            override_type: o.type, apir_code: o.apir_code,
            target_pct: Number(nv), reason_note: o.reason || 'validated', active: true } });
        }
      })));
    onConfirm(updates);
  };

  return (
    <div className="ts-modal-overlay" onClick={onClose}>
      <div className="ts-modal val-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ts-modal-head">
          <div>
            <h3>{isReview ? 'Review sub exceptions'
              : cols.length ? 'Validate sub-portfolios before submitting' : 'Submit for approval'}</h3>
            <div className="trading-muted ts-modal-sub">
              {isReview
                ? 'Every recorded exception on the visible portfolios — adjust weights and Save changes.'
                : cols.length
                  ? 'Confirm each exception still stands against the proposed model (or set a new weight).'
                  : `${(validation.allCols || []).map((c) => c.display_name).join(', ')}`}
            </div>
          </div>
          <button className="trading-x ts-close" onClick={onClose}>✕</button>
        </div>
        <div className="ts-modal-body">
          {!isReview && cols.length === 0 && (
            <div className="trading-muted">No sub exceptions to confirm — ready to submit.</div>
          )}
          {cols.map(({ col, data }) => (
            <div key={col.portfolio_code} className="val-col">
              <div className="val-col-head">
                <strong>{col.display_name}</strong> <span className="trading-code">{col.blender_code}</span>
                {!data.model_ok && <span className="compliance-badge fail">model total {fmt(data.model_total, 1)}%</span>}
              </div>
              {(data.subs || []).map((s) => (
                <div key={s.portfolio_code} className="val-sub">
                  <div className="val-sub-head">
                    <span className="trading-code">{s.portfolio_code}</span>
                    <span className={`compliance-badge ${s.ok_total ? 'pass' : 'fail'}`}
                      title="Resolved total against the proposed model">
                      {s.ok_total ? '✓' : '✗'} {fmt(s.total, 1)}%
                    </span>
                  </div>
                  {s.sac_deltas.length > 0 && (() => {
                    const maxD = Math.max(2, ...s.sac_deltas.map((d) => Math.abs(d.delta)));
                    return (
                      <div className="val-tilts">
                        <div className="val-tilts-head">⚠ Sub-asset-class tilts vs master</div>
                        {s.sac_deltas.map((d) => (
                          <div key={d.sac} className="val-tilt-row">
                            <span className="val-tilt-label">{d.label}</span>
                            <span className="val-tilt-vals" title={`sub ${fmt(d.sub, 1)}% vs master ${fmt(d.model, 1)}%`}>
                              {fmt(d.sub, 1)} vs {fmt(d.model, 1)}
                            </span>
                            <TiltBar value={d.delta} max={maxD} color="#f0a500" suffix="% vs master" />
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                  {s.exceptions.map((o) => {
                    const wk = exKey(col, s, o);
                    const derived = o.source === 'platform';
                    return (
                      <div key={wk} className="val-ex">
                        {derived ? (
                          <span className="veh-newtag" title="Derived from the platform unit-class mapping — applies automatically, no confirmation needed">auto</span>
                        ) : isReview ? null : (
                          <label className="val-ok">
                            <input type="checkbox" checked={!!ticks[wk]}
                              onChange={(e) => setTicks((p) => ({ ...p, [wk]: e.target.checked }))} />
                            OK
                          </label>
                        )}
                        <span className={`div-tag ${OV_TAG[o.type] || 'reweight'}`}>{o.type.replace('_', ' ')}</span>
                        <span className="val-ex-fund">{o.fund_name}</span>
                        {o.type !== 'exclude' && (
                          <input className="trading-input val-w" type="number" step="0.1"
                            title="Adjust the exception's weight (saved on confirm)"
                            value={weights[wk] ?? (o.target_pct ?? '')}
                            placeholder={o.target_pct == null ? 'model' : ''}
                            onChange={(e) => setWeights((p) => ({ ...p, [wk]: e.target.value }))} />
                        )}
                        <span className="trading-muted val-reason">{o.reason}</span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="ad-actions ts-modal-actions">
          <span className="trading-muted">
            {isReview ? 'Adjust weights, then Save changes.'
              : !exKeys.length ? ''
              : allTicked ? 'All exceptions confirmed.' : `${exKeys.filter((k) => !ticks[k]).length} exception(s) awaiting confirmation`}
          </span>
          {isSubmit && (
            <span className="effective-date"
              title="Effective date (optional). Blank = targets effective as soon as approved. A future date holds the approved targets back until that day — note the strategy shows no approved target in the meantime.">
              <label htmlFor="submitEffectiveDate">Effective</label>
              <input id="submitEffectiveDate" type="date" value={effectiveDate || ''}
                onChange={(e) => onEffectiveDateChange && onEffectiveDateChange(e.target.value)} />
              {effectiveDate
                ? <button className="trading-x sm" title="Clear (effective on approval)"
                    onClick={() => onEffectiveDateChange && onEffectiveDateChange('')}>✕</button>
                : <span className="trading-muted">on approval</span>}
            </span>
          )}
          <button className="trading-btn sm" onClick={onClose}>{isReview ? 'Close' : 'Go back'}</button>
          <button className="trading-btn primary sm" disabled={!isReview && !allTicked} onClick={confirm}>
            {isReview ? 'Save changes' : `Confirm & ${action === 'submitAll' ? 'submit all' : 'submit'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────── Quick compare: one portfolio's Proposed vs another's Current & Proposed ───────────
   Reads the SAME live state as the grid (cur/prop closures), so unsaved edits are included. */
/* ─────────── Copy-from modal: pick a source allocation (trading models + Portfolio Lab) ─────────── */
function CopyFromModal({ col, allSuites, labPortfolios, busy, onPick, onClose }) {
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const match = (...parts) => !needle || parts.some((p) => (p || '').toLowerCase().includes(needle));

  const suiteGroups = allSuites.map((s) => ({
    name: s.name,
    items: (s.portfolios || [])
      .filter((p) => p.portfolio_code !== col.portfolio_code)
      .filter((p) => match(p.display_name, p.portfolio_code, s.name)),
  })).filter((g) => g.items.length);
  const labItems = labPortfolios.filter((p) => match(p.name, p.client, p.portfolio_code));

  return (
    <div className="ts-modal-overlay" onClick={onClose}>
      <div className="ts-modal copyfrom-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ts-modal-head">
          <div>
            <h3>Copy allocation into {col.display_name}</h3>
            <div className="trading-muted ts-modal-sub">
              Replaces this column's <strong>Proposed</strong> weights — funds not in the source go to 0.
              Nothing is stored until you Save draft.
            </div>
          </div>
          <button className="trading-x ts-close" onClick={onClose}>✕</button>
        </div>
        <input className="copyfrom-search" autoFocus placeholder="Search by name, code or client…"
          value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="ts-modal-body copyfrom-body">
          {suiteGroups.length > 0 && (
            <div className="copyfrom-section">
              <div className="copyfrom-section-head" title="Canonical targets authored in this app (Shadow_Data fallback if never authored)">
                Trading model portfolios
              </div>
              {suiteGroups.map((g) => (
                <div key={g.name} className="copyfrom-group">
                  <div className="copyfrom-group-head">{g.name}</div>
                  {g.items.map((p) => (
                    <button key={p.portfolio_code} className="copyfrom-row" disabled={busy}
                      onClick={() => onPick({ kind: 'trading', code: p.portfolio_code, label: p.display_name || p.portfolio_code })}>
                      <span className="copyfrom-row-name">{p.display_name}</span>
                      <span className="trading-code">{p.portfolio_code}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
          {labItems.length > 0 && (
            <div className="copyfrom-section">
              <div className="copyfrom-section-head" title="Saved portfolios from Portfolio Lab (fund codes resolved to APIRs via the AP List)">
                Portfolio Lab
              </div>
              <div className="copyfrom-group">
                {labItems.map((p) => (
                  <button key={p.id} className="copyfrom-row" disabled={busy}
                    onClick={() => onPick({ kind: 'lab', id: p.id, label: p.name })}>
                    <span className="copyfrom-row-name">{p.name}</span>
                    <span className="copyfrom-row-meta">
                      {p.client ? `${p.client} · ` : ''}{p.fund_count} fund{p.fund_count === 1 ? '' : 's'}
                      {p.updated_at ? ` · ${String(p.updated_at).slice(0, 10)}` : ''}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {!suiteGroups.length && !labItems.length && (
            <div className="trading-muted copyfrom-empty">No portfolios match “{q}”.</div>
          )}
          {busy && <div className="trading-muted copyfrom-empty">Copying…</div>}
        </div>
      </div>
    </div>
  );
}

function CompareModal({ col, columns, rows, cur, prop, onClose }) {
  const others = columns.filter((c) => c.portfolio_code !== col.portfolio_code);
  const [otherPc, setOtherPc] = useState(others[0]?.portfolio_code || '');
  const other = others.find((c) => c.portfolio_code === otherPc);
  const pc = col.portfolio_code;
  const vis = 0.0005;   // hide true zeros, keep small real weights

  const body = rows
    .map(([ac, group]) => [ac, group.filter((r) => Math.abs(prop(pc, r.position_key)) > vis
      || (other && (Math.abs(cur(otherPc, r.position_key)) > vis || Math.abs(prop(otherPc, r.position_key)) > vis)))])
    .filter(([, g]) => g.length);
  const totals = rows.flatMap(([, g]) => g).reduce((t, r) => ({
    a: t.a + prop(pc, r.position_key),
    bc: t.bc + (other ? cur(otherPc, r.position_key) : 0),
    bp: t.bp + (other ? prop(otherPc, r.position_key) : 0),
  }), { a: 0, bc: 0, bp: 0 });

  return (
    <div className="ts-modal-overlay" onClick={onClose}>
      <div className="ts-modal cmp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ts-modal-head">
          <div>
            <h3>Quick compare <span className="trading-muted">{col.display_name}</span></h3>
            <div className="trading-muted ts-modal-sub">
              This portfolio's Proposed (live edits included) against another portfolio's Current and Proposed
            </div>
          </div>
          <button className="trading-x ts-close" onClick={onClose}>✕</button>
        </div>
        <div className="rules-scope">
          <span className="trading-muted">Compare with</span>
          <select value={otherPc} onChange={(e) => setOtherPc(e.target.value)}>
            {others.map((c) => <option key={c.portfolio_code} value={c.portfolio_code}>{c.display_name} ({c.portfolio_code})</option>)}
          </select>
        </div>
        <div className="ts-modal-body">
          <table className="rules-table ts-table cmp-table">
            <thead><tr>
              <th className="l">Fund</th>
              <th>{col.display_name}<br />Proposed</th>
              <th>{other ? other.display_name : '—'}<br />Current</th>
              <th>{other ? other.display_name : '—'}<br />Proposed</th>
              <th title="This portfolio's Proposed less the other's Proposed">Δ Prop</th>
            </tr></thead>
            <tbody>
              {body.map(([ac, group], gi) => {
                const aT = group.reduce((s, r) => s + prop(pc, r.position_key), 0);
                const bcT = other ? group.reduce((s, r) => s + cur(otherPc, r.position_key), 0) : 0;
                const bpT = other ? group.reduce((s, r) => s + prop(otherPc, r.position_key), 0) : 0;
                const dT = aT - bpT;
                return (
                <React.Fragment key={ac}>
                  <tr className="ac-header cmp-ac">
                    <td className="l"><span className="ac-dot" style={{ background: acColour(group[0]?.asset_class_group || ac, gi) }} />{ac}</td>
                    <td>{fmt(aT, 1)}</td>
                    <td>{other ? fmt(bcT, 1) : ''}</td>
                    <td>{other ? fmt(bpT, 1) : ''}</td>
                    <td className={Math.abs(dT) > 0.05 ? 'cmp-diff' : undefined}>
                      {other && Math.abs(dT) > 0.005 ? fmt(dT, 1) : ''}
                    </td>
                  </tr>
                  {group.map((r) => {
                    const a = prop(pc, r.position_key);
                    const bCur = other ? cur(otherPc, r.position_key) : 0;
                    const bProp = other ? prop(otherPc, r.position_key) : 0;
                    const d = a - bProp;
                    return (
                      <tr key={r.position_key}>
                        <td className="l bulk-name" title={r.fund_name}>{r.fund_name || r.apir_code}</td>
                        <td>{Math.abs(a) > vis ? fmt(a, 2) : ''}</td>
                        <td>{other && Math.abs(bCur) > vis ? fmt(bCur, 2) : ''}</td>
                        <td>{other && Math.abs(bProp) > vis ? fmt(bProp, 2) : ''}</td>
                        <td className={Math.abs(d) > 0.05 ? 'cmp-diff' : undefined}>
                          {other && Math.abs(d) > 0.005 ? fmt(d, 2) : ''}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
                );
              })}
              <tr className="trading-total-row">
                <td className="l">Total</td>
                <td>{fmt(totals.a, 1)}%</td>
                <td>{other ? `${fmt(totals.bc, 1)}%` : ''}</td>
                <td>{other ? `${fmt(totals.bp, 1)}%` : ''}</td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════ Suite view: the suite's portfolios as editable columns ═══════════════ */
function SuiteView({ suite, onDrill, onDirtyChange, onPendingChanged, onEvolution }) {
  const [data, setData] = useState(null);         // { columns, funds, weights, actuals }
  // proposed[pc][apir] overrides the (fixed) current weight; where unset, proposed = current.
  const [proposed, setProposed] = useState({});
  const [addedFunds, setAddedFunds] = useState([]);
  const [showAddFund, setShowAddFund] = useState(false);
  const [saving, setSaving] = useState(null);
  const [saveMsg, setSaveMsg] = useState(null);
  const [tiltLevel, setTiltLevel] = useState('acg');   // 'acg' | 'sac'
  const [mer, setMer] = useState({});                  // mer[position_key] = manual Management Fees & Costs % (FE fee-table default otherwise)
  const [rebate, setRebate] = useState({});            // rebate[position_key] = manual rebate %
  const [compliance, setCompliance] = useState({});    // {pc: {results, summary}}
  const [reasons, setReasons] = useState({});          // reasons[`${pc}::${key}`] = soft-flag override reason
  const [submitting, setSubmitting] = useState(null);
  const [allSuites, setAllSuites] = useState([]);      // for the copy-across picker (grouped by suite)
  const [labPortfolios, setLabPortfolios] = useState([]);  // Portfolio Lab sources for the copy picker
  const [copyModalCol, setCopyModalCol] = useState(null);  // column the copy-from modal targets (null = closed)
  const [copyBusy, setCopyBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);       // bump to refetch (e.g. after a Shadow import)

  useEffect(() => {
    tradingAPI.listSuites().then((d) => setAllSuites(d.suites || [])).catch(() => setAllSuites([]));
    tradingAPI.listLabPortfolios().then((d) => setLabPortfolios(d.portfolios || [])).catch(() => setLabPortfolios([]));
  }, []);

  // MER/Rebate/Net columns collapsed by default — toggle remembered across sessions
  const [showMerCols, setShowMerCols] = useState(() => {
    try { return localStorage.getItem('trading.showMerCols') === '1'; } catch { return false; }
  });
  const toggleMerCols = () => setShowMerCols((v) => {
    try { localStorage.setItem('trading.showMerCols', v ? '0' : '1'); } catch { /* ignore */ }
    return !v;
  });

  // View toggles for a clean, exportable table (all remembered per session).
  const [hideHeaderDetail, setHideHeaderDetail] = useState(() => localStorage.getItem('trading.hideHeaderDetail') === '1');
  const [hideUnchanged, setHideUnchanged] = useState(() => localStorage.getItem('trading.hideUnchanged') === '1');
  const [hideSaaTilts, setHideSaaTilts] = useState(() => localStorage.getItem('trading.hideSaaTilts') === '1');
  const [deltasOnly, setDeltasOnly] = useState(() => localStorage.getItem('trading.deltasOnly') === '1');
  const [hideFunds, setHideFunds] = useState(() => localStorage.getItem('trading.hideFunds') === '1');
  const [tiltHeat, setTiltHeat] = useState(() => localStorage.getItem('trading.tiltHeat') === '1');
  const mkToggle = (setter, lsKey) => () => setter((p) => {
    try { localStorage.setItem(lsKey, p ? '0' : '1'); } catch { /* ignore */ }
    return !p;
  });
  const toggleHideHeader = mkToggle(setHideHeaderDetail, 'trading.hideHeaderDetail');
  const toggleHideUnchanged = mkToggle(setHideUnchanged, 'trading.hideUnchanged');
  const toggleHideSaa = mkToggle(setHideSaaTilts, 'trading.hideSaaTilts');
  const toggleDeltasOnly = mkToggle(setDeltasOnly, 'trading.deltasOnly');
  const toggleHideFunds = mkToggle(setHideFunds, 'trading.hideFunds');
  const toggleTiltHeat = mkToggle(setTiltHeat, 'trading.tiltHeat');
  // Once any view is being pared back for reading/export, drop the per-column
  // Copy-from / Save-draft / Submit action row too (it's a working-edit control, not data).
  const anyHidden = hideHeaderDetail || hideUnchanged || hideSaaTilts || deltasOnly || hideFunds;
  // Conditional formatting for tilt cells: shade by magnitude relative to the row set's max.
  // Green = over/increase, red = under/decrease; stronger colour = bigger tilt.
  const tiltHeatBg = (v, max) => {
    if (!tiltHeat || v == null || !Number.isFinite(v)) return undefined;
    const m = Math.max(0.0001, max || 0);
    const r = Math.min(1, Math.abs(v) / m);
    if (r < 0.02) return undefined;
    const a = (0.14 + r * 0.5).toFixed(2);
    return v >= 0 ? `rgba(75, 163, 90, ${a})` : `rgba(200, 66, 66, ${a})`;
  };
  const [exporting, setExporting] = useState(false);
  const gridExportRef = useRef(null);

  const [baseline, setBaseline] = useState({});        // proposed state as loaded/saved — revert + dirty anchor
  const [versionNote, setVersionNote] = useState('');  // optional note stored on Save draft / Submit
  const [effectiveDate, setEffectiveDate] = useState('');  // optional; blank = effective on approval
  const [fundFilter, setFundFilter] = useState('');

  useEffect(() => {
    setData(null); setProposed({}); setBaseline({}); setAddedFunds([]); setSaveMsg(null); setShowAddFund(false);
    setMer({}); setRebate({}); setCompliance({}); setReasons({}); setRemovedKeys(new Set());
    setVersionNote(''); setFundFilter('');
    tradingAPI.getSuiteMatrix(suite).then((d) => {
      setData(d);
      // A saved-but-unapproved draft loads into PROPOSED (current stays the approved
      // instruction), so Δ shows exactly what's awaiting approval.
      const p = {};
      Object.entries(d.drafts || {}).forEach(([pc, dw]) => {
        const next = {};
        Object.keys(d.weights?.[pc] || {}).forEach((k) => { next[k] = 0; });
        Object.entries(dw).forEach(([k, v]) => { next[k] = v; });
        p[pc] = next;
      });
      if (Object.keys(p).length) setProposed(p);
      setBaseline(JSON.parse(JSON.stringify(p)));
    }).catch(() => setData({ columns: [], funds: [], weights: {}, actuals: {} }));
  }, [suite, reloadKey]);

  // Dirty = proposed weights differ from what was loaded/last saved. Reported up so the
  // shell can guard navigation; beforeunload covers tab close/refresh.
  const isDirty = useMemo(() => JSON.stringify(proposed) !== JSON.stringify(baseline), [proposed, baseline]);
  const colDirty = (pc) => JSON.stringify(proposed[pc] || null) !== JSON.stringify(baseline[pc] || null);
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(isDirty);
    return () => { if (onDirtyChange) onDirtyChange(false); };
  }, [isDirty, onDirtyChange]);
  useEffect(() => {
    if (!isDirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [isDirty]);

  const exportExcel = async () => {
    try {
      const blob = await tradingAPI.exportSuiteGrid(suite, {
        proposals, mer, rebate, portfolio_codes: columns.map((c) => c.portfolio_code) });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${suite.replace(/[,\s]+/g, '_')}_targets.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setSaveMsg({ ok: false, text: 'Excel export failed' }); }
  };

  // Export the grid table exactly as shown (respects the view toggles above) to a PNG.
  // html2canvas is loaded lazily so it only ships when someone exports.
  const exportPng = async () => {
    const node = gridExportRef.current;
    if (!node) return;
    setExporting(true);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(node, {
        scale: 2, backgroundColor: '#ffffff', useCORS: true, logging: false,
        windowWidth: node.scrollWidth, width: node.scrollWidth, height: node.scrollHeight,
        // Drop the per-column action buttons (Copy from / Save draft / Submit) from the image.
        ignoreElements: (el) => !!(el.classList && el.classList.contains('no-export')),
        // Only the Δ-only view rotates the vehicle names vertical (writing-mode: vertical-rl
        // + rotate(180deg)); html2canvas mishandles that rotate (flips glyphs upside down),
        // so strip the transform on the clone. In every other view the names are horizontal
        // and must be LEFT ALONE — forcing them vertical here garbles the glyphs and inflates
        // the header far beyond the captured scrollHeight, clipping the SAA tilts at the bottom.
        onclone: (doc) => {
          // The Total & Compliance rows are `position: sticky; bottom` so they pin to the
          // bottom of the on-screen scroll box. html2canvas anchors sticky elements to that
          // offset, dropping them mid-table — un-stick them so they flow in document order
          // (Total right after the funds, Compliance at the foot).
          doc.querySelectorAll('.trading-total-row td, .trading-compliance-row td').forEach((el) => {
            el.style.position = 'static';
          });
          if (!deltasOnly) return;
          doc.querySelectorAll('.veh-name').forEach((el) => {
            el.style.transform = 'none';
            el.style.writingMode = 'vertical-rl';
            el.style.textOrientation = 'mixed';
          });
        },
      });
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = `${suite.replace(/[,\s]+/g, '_')}_trade_sheet.png`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { setSaveMsg({ ok: false, text: 'PNG export failed' }); }
    finally { setExporting(false); }
  };

  // Excel multi-cell paste into Δ (adds to current) or Proposed (absolute) columns
  const onGridPaste = (isDelta) => (e) => {
    const targets = gridPasteTargets(e);
    if (!targets) return;
    setProposed((prev) => {
      const next = { ...prev };
      targets.forEach(({ pc, key, val }) => {
        const num = parseFloat(String(val).replace(/[%,]/g, ''));
        if (!Number.isFinite(num)) return;
        const cv = Number(data?.weights?.[pc]?.[key]) || 0;
        next[pc] = { ...(next[pc] || {}), [key]: isDelta ? cv + num : num };
      });
      return next;
    });
    setSaveMsg({ ok: true, text: `Pasted ${targets.length} values` });
  };

  // Confirm from the exceptions modal: persist any re-weighted exceptions, then (for the
  // submit gate) resume the submission; for the standalone review, just close.
  const confirmValidation = async (updates) => {
    const v = validation;
    setValidation(null);
    try {
      const byBc = {};
      (updates || []).forEach((u) => { (byBc[u.bc] = byBc[u.bc] || []).push(u.payload); });
      for (const bc of Object.keys(byBc)) {
        await tradingAPI.saveOverrides(bc, byBc[bc]);   // upsert on (sub, slot)
      }
    } catch (e) {
      setSaveMsg({ ok: false, text: 'Exception weight update failed — action cancelled' });
      return;
    }
    if (v.action === 'review') {
      setSaveMsg({ ok: true, text: (updates || []).length ? `Updated ${updates.length} exception weight(s).` : 'No changes.' });
      return;
    }
    if (v.action === 'submit') await submitColumn(v.cols.length ? v.cols[0].col : v.allCols[0], true);
    else await submitAll(true);
  };

  const revertColumn = (pc) => setProposed((prev) => {
    const next = { ...prev };
    if (baseline[pc]) next[pc] = JSON.parse(JSON.stringify(baseline[pc])); else delete next[pc];
    return next;
  });
  const discardAll = () => setProposed(JSON.parse(JSON.stringify(baseline)));

  // One-time explicit import of a vehicle's Shadow_Data target into the app as a draft.
  const importShadow = async (col) => {
    setSaveMsg(null);
    try {
      const res = await tradingAPI.importShadowTarget(col.portfolio_code);
      setSaveMsg({ ok: true, text: `Imported Shadow_Data into ${col.display_name} as draft v${res.version} (${fmt(res.target_total, 1)}%, as at ${res.as_at})` });
      setReloadKey((k) => k + 1);
    } catch (e) {
      setSaveMsg({ ok: false, text: e?.response?.data?.detail || 'Import failed' });
    }
  };

  // Which portfolios in this suite are being traded on — an explicit per-suite selection,
  // remembered in localStorage. Until the user chooses, defaults to portfolios that have a
  // stored target (all, when none do yet).
  const storageKey = `trading.hiddenPortfolios.${suite}`;
  const [hiddenSel, setHiddenSel] = useState(null);   // null = no explicit choice yet
  const [showPicker, setShowPicker] = useState(false);
  useEffect(() => {
    try {
      const s = JSON.parse(localStorage.getItem(storageKey) || 'null');
      setHiddenSel(Array.isArray(s) ? new Set(s) : null);
    } catch { setHiddenSel(null); }
    setShowPicker(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suite]);

  const allColumns = data?.columns || [];
  const allUnseeded = allColumns.length > 0 && allColumns.every((c) => !c.seeded);
  const effectiveHidden = useMemo(() => {
    if (hiddenSel) return hiddenSel;
    if (allUnseeded) return new Set();
    return new Set(allColumns.filter((c) => !c.seeded).map((c) => c.portfolio_code));
  }, [hiddenSel, allColumns, allUnseeded]);
  const columns = useMemo(
    () => allColumns.filter((c) => !effectiveHidden.has(c.portfolio_code)),
    [allColumns, effectiveHidden]);
  const setHiddenPersist = (next) => {
    setHiddenSel(next);
    try { localStorage.setItem(storageKey, JSON.stringify([...next])); } catch { /* ignore */ }
  };
  const togglePortfolio = (pc) => {
    const next = new Set(effectiveHidden);
    if (next.has(pc)) next.delete(pc); else next.add(pc);
    setHiddenPersist(next);
  };

  const [removedKeys, setRemovedKeys] = useState(new Set());
  const [compareCol, setCompareCol] = useState(null);    // column in the quick-compare modal
  const [cascadeCol, setCascadeCol] = useState(null);    // column in the cascade-map modal
  // Row grouping level (from the AP List taxonomy): asset class group (default),
  // asset class (middle), or sub-asset class.
  const [groupLevel, setGroupLevel] = useState('acg');   // 'acg' | 'ac' | 'sac'
  const sacNm = (c) => data?.sac_names?.[c] || c;
  const rows = useMemo(() => {
    const map = new Map();
    (data?.funds || []).forEach((f) => map.set(f.position_key, f));
    addedFunds.forEach((f) => { if (!map.has(f.position_key)) map.set(f.position_key, f); });
    removedKeys.forEach((k) => map.delete(k));
    const ff = fundFilter.trim().toLowerCase();
    const list = Array.from(map.values()).filter((r) => !ff
      || (r.fund_name || '').toLowerCase().includes(ff)
      || (r.apir_code || r.position_key || '').toLowerCase().includes(ff)
      || (r.sub_asset_class || '').toLowerCase().includes(ff));
    const keyOf = (r) => (groupLevel === 'acg' ? (r.asset_class_group || 'Other')
      : groupLevel === 'ac' ? (r.asset_class || r.asset_class_group || 'Other')
        : (r.sub_asset_class ? sacNm(r.sub_asset_class) : 'Other'));
    const groups = {}; const rank = {};
    const gOrder = data?.group_order || {};
    // canonical group ordering: admin taxonomy display_order, then the static fallback
    const rankOf = (g) => (gOrder[g] !== undefined ? gOrder[g]
      : g === 'EM Equities' ? 2.5 : (acRank(g) === 999 ? 999 : acRank(g) + 0.1));
    list.forEach((r) => {
      const k = keyOf(r);
      (groups[k] = groups[k] || []).push(r);
      const rr = rankOf(r.asset_class_group || k);
      rank[k] = rank[k] === undefined ? rr : Math.min(rank[k], rr);
    });
    Object.values(groups).forEach((g) => g.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
    return Object.entries(groups).sort((a, b) => (rank[a[0]] - rank[b[0]]) || a[0].localeCompare(b[0]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, addedFunds, removedKeys, groupLevel, fundFilter]);
  const totalFunds = useMemo(() => {
    const map = new Map();
    (data?.funds || []).forEach((f) => map.set(f.position_key, f));
    addedFunds.forEach((f) => { if (!map.has(f.position_key)) map.set(f.position_key, f); });
    removedKeys.forEach((k) => map.delete(k));
    return map.size;
  }, [data, addedFunds, removedKeys]);
  const shownFunds = useMemo(() => rows.reduce((s, [, g]) => s + g.length, 0), [rows]);

  const existingKeys = useMemo(() => {
    const s = new Set();
    rows.forEach(([, g]) => g.forEach((r) => s.add(r.position_key)));
    return s;
  }, [rows]);

  // Current is fixed (Shadow_Data). Proposed defaults to current; the Δ and Proposed cells
  // both write the single `proposed` state, so editing one solves the other.
  const cur = (pc, k) => Number(data?.weights?.[pc]?.[k]) || 0;
  const held = (pc, k) => data?.weights?.[pc]?.[k] != null;
  const prop = (pc, k) => { const v = proposed[pc]?.[k]; return v === undefined ? cur(pc, k) : (Number(v) || 0); };
  const setProp = (pc, k, v) => setProposed((prev) => ({ ...prev, [pc]: { ...(prev[pc] || {}), [k]: v } }));

  // A row can only be deleted when it's empty on BOTH sides in every portfolio: no weight
  // in the last instruction (current) and no proposed weight. Guards against accidental
  // Add-fund rows without allowing a live position to vanish from the grid.
  const deletable = (key) => columns.every((c) => !held(c.portfolio_code, key) && prop(c.portfolio_code, key) === 0);
  const removeFund = (key) => {
    if (!deletable(key)) return;
    setAddedFunds((prev) => prev.filter((f) => f.position_key !== key));
    setRemovedKeys((prev) => new Set([...prev, key]));
  };

  // Fee/rebate are GLOBAL fund-level attributes: the fee defaults to FE_Fee_Table
  // (Management Fees & Costs) with any manual override from the persisted
  // trading.fund_attribute store, edited here, and saved on blur — so values
  // propagate across suites and survive a fund being removed and re-added.
  const merRaw = (r) => (mer[r.position_key] !== undefined ? mer[r.position_key] : (r.mer ?? ''));
  const merNum = (r) => Number(merRaw(r)) || 0;
  const setMerVal = (k, v) => setMer((prev) => ({ ...prev, [k]: v }));
  const rebateRaw = (r) => (rebate[r.position_key] !== undefined ? rebate[r.position_key] : (r.rebate ?? ''));
  const rebateNum = (r) => Number(rebateRaw(r)) || 0;
  const setRebateVal = (k, v) => setRebate((prev) => ({ ...prev, [k]: v }));
  const netMerNum = (r) => merNum(r) - rebateNum(r);
  const persistFundAttr = (r) => {
    const k = r.position_key;
    if (mer[k] === undefined && rebate[k] === undefined) return;   // untouched → nothing to save
    const m = merRaw(r), b = rebateRaw(r);
    tradingAPI.saveFundAttributes([{
      position_key: k,
      mer_pct: m === '' ? null : Number(m),
      rebate_pct: b === '' ? null : Number(b),
    }]).catch(() => setSaveMsg({ ok: false, text: 'Fee/rebate save failed' }));
  };

  const totals = useMemo(() => {
    const t = {};
    columns.forEach((c) => {
      let curT = 0, propT = 0;
      rows.forEach(([, g]) => g.forEach((r) => { curT += cur(c.portfolio_code, r.position_key); propT += prop(c.portfolio_code, r.position_key); }));
      t[c.portfolio_code] = { curT, propT };
    });
    return t;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, rows, data, proposed]);

  // Growth / Defensive / Currency exposure + implied SAA tilts per column, recomputed
  // live from proposed weights. growth_factor + unhedged come per-fund from the matrix;
  // the SAA neutral (data.neutrals) is the live-year strategic allocation for the tilts.
  const summary = useMemo(() => {
    const flat = [];
    rows.forEach(([, g]) => g.forEach((r) => flat.push(r)));
    const neutrals = data?.neutrals || {};
    const neutralsSac = data?.neutrals_sac || {};
    const neutralsCur = data?.neutrals_currency || {};
    const sacToAc = data?.sac_to_ac || {};
    const out = {};
    columns.forEach((c) => {
      const pc = c.portfolio_code;
      let cG = 0, pG = 0, cT = 0, pT = 0, cC = 0, pC = 0, cM = 0, pM = 0, cN = 0, pN = 0;
      const cGrp = {}, pGrp = {}, cSac = {}, pSac = {}, cMid = {}, pMid = {};
      flat.forEach((r) => {
        const k = r.position_key, cv = cur(pc, k), pv = prop(pc, k), gf = Number(r.growth_factor) || 0;
        cG += cv * gf; pG += pv * gf; cT += cv; pT += pv;
        const mv = merNum(r), nv = netMerNum(r);
        cM += (cv / 100) * mv; pM += (pv / 100) * mv;
        cN += (cv / 100) * nv; pN += (pv / 100) * nv;
        if (r.unhedged) { cC += cv; pC += pv; }
        const acg = r.asset_class_group || '—';
        cGrp[acg] = (cGrp[acg] || 0) + cv; pGrp[acg] = (pGrp[acg] || 0) + pv;
        const mid = r.asset_class || r.asset_class_group || '—';
        cMid[mid] = (cMid[mid] || 0) + cv; pMid[mid] = (pMid[mid] || 0) + pv;
        const sac = r.sub_asset_class;
        if (sac) { cSac[sac] = (cSac[sac] || 0) + cv; pSac[sac] = (pSac[sac] || 0) + pv; }
      });
      const neu = neutrals[pc] || {};
      const neuS = neutralsSac[pc] || {};
      // AP List middle level: SAA neutrals (stored per SAC) rolled up via sac→asset_class
      const neuM = {};
      Object.entries(neuS).forEach(([sac, v]) => {
        const mid = sacToAc[sac] || sac;
        neuM[mid] = (neuM[mid] || 0) + v;
      });
      const hasSAA = Object.keys(neu).length > 0;
      const nCur = neutralsCur[pc];                 // SAA currency (unhedged) neutral, or undefined
      out[pc] = {
        cG, pG, cDef: cT - cG, pDef: pT - pG, cC, pC, cM, pM, cN, pN, hasSAA,
        nCur: nCur ?? 0, nCurHas: nCur != null,
        curBy: { acg: cGrp, ac: cMid, sac: cSac },
        propBy: { acg: pGrp, ac: pMid, sac: pSac },
        neuBy: { acg: neu, ac: neuM, sac: neuS },
      };
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, rows, data, proposed, mer, rebate]);

  // sub-asset-class → parent asset-class-group (from the holdings), for SAC tilt colours.
  const sacToAcg = useMemo(() => {
    const m = {};
    rows.forEach(([acg, g]) => g.forEach((r) => { if (r.sub_asset_class) m[r.sub_asset_class] = acg; }));
    return m;
  }, [rows]);

  // Shared, ordered row list for the tilt block + a common bar scale so columns are
  // comparable. At the asset-class-group level every group present in the book is shown
  // (even with no tilt in any column); the finer levels show the union across columns
  // where any column has a meaningful tilt. Keyed to the chosen level ('acg' | 'ac' | 'sac').
  const sacLabel = (code) => (data?.sac_names?.[code]) || code;
  const [tiltBasis, setTiltBasis] = useState('prop');   // 'cur' | 'prop' | 'delta'
  const tiltRows = useMemo(() => {
    // basis: cur = current − SAA, prop = proposed − SAA, delta = proposed − current
    // (delta needs no SAA on record — it's pure weight movement)
    const val = (s, k) => {
      if (!s) return null;
      // Currency is a cross-cutting FX overlay (total unhedged exposure), not an asset
      // class, so it lives in its own row and is level-independent: exposure vs the SAA
      // currency neutral.
      if (k === CURRENCY_TILT_KEY) {
        if (tiltBasis === 'delta') return s.pC - s.cC;
        if (!s.nCurHas) return null;
        return tiltBasis === 'cur' ? s.cC - s.nCur : s.pC - s.nCur;
      }
      if (tiltBasis !== 'delta' && !s.hasSAA) return null;
      const cv = s.curBy?.[tiltLevel]?.[k] || 0;
      const pv = s.propBy?.[tiltLevel]?.[k] || 0;
      const nv = s.neuBy?.[tiltLevel]?.[k] || 0;
      return tiltBasis === 'cur' ? cv - nv : tiltBasis === 'delta' ? pv - cv : pv - nv;
    };
    const keys = new Set();
    let max = 2;
    columns.forEach((c) => {
      const s = summary[c.portfolio_code];
      if (!s) return;
      const all = new Set([
        ...Object.keys(s.curBy?.[tiltLevel] || {}),
        ...Object.keys(s.propBy?.[tiltLevel] || {}),
        ...Object.keys(s.neuBy?.[tiltLevel] || {}),
      ]);
      all.forEach((k) => {
        const v = val(s, k);
        if (v == null) return;
        // ACG view: always surface a group that exists in the book, even when every
        // portfolio sits on its SAA neutral (no tilt) — otherwise an on-neutral class
        // like Global Alpha silently disappears. Finer levels (ac/sac) stay filtered to
        // meaningful tilts so the list doesn't explode.
        if (tiltLevel === 'acg' || Math.abs(v) > 0.05) keys.add(k);
        if (Math.abs(v) > max) max = Math.abs(v);
      });
    });
    // Currency overlay row (unhedged exposure vs SAA currency neutral) — shown below the
    // asset classes whenever the suite carries FX exposure/neutral, at every level.
    let showCur = false;
    columns.forEach((c) => {
      const v = val(summary[c.portfolio_code], CURRENCY_TILT_KEY);
      if (v == null) return;
      showCur = true;
      if (Math.abs(v) > max) max = Math.abs(v);
    });
    const rank = (k) => (tiltLevel === 'sac' ? acRank(sacToAcg[k] || 'zz') : acRank(k));
    const label = (k) => (tiltLevel === 'sac' ? sacLabel(k) : k);
    const parent = (k) => (tiltLevel === 'sac' ? (sacToAcg[k] || 'Other') : k);
    const items = [...keys].sort((a, b) => (rank(a) - rank(b)) || label(a).localeCompare(label(b)))
      .map((k) => ({ key: k, label: label(k), parent: parent(k) }));
    if (showCur) items.push({ key: CURRENCY_TILT_KEY, label: 'Currency', parent: 'Currency' });
    return { items, max, val };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, summary, tiltLevel, tiltBasis, sacToAcg, data]);

  const addFund = (f) => setAddedFunds((prev) => [...prev, f]);

  // Proposed weights as {pc: {position_key: pct}} — the payload compliance runs over.
  const proposals = useMemo(() => {
    const out = {};
    columns.forEach((c) => {
      const pc = c.portfolio_code, o = {};
      rows.forEach(([, g]) => g.forEach((r) => { o[r.position_key] = prop(pc, r.position_key); }));
      out[pc] = o;
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, rows, proposed, data]);

  // Live compliance evaluation (debounced) — the backend recomputes every metric.
  useEffect(() => {
    if (!columns.length) return undefined;
    const t = setTimeout(() => {
      tradingAPI.evaluateCompliance(suite, proposals, data?.saa_year)
        .then((res) => setCompliance(res.portfolios || {}))
        .catch(() => {});
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposals, suite, data?.saa_year]);

  const setReason = (pc, key, v) => setReasons((prev) => ({ ...prev, [`${pc}::${key}`]: v }));

  // Copy another allocation into this column's PROPOSED weights (current is untouched).
  // Sources: a trading portfolio (src = {kind:'trading', code}) or a saved Portfolio Lab
  // portfolio (src = {kind:'lab', id}). Funds the grid doesn't have yet are added as
  // rows; held funds not in the source are proposed to 0 so the copy fully replaces
  // the allocation.
  const copyFrom = async (col, src) => {
    setSaveMsg(null); setCopyBusy(true);
    try {
      const res = src.kind === 'lab'
        ? await tradingAPI.getLabPortfolioAllocation(src.id)
        : await tradingAPI.getPortfolioAllocation(src.code);
      let lines = res.lines || [];
      if (!lines.length) {
        setSaveMsg({ ok: false, text: `${src.label} has no allocation to copy` });
        return;
      }
      // Cash tokens are PLATFORM-SPECIFIC codes — a platform rejects any token that isn't
      // its own. Remap the source's cash-token weight onto the target's existing token
      // rather than importing a foreign one.
      const tokens = new Set(res.cash_tokens || []);
      let remapped = null;
      if (tokens.size) {
        const srcCash = lines.filter((l) => tokens.has(l.position_key));
        const tgtToken = [...existingKeys].find((k) => tokens.has(k) && held(col.portfolio_code, k))
          || [...existingKeys].find((k) => tokens.has(k));
        if (srcCash.length && tgtToken && !srcCash.some((l) => l.position_key === tgtToken)) {
          const w = srcCash.reduce((s, l) => s + (Number(l.target_pct) || 0), 0);
          lines = lines.filter((l) => !tokens.has(l.position_key));
          lines.push({ ...srcCash[0], position_key: tgtToken, apir_code: tgtToken, target_pct: w });
          remapped = `${srcCash.map((l) => l.position_key).join('+')} → ${tgtToken}`;
        }
      }
      const missing = lines.filter((l) => !existingKeys.has(l.position_key));
      if (missing.length) setAddedFunds((prev) => [...prev, ...missing]);
      const next = {};
      existingKeys.forEach((k) => { next[k] = 0; });
      lines.forEach((l) => { next[l.position_key] = (next[l.position_key] || 0) + (Number(l.target_pct) || 0); });
      setProposed((prev) => ({ ...prev, [col.portfolio_code]: next }));
      const skipped = (res.unmatched || []).length
        ? ` — no APIR for ${res.unmatched.join(', ')} (skipped)` : '';
      setSaveMsg({ ok: true, text: `Copied ${src.label} allocation (${res.source}) into ${col.display_name}${remapped ? ` — cash token remapped ${remapped}` : ''}${skipped} — review then Save draft` });
    } catch (e) {
      setSaveMsg({ ok: false, text: e?.response?.data?.detail || 'Copy failed' });
    } finally {
      setCopyBusy(false);
    }
  };

  const doSubmit = async (col) => {
    const pc = col.portfolio_code;
    const lines = buildLines(col);
    if (!lines.length) return { ok: false, text: `${col.display_name}: no target lines` };
    const overrides = {};
    (compliance[pc]?.results || []).filter((r) => r.status === 'flag').forEach((r) => {
      const key = r.position_key || r.rule_code;
      const reason = reasons[`${pc}::${key}`];
      if (reason) overrides[key] = reason;
    });
    try {
      const res = await tradingAPI.submitForApproval(suite, {
        blender_code: col.blender_code, seed_portfolio_code: pc, seed_as_at: col.as_at,
        effective_date: effectiveDate || null,
        notes: versionNote.trim(), lines, overrides });
      setBaseline((prev) => ({ ...prev, [pc]: JSON.parse(JSON.stringify(proposed[pc] || {})) }));
      return { ok: true, text: `${col.display_name} v${res.version}` };
    } catch (e) {
      const d = e?.response?.data?.detail;
      return { ok: false, text: `${col.display_name}: ${d && d.message ? d.message : (typeof d === 'string' ? d : 'submit failed')}` };
    }
  };

  const submitColumn = async (col, bypass = false) => {
    if (!bypass && !(await gateAction('submit', [col]))) return;
    setSubmitting(col.portfolio_code); setSaveMsg(null);
    const r = await doSubmit(col);
    setSaveMsg({ ok: r.ok, text: r.ok ? `Submitted ${r.text} for approval` : r.text });
    if (r.ok) { setVersionNote(''); if (onPendingChanged) onPendingChanged(); }
    setSubmitting(null);
  };

  const submitAll = async (bypass = false) => {
    if (!bypass && !(await gateAction('submitAll', columns))) return;
    setSaveMsg(null); setSubmitting('__all__');
    const oks = [], fails = [];
    for (const col of columns) {
      const blocked = (compliance[col.portfolio_code]?.summary?.hard_fail || 0) > 0;
      if (blocked) { fails.push(`${col.display_name}: hard-stop`); continue; }
      const r = await doSubmit(col);   // sequential — each runs the full compliance gate
      (r.ok ? oks : fails).push(r.text);
    }
    setSubmitting(null); setVersionNote(''); setEffectiveDate('');
    if (onPendingChanged) onPendingChanged();
    setSaveMsg({ ok: fails.length === 0,
      text: `Submitted ${oks.length}/${columns.length}${oks.length ? ` (${oks.join(', ')})` : ''}`
        + (fails.length ? ` — not submitted: ${fails.join(' · ')}` : '') });
  };

  // Re-point the tilt neutrals to a different SAA year without rebuilding the matrix,
  // so proposed edits are preserved.
  const discardDraft = async (col) => {
    if (!col.draft_set_id) return;
    if (!window.confirm(`Discard ${col.display_name}'s ${col.draft_source}? The approved instruction is untouched; unsubmitted draft edits are deleted.`)) return;
    setSaveMsg(null);
    try {
      await tradingAPI.discardDraft(col.draft_set_id);
      setSaveMsg({ ok: true, text: `Discarded ${col.display_name} ${col.draft_source}` });
      setReloadKey((k) => k + 1);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setSaveMsg({ ok: false, text: typeof d === 'string' ? d : 'Could not discard draft' });
    }
  };

  const approvePending = async (col) => {
    if (!col.pending_set_id) return;
    if (!window.confirm(`Approve ${col.display_name} ${col.draft_source} as the standing instruction? This supersedes the current approved version.`)) return;
    setSaveMsg(null);
    try {
      await tradingAPI.approvalDecision(col.pending_set_id, 'approve');
      setSaveMsg({ ok: true, text: `Approved ${col.display_name} ${col.draft_source} — now the standing instruction` });
      setReloadKey((k) => k + 1);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setSaveMsg({ ok: false, text: typeof d === 'string' ? d : 'Approval failed' });
    }
  };

  const changeSaaYear = async (yr) => {
    try {
      const res = await tradingAPI.getSuiteNeutrals(suite, yr);
      setData((prev) => (prev ? { ...prev, neutrals: res.neutrals, neutrals_sac: res.neutrals_sac, neutrals_currency: res.neutrals_currency, saa_year: res.saa_year } : prev));
    } catch (e) {
      setSaveMsg({ ok: false, text: 'Could not load SAA year' });
    }
  };

  const buildLines = (col) => {
    const lines = [];
    rows.forEach(([, g]) => g.forEach((r) => {
      const val = prop(col.portfolio_code, r.position_key);
      if (!val) return;
      lines.push({ position_key: r.position_key, apir_code: r.apir_code, asset_class_group: r.asset_class_group,
        sub_asset_class: r.sub_asset_class, fund_name: r.fund_name, target_pct: val, sort_order: r.sort_order });
    }));
    return lines;
  };

  // Sub-portfolio validation gate: before a save/submit completes, every sub exception
  // must be explicitly confirmed (or re-weighted), with ≠100% totals and SAC tilt
  // differences vs the master flagged. `validation` drives the modal.
  const [validation, setValidation] = useState(null);   // {action, cols: [{col, data, lines}]}
  const gateAction = async (action, cols) => {
    const withData = [];
    for (const col of cols) {
      const lines = buildLines(col);
      try {
        const v = await tradingAPI.validateSubs(col.blender_code, lines);
        if ((v.subs || []).length || !v.model_ok) withData.push({ col, data: v, lines });
      } catch (e) { /* validation unavailable → proceed unguarded */ }
    }
    if (!withData.length && action !== 'submit' && action !== 'submitAll') {
      return true;   // nothing to validate → proceed
    }
    // Submit always confirms through the modal (final check + effective date),
    // even with no exceptions to tick.
    setValidation({ action, cols: withData, allCols: cols });
    return false;
  };

  // Save draft is NOT gated by the exceptions modal — drafts are a work-in-progress and
  // exceptions are only confirmed at submit. Returns {ok, text} for the bulk caller.
  const saveColumn = async (col) => {
    setSaving(col.portfolio_code); setSaveMsg(null);
    const r = await doSaveDraft(col);
    setSaveMsg({ ok: r.ok, text: r.ok ? `Saved ${col.display_name} ${r.text}` : r.text });
    if (r.ok) setVersionNote('');
    setSaving(null);
    return r;
  };

  const doSaveDraft = async (col) => {
    try {
      const lines = buildLines(col);
      const res = await tradingAPI.saveDraft(col.blender_code, {
        notes: versionNote.trim(), seed_portfolio_code: col.portfolio_code, seed_as_at: col.as_at, lines });
      setBaseline((prev) => ({ ...prev, [col.portfolio_code]: JSON.parse(JSON.stringify(proposed[col.portfolio_code] || {})) }));
      if (res.skipped) {
        return { ok: true, skipped: true, text: `no changes vs ${res.unchanged_vs} — nothing saved` };
      }
      return { ok: true, text: `draft v${res.version} — total ${fmt(res.target_total, 1)}%` };
    } catch (e) {
      return { ok: false, text: `${col.display_name}: ${e?.response?.data?.detail || 'save failed'}` };
    }
  };

  // Bulk: save every visible portfolio as a draft (no gate — same as per-column save).
  // Unchanged columns are skipped server-side (no-diff guard) rather than versioned.
  const saveAll = async () => {
    setSaveMsg(null); setSaving('__all__');
    const oks = [], skips = [], fails = [];
    for (const col of columns) {
      const r = await doSaveDraft(col);
      if (r.ok && r.skipped) skips.push(col.display_name);
      else (r.ok ? oks : fails).push(r.ok ? col.display_name : r.text);
    }
    setSaving(null); setVersionNote('');
    setSaveMsg({ ok: fails.length === 0,
      text: `Saved ${oks.length}/${columns.length} draft${oks.length === 1 ? '' : 's'}`
        + (skips.length ? ` · ${skips.length} unchanged (skipped)` : '')
        + (fails.length ? ` — failed: ${fails.join(' · ')}` : '') });
  };

  // Bulk: discard every live no-longer-wanted draft in view (approved instructions untouched).
  const discardAllDrafts = async () => {
    const withDrafts = columns.filter((c) => c.draft_set_id);
    if (!withDrafts.length) { setSaveMsg({ ok: true, text: 'No drafts to discard.' }); return; }
    if (!window.confirm(`Discard ${withDrafts.length} draft${withDrafts.length === 1 ? '' : 's'} (${withDrafts.map((c) => `${c.display_name} ${c.draft_source}`).join(', ')})? Approved instructions are untouched.`)) return;
    setSaveMsg(null); setSaving('__all__');
    const oks = [], fails = [];
    for (const c of withDrafts) {
      try {
        await tradingAPI.discardDraft(c.draft_set_id);
        oks.push(c.display_name);
      } catch (e) {
        const d = e?.response?.data?.detail;
        fails.push(`${c.display_name}: ${typeof d === 'string' ? d : 'discard failed'}`);
      }
    }
    setSaving(null);
    setSaveMsg({ ok: fails.length === 0,
      text: `Discarded ${oks.length}/${withDrafts.length} draft${withDrafts.length === 1 ? '' : 's'}`
        + (fails.length ? ` — failed: ${fails.join(' · ')}` : '') });
    setReloadKey((k) => k + 1);
  };

  // Review & manage the sub exceptions on the visible portfolios (adjust weights) without
  // saving or submitting — opens the exceptions modal in read/manage mode.
  const reviewExceptions = async () => {
    setSaveMsg(null);
    const withData = [];
    for (const col of columns) {
      try {
        const v = await tradingAPI.validateSubs(col.blender_code, buildLines(col));
        if ((v.subs || []).some((s) => (s.exceptions || []).length)) withData.push({ col, data: v });
      } catch (e) { /* validation unavailable → skip this column */ }
    }
    if (!withData.length) { setSaveMsg({ ok: true, text: 'No sub exceptions on the visible portfolios.' }); return; }
    setValidation({ action: 'review', cols: withData, allCols: columns });
  };

  if (!data) return <div className="trading-muted">Loading suite…</div>;
  if (!allColumns.length) return <div className="trading-muted">No portfolios in this suite.</div>;

  return (
    <>
      <header className="trading-header">
        <div>
          <h2>{suite.split(',').join(' + ')}</h2>
          <div className="trading-header-sub">
            <button className="trading-link" title="Choose which portfolios to trade on in this suite (remembered)"
              onClick={() => setShowPicker((v) => !v)}>
              Portfolios ({columns.length}/{allColumns.length}) {showPicker ? '▲' : '▼'}
            </button>
            <div className="tilt-toggle grid-level" title="Row grouping — the three AP List taxonomy levels">
              {[['acg', 'Asset class group', 'Broadest grouping (default)'], ['ac', 'Asset class', "AP List's middle level"],
                ['sac', 'Sub-asset class', 'Finest level — SACs with friendly names']].map(([k, lbl, tip]) => (
                  <button key={k} className={groupLevel === k ? 'active' : ''} title={tip} onClick={() => setGroupLevel(k)}>{lbl}</button>
              ))}
            </div>
            <button className={`trading-btn addfund-btn ${showAddFund ? 'active' : ''}`} onClick={() => setShowAddFund((v) => !v)}>
              {showAddFund ? '× Close' : '+ Add fund'}
            </button>
            <button className="trading-link" title="Show/hide the Management Fees & Costs, Rebate and Net columns (remembered)"
              onClick={toggleMerCols}>
              {showMerCols ? '▾ Fee cols' : '▸ Fee cols'}
            </button>
            <button className="trading-link" title="Download this grid as xlsx" onClick={exportExcel}>⇓ Excel</button>
            <button className={`trading-link ${hideHeaderDetail ? 'active' : ''}`}
              title="Header shows just the portfolio name — hide the code/version badges & links (remembered)"
              onClick={toggleHideHeader}>{hideHeaderDetail ? '☑' : '☐'} Simple headers</button>
            <button className={`trading-link ${hideUnchanged ? 'active' : ''}`}
              title="Hide fund rows whose proposed weight equals current across all shown portfolios (remembered)"
              onClick={toggleHideUnchanged}>{hideUnchanged ? '☑' : '☐'} Hide unchanged</button>
            <button className={`trading-link ${deltasOnly ? 'active' : ''}`}
              title="Show only the change (Δ) column per portfolio — narrow columns for easy side-by-side comparison; SAA tilts show as numbers (remembered)"
              onClick={toggleDeltasOnly}>{deltasOnly ? '☑' : '☐'} Δ only</button>
            <button className={`trading-link ${hideFunds ? 'active' : ''}`}
              title="Hide the fund holdings & asset-class rows — just show the SAA tilts (remembered)"
              onClick={toggleHideFunds}>{hideFunds ? '☑' : '☐'} Hide funds</button>
            <button className={`trading-link ${hideSaaTilts ? 'active' : ''}`}
              title="Hide the SAA tilts section (remembered)"
              onClick={toggleHideSaa}>{hideSaaTilts ? '☑' : '☐'} Hide SAA tilts</button>
            <button className={`trading-link ${tiltHeat ? 'active' : ''}`}
              title="Conditional formatting on the SAA tilts — shade each cell by tilt size (green over/increase, red under/decrease) (remembered)"
              onClick={toggleTiltHeat}>{tiltHeat ? '☑' : '☐'} Tilt heatmap</button>
            <button className="trading-link" title="Export the table exactly as shown (respects the view toggles) as a PNG"
              disabled={exporting} onClick={exportPng}>{exporting ? '… PNG' : '🖼 Export PNG'}</button>
            <button className="trading-link" title="Review & adjust the recorded sub exceptions on the visible portfolios"
              onClick={reviewExceptions}>⚑ Review exceptions</button>
            <button className="trading-link" title="Save every portfolio in view as a draft (no submission; unchanged columns are skipped)"
              disabled={saving === '__all__'} onClick={() => saveAll()}>
              {saving === '__all__' ? 'Saving…' : '⇊ Save all drafts'}
            </button>
            {columns.some((c) => c.draft_set_id) && (
              <button className="trading-link" title="Discard every live draft in view (approved instructions untouched)"
                disabled={saving === '__all__'} onClick={() => discardAllDrafts()}>
                ✕ Discard all drafts
              </button>
            )}
            <button className="trading-link" title="Submit every portfolio in view for approval (skips hard-stopped ones)"
              disabled={submitting === '__all__'} onClick={() => submitAll()}>
              {submitting === '__all__' ? 'Submitting…' : '⇈ Submit all'}
            </button>
            <span className="suite-search">
              <input placeholder="Find fund…" value={fundFilter} onChange={(e) => setFundFilter(e.target.value)} />
              {fundFilter && <button className="trading-x sm" onClick={() => setFundFilter('')}>✕</button>}
              {fundFilter && <span className="trading-muted">{shownFunds}/{totalFunds}</span>}
            </span>
            <input className="version-note" placeholder="Version note (optional, saved on draft/submit)…"
              value={versionNote} onChange={(e) => setVersionNote(e.target.value)} />
            {isDirty && (
              <span className="dirty-chip" title="Proposed edits not yet saved as a draft">
                unsaved edits
                <button className="trading-link" onClick={discardAll}>discard all</button>
              </span>
            )}
          </div>
        </div>
      </header>

      {showPicker && (
        <div className="trading-master">
          <div className="trading-master-head">
            <span className="trading-muted">Select the portfolios to trade on in this suite (remembered)</span>
            <button className="trading-link" onClick={() => setHiddenPersist(new Set())}>All</button>
            <button className="trading-link" onClick={() => setHiddenPersist(new Set(allColumns.filter((c) => !c.seeded).map((c) => c.portfolio_code)))}>With targets only</button>
            <button className="trading-link" onClick={() => setHiddenPersist(new Set(allColumns.map((c) => c.portfolio_code)))}>None</button>
          </div>
          <div className="trading-master-list">
            {allColumns.map((c) => (
              <label key={c.portfolio_code} className="trading-master-item">
                <input type="checkbox" checked={!effectiveHidden.has(c.portfolio_code)} onChange={() => togglePortfolio(c.portfolio_code)} />
                <span className="trading-mname">{c.display_name}</span>
                <span className="trading-code">{c.portfolio_code}</span>
                {c.target_source && <span className="veh-newtag approved picker-tag">{c.target_source}</span>}
                {c.draft_source && <span className="veh-newtag picker-tag">{c.draft_source}</span>}
                {!c.target_source && !c.draft_source && <span className="trading-muted">no target</span>}
              </label>
            ))}
          </div>
        </div>
      )}

      {showAddFund && <AddFundPanel onAdd={addFund} existingKeys={existingKeys} onClose={() => setShowAddFund(false)} />}
      {saveMsg && <div className={`trading-save-msg ${saveMsg.ok ? 'ok' : 'err'}`}>{saveMsg.text}</div>}
      {!columns.length && (
        <div className="trading-muted">All portfolios deselected — open the Portfolios picker above to choose which to trade on.</div>
      )}
      {columns.length > 0 && !rows.length && (
        <div className="trading-save-msg ok">
          No canonical targets stored for these portfolios yet — <strong>⇩ import Shadow</strong> on a column for a
          one-time import, or build from scratch with <strong>+ Add fund</strong> / <strong>⧉ Copy from…</strong>,
          then Save draft / Submit.
        </div>
      )}

      {columns.length > 0 && (
      <div className="trading-grid-wrap">
        <table ref={gridExportRef} className={`trading-grid matrix triple${showMerCols ? '' : ' hide-mer'}${hideHeaderDetail ? ' simple-hdr' : ''}${deltasOnly ? ' deltas-only' : ''}`}>
          <thead>
            <tr>
              <th className="l sticky-l" rowSpan={2}>Fund Name</th>
              <th className="l code-col" rowSpan={2}>Code</th>
              <th className="mer-col" rowSpan={2} title="Management Fees & Costs — from the FE fee table; manual override wins">Mgmt Fees &amp; Costs %</th>
              <th className="rebate-col" rowSpan={2} title="Manual — fund rebate">Rebate %</th>
              <th className="netmer-col" rowSpan={2} title="Management Fees & Costs less rebate">Net %</th>
              {columns.map((c) => (
                <th key={c.portfolio_code} className="port-group clickable" colSpan={deltasOnly ? 1 : 3} title="Open vehicles & overrides"
                    onClick={() => onDrill(c.blender_code, c.display_name)}>
                  <div className="veh-name">{c.display_name}</div>
                  {!hideHeaderDetail && (<>
                  <div className="veh-plat">{c.blender_code} ›</div>
                  {c.target_source && (
                    <div className="veh-newtag approved"
                         title={`Standing instruction (${c.target_source}${c.as_at ? `, ${c.as_at}` : ''})`}>
                      {c.target_source}
                    </div>
                  )}
                  {c.draft_source && (
                    <div className="veh-newtag"
                         title="Awaiting approval — loaded into the Proposed column">
                      {c.draft_source}
                      {c.draft_set_id && (
                        <button className="draft-discard" title="Discard this draft (approved instruction untouched)"
                          onClick={(e) => { e.stopPropagation(); discardDraft(c); }}>✕</button>
                      )}
                    </div>
                  )}
                  {!c.target_source && !c.draft_source && (
                    <button className="veh-import" title="One-time import of this vehicle's Shadow_Data target as a draft"
                      onClick={(e) => { e.stopPropagation(); importShadow(c); }}>
                      ⇩ import Shadow
                    </button>
                  )}
                  <div className="hdr-links">
                    <button title="Quick compare — this portfolio's Proposed vs another portfolio"
                      onClick={(e) => { e.stopPropagation(); setCompareCol(c); }}>⇄</button>
                    <button title="Version history (audit evolution)"
                      onClick={(e) => { e.stopPropagation(); if (onEvolution) onEvolution(c.blender_code); }}>⏱</button>
                    <button title="Cascade map (subs & exceptions)"
                      onClick={(e) => { e.stopPropagation(); setCascadeCol(c); }}>⛓</button>
                  </div>
                  </>)}
                </th>
              ))}
            </tr>
            <tr className="subhead">
              {columns.map((c) => (
                <React.Fragment key={c.portfolio_code}>
                  {!deltasOnly && <th className="cur-col" title="The standing instruction — last APPROVED target (fixed)">Current</th>}
                  <th className="chg-col" title="Change vs current — type here to add/subtract; Proposed solves automatically">Δ</th>
                  {!deltasOnly && <th className="prop-col" title="The new target weight — type here directly; Δ solves automatically">Proposed</th>}
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {!hideFunds && rows.map(([ac, groupRows], gi) => {
              // "Changes only" hides fund rows unchanged across every shown portfolio.
              // Asset-class totals still sum the FULL group so they stay accurate.
              const shownRows = hideUnchanged
                ? groupRows.filter((r) => columns.some((c) =>
                    Math.abs(prop(c.portfolio_code, r.position_key) - cur(c.portfolio_code, r.position_key)) > 0.005))
                : groupRows;
              if (hideUnchanged && !shownRows.length) return null;
              return (
              <React.Fragment key={ac}>
                <tr className="ac-header">
                  <td className="l sticky-l" colSpan={2}><span className="ac-dot" style={{ background: acColour(groupRows[0]?.asset_class_group || ac, gi) }} />{ac}</td>
                  <td className="mer-col" /><td className="rebate-col" /><td className="netmer-col" />
                  {columns.map((c) => {
                    const curT = groupRows.reduce((s, r) => s + cur(c.portfolio_code, r.position_key), 0);
                    const propT = groupRows.reduce((s, r) => s + prop(c.portfolio_code, r.position_key), 0);
                    const empty = Math.abs(curT) < 0.05 && Math.abs(propT) < 0.05;
                    return (
                      <React.Fragment key={c.portfolio_code}>
                        {!deltasOnly && <td className="cur-col ac-total">{empty ? '' : `${fmt(curT, 1)}%`}</td>}
                        <td className="chg-col ac-total">{Math.abs(propT - curT) < 0.05 ? '' : fmt(propT - curT, 1)}</td>
                        {!deltasOnly && <td className="prop-col ac-total">{empty ? '' : `${fmt(propT, 1)}%`}</td>}
                      </React.Fragment>
                    );
                  })}
                </tr>
                {shownRows.map((r) => (
                  <tr key={r.position_key} className="holding-row">
                    <td className="l sticky-l trading-fund" title={r.fund_name || r.apir_code}>
                      {deletable(r.position_key) && (
                        <button className="fund-del" title="Remove row (no weights either side)"
                          onClick={() => removeFund(r.position_key)}>✕</button>
                      )}
                      {r.fund_name || r.apir_code}
                    </td>
                    <td className="l code-col">{r.apir_code || r.position_key}</td>
                    <td className="mer-col">
                      <span className="mer-input">
                        <input className="trading-input" type="number" step="0.01" placeholder="—" onKeyDown={gridNav}
                          value={merRaw(r)} onChange={(e) => setMerVal(r.position_key, e.target.value)}
                          onBlur={() => persistFundAttr(r)} />
                        <span className="mer-pct">%</span>
                      </span>
                    </td>
                    <td className="rebate-col">
                      <span className="mer-input">
                        <input className="trading-input" type="number" step="0.01" placeholder="—" onKeyDown={gridNav}
                          value={rebateRaw(r)} onChange={(e) => setRebateVal(r.position_key, e.target.value)}
                          onBlur={() => persistFundAttr(r)} />
                        <span className="mer-pct">%</span>
                      </span>
                    </td>
                    <td className="netmer-col">{merRaw(r) === '' && rebateRaw(r) === '' ? '' : <>{fmt(netMerNum(r), 2)}<span className="mer-pct">%</span></>}</td>
                    {columns.map((c) => {
                      const pc = c.portfolio_code, k = r.position_key;
                      const cv = cur(pc, k), pv = prop(pc, k), dv = pv - cv;
                      return (
                        <React.Fragment key={pc}>
                          {!deltasOnly && <td className="cur-col">{held(pc, k) ? fmt(cv, 2) : <span className="trading-muted">–</span>}</td>}
                          <td className="chg-col">
                            <input className="trading-input" type="number" step="0.1" onKeyDown={gridNav}
                              data-pc={pc} data-key={k} onPaste={onGridPaste(true)}
                              value={Number.isFinite(dv) && Math.abs(dv) > 0.0005 ? Number(dv.toFixed(4)) : ''}
                              onChange={(e) => setProp(pc, k, cv + (parseFloat(e.target.value) || 0))} />
                          </td>
                          {!deltasOnly && (
                          <td className="prop-col">
                            <input className="trading-input" type="number" step="0.1" onKeyDown={gridNav}
                              data-pc={pc} data-key={k} onPaste={onGridPaste(false)}
                              value={proposed[pc]?.[k] ?? (held(pc, k) ? Number(cv.toFixed(4)) : '')} placeholder="—"
                              onChange={(e) => setProp(pc, k, e.target.value === '' ? 0 : (parseFloat(e.target.value) || 0))} />
                          </td>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
              );
            })}
            <tr className="trading-total-row">
              <td className="l sticky-l" colSpan={2} title="Proposed must sum to 100% (excl. geared) before submission">Total</td>
              <td className="mer-col" /><td className="rebate-col" /><td className="netmer-col" />
              {columns.map((c) => {
                const { curT, propT } = totals[c.portfolio_code] || { curT: 0, propT: 0 };
                const ok = Math.abs(propT - 100) <= 0.05;
                return (
                  <React.Fragment key={c.portfolio_code}>
                    {!deltasOnly && <td className="cur-col">{Math.abs(curT) < 0.05 ? '' : `${fmt(curT, 1)}%`}</td>}
                    <td className="chg-col">{Math.abs(propT - curT) < 0.05 ? '' : fmt(propT - curT, 1)}</td>
                    {!deltasOnly && <td className={`prop-col ${ok ? 'ok' : 'err'}`}>{fmt(propT, 1)}%</td>}
                  </React.Fragment>
                );
              })}
            </tr>
            {[
              { key: 'G', label: 'Growth', tip: 'Σ weight × growth factor (per sub-asset-class, from the investment-config taxonomy)', cf: (s) => s.cG, pf: (s) => s.pG, dp: 1, suffix: '%' },
              { key: 'D', label: 'Defensive', tip: 'Total − growth exposure', cf: (s) => s.cDef, pf: (s) => s.pDef, dp: 1, suffix: '%' },
              { key: 'C', label: 'Currency', tip: 'Unhedged (non-cash) foreign-currency exposure — Σ weight of unhedged holdings', cf: (s) => s.cC, pf: (s) => s.pC, dp: 1, suffix: '%' },
              { key: 'M', label: 'Fees & Costs (wtd)', tip: 'Weighted-average cost: Σ (weight ÷ 100 × Management Fees & Costs %)', cf: (s) => s.cM, pf: (s) => s.pM, dp: 2, suffix: '%' },
              { key: 'N', label: 'Net (wtd)', tip: 'Weighted-average cost after rebates: Σ (weight ÷ 100 × (Management Fees & Costs − rebate))', cf: (s) => s.cN, pf: (s) => s.pN, dp: 2, suffix: '%' },
            ].map((m) => (
              <tr key={m.key} className={`trading-summary-row sum-${m.key}`}>
                <td className="l sticky-l" colSpan={2} title={m.tip}>{m.label}</td>
                <td className="mer-col" /><td className="rebate-col" /><td className="netmer-col" />
                {columns.map((c) => {
                  const s = summary[c.portfolio_code] || {};
                  const cv = m.cf(s) || 0, pv = m.pf(s) || 0;
                  const tol = m.dp >= 2 ? 0.005 : 0.05;
                  return (
                    <React.Fragment key={c.portfolio_code}>
                      {!deltasOnly && <td className="cur-col">{Math.abs(cv) < tol ? '' : `${fmt(cv, m.dp)}${m.suffix}`}</td>}
                      <td className="chg-col">{Math.abs(pv - cv) < tol ? '' : fmt(pv - cv, m.dp)}</td>
                      {!deltasOnly && <td className="prop-col">{Math.abs(pv) < tol ? '' : `${fmt(pv, m.dp)}${m.suffix}`}</td>}
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
            {!hideSaaTilts && (<>
            <tr className="trading-tilt-head">
              <td className="l sticky-l" colSpan={2}>
                <div className="tilt-head-top">
                  <span title="Deviation from the strategic asset allocation neutral — bar right = overweight, left = underweight">SAA tilts</span>
                  {data?.saa_years?.length ? (
                    <select className="saa-year-select" value={data?.saa_year || ''}
                            title="SAA vintage the tilts are measured against (edits are kept when switching)"
                            onChange={(e) => changeSaaYear(Number(e.target.value))}>
                      {data.saa_years.map((y) => <option key={y} value={y}>FY{y}</option>)}
                    </select>
                  ) : (data?.saa_year ? <span> (FY{data.saa_year})</span> : null)}
                </div>
                <div className="tilt-selects">
                  <select className="saa-year-select" value={tiltLevel} onChange={(e) => setTiltLevel(e.target.value)} title="Aggregation level">
                    <option value="acg">Asset class group</option>
                    <option value="ac">Asset class</option>
                    <option value="sac">Sub-asset class</option>
                  </select>
                  <select className="saa-year-select" value={tiltBasis} onChange={(e) => setTiltBasis(e.target.value)} title="Weights basis">
                    <option value="cur">Current vs SAA</option>
                    <option value="prop">Proposed vs SAA</option>
                    <option value="delta">Δ change</option>
                  </select>
                </div>
                <div className="tilt-legend">
                  {tiltBasis === 'delta' ? 'bar = weight change (proposed − current)' : 'colour = asset class · left = under, right = over vs SAA'}
                </div>
              </td>
              <td className="mer-col" /><td className="rebate-col" /><td className="netmer-col" />
              {columns.map((c) => (
                <td key={c.portfolio_code} className="tilt-headcell" colSpan={deltasOnly ? 1 : 3}>
                  {tiltBasis !== 'delta' && !(summary[c.portfolio_code] || {}).hasSAA
                    ? <span className="trading-muted">no SAA</span> : ''}
                </td>
              ))}
            </tr>
            {tiltRows.items.map((it) => {
              const colour = acColour(it.parent, acRank(it.parent));
              // Heatmap is row-wise: shade by tilt size relative to the biggest tilt in THIS
              // asset-class row (compare portfolios within a class), not the whole grid.
              const rowMax = columns.reduce((m, c) => {
                const rv = tiltRows.val(summary[c.portfolio_code], it.key);
                return rv != null && Number.isFinite(rv) ? Math.max(m, Math.abs(rv)) : m;
              }, 0);
              return (
                <tr key={`tilt-${it.key}`} className={`trading-tilt-ac ${tiltLevel === 'sac' ? 'sac' : ''}`}>
                  <td className="l sticky-l" colSpan={2}>
                    <span className="ac-dot" style={{ background: colour }} />{it.label}
                  </td>
                  <td className="mer-col" /><td className="rebate-col" /><td className="netmer-col" />
                  {columns.map((c) => {
                    const v = tiltRows.val(summary[c.portfolio_code], it.key);
                    return (
                      <td key={c.portfolio_code} className="tilt-cell" colSpan={deltasOnly ? 1 : 3}
                          style={{ background: tiltHeatBg(v, rowMax) }}>
                        {v == null ? null : (deltasOnly
                          ? <span className={`tilt-num ${v > 0.05 ? 'pos' : v < -0.05 ? 'neg' : ''}`}
                              title={tiltBasis === 'delta' ? '% vs current' : '% vs SAA'}>{fmt(v, 1)}</span>
                          : <TiltBar value={v} max={tiltRows.max} color={colour}
                              suffix={tiltBasis === 'delta' ? '% vs current' : '% vs SAA'} />)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            </>)}
            <tr className="trading-compliance-row">
              <td className="l sticky-l" colSpan={2}
                  title="Live pre-trade checks on the proposed weights — hard-stops block submission, flags need a reason (details below the grid)">Compliance</td>
              <td className="mer-col" /><td className="rebate-col" /><td className="netmer-col" />
              {columns.map((c) => {
                const s = compliance[c.portfolio_code]?.summary;
                const worst = s?.worst || 'pass';
                const txt = worst === 'fail' ? `✗ ${s.hard_fail} block`
                  : worst === 'flag' ? `⚠ ${s.soft_flag} flag` : '✓ clear';
                return (
                  <td key={c.portfolio_code} className="prop-col" colSpan={deltasOnly ? 1 : 3}>
                    <span className={`compliance-badge ${worst}`}>{txt}</span>
                  </td>
                );
              })}
            </tr>
            {!anyHidden && (
            <tr className="trading-save-row no-export">
              <td className="l sticky-l" colSpan={2}></td>
              <td className="mer-col" /><td className="rebate-col" /><td className="netmer-col" />
              {columns.map((c) => {
                const s = compliance[c.portfolio_code]?.summary;
                const blocked = (s?.hard_fail || 0) > 0;
                return (
                  <td key={c.portfolio_code} className="prop-col" colSpan={deltasOnly ? 1 : 3}>
                    <div className="save-actions">
                      <button className="copy-open" title="Copy another portfolio's allocation into Proposed (trading models or Portfolio Lab)"
                        onClick={() => setCopyModalCol(c)}>⧉ Copy from…</button>
                      <div className="save-btns">
                        {colDirty(c.portfolio_code) && (
                          <button className="trading-btn sm" title="Revert this portfolio's edits to the loaded draft"
                            onClick={() => revertColumn(c.portfolio_code)}>↺</button>
                        )}
                        <button className="trading-btn sm" disabled={saving === c.portfolio_code}
                          title="Store a new draft version (not submitted — reloads into Proposed)" onClick={() => saveColumn(c)}>
                          {saving === c.portfolio_code ? '…' : 'Save draft'}
                        </button>
                        <button className="trading-btn primary sm" disabled={submitting === c.portfolio_code || blocked}
                          title={blocked ? 'Resolve hard-stops first' : 'Submit for approval'} onClick={() => submitColumn(c)}>
                          {submitting === c.portfolio_code ? '…' : 'Submit'}
                        </button>
                        {c.pending_set_id && (
                          <button className="trading-btn approve sm" disabled={submitting === c.portfolio_code}
                            title={`Approve ${c.draft_source} as the standing instruction (maker-checker: the author cannot approve their own submission)`}
                            onClick={() => approvePending(c)}>
                            ✓ Approve
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                );
              })}
            </tr>
            )}
          </tbody>
        </table>
      </div>
      )}

      {columns.length > 0 && (
        <ComplianceReport columns={columns} compliance={compliance} reasons={reasons} setReason={setReason} />
      )}

      {columns.length > 0 && (
        <div className="trading-embedded-report">
          <ChangesReport suites={allSuites} lockedCodes={columns.map((c) => c.blender_code).filter(Boolean)} />
        </div>
      )}

      <ValidationModal validation={validation} onClose={() => setValidation(null)} onConfirm={confirmValidation}
        effectiveDate={effectiveDate} onEffectiveDateChange={setEffectiveDate} />
      {copyModalCol && (
        <CopyFromModal col={copyModalCol} allSuites={allSuites} labPortfolios={labPortfolios} busy={copyBusy}
          onPick={async (src) => { await copyFrom(copyModalCol, src); setCopyModalCol(null); }}
          onClose={() => setCopyModalCol(null)} />
      )}
      {compareCol && (
        <CompareModal col={compareCol} columns={allColumns} rows={rows} cur={cur} prop={prop}
          onClose={() => setCompareCol(null)} />
      )}
      {cascadeCol && (
        <CascadeModal blenderCode={cascadeCol.blender_code} displayName={cascadeCol.display_name}
          onClose={() => setCascadeCol(null)} />
      )}
    </>
  );
}

/* ─────────── Compliance report: per-portfolio hard-stops + soft-flags (with reasons) ─────────── */
function ComplianceReport({ columns, compliance, reasons, setReason }) {
  const withIssues = columns.filter((c) => {
    const s = compliance[c.portfolio_code]?.summary;
    return s && (s.hard_fail > 0 || s.soft_flag > 0);
  });
  if (!withIssues.length) {
    return <div className="compliance-report clear">✓ All portfolios pass pre-trade compliance.</div>;
  }
  return (
    <div className="compliance-report">
      <h4>Pre-trade compliance</h4>
      {withIssues.map((c) => {
        const pc = c.portfolio_code;
        const results = (compliance[pc]?.results || []).filter((r) => r.status !== 'pass');
        return (
          <div key={pc} className="cr-portfolio">
            <div className="cr-head">{c.display_name} <span className="trading-muted">{c.blender_code}</span></div>
            {results.map((r, i) => {
              const key = r.position_key || r.rule_code;
              return (
                <div key={i} className={`cr-item ${r.status}`}>
                  <span className={`cr-tag ${r.status}`}>{r.status === 'fail' ? 'HARD' : 'FLAG'}</span>
                  <span className="cr-detail">{r.detail || r.label}</span>
                  {r.status === 'flag' && (
                    <input className="cr-reason" placeholder="Reason to accept…"
                      value={reasons[`${pc}::${key}`] || ''} onChange={(e) => setReason(pc, key, e.target.value)} />
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
      <div className="cr-note">Hard-stops must be fixed. Each flag needs a reason before Submit.</div>
    </div>
  );
}

/* ═══════════════ Strategy view (drill): Model + vehicles + overrides ═══════════════ */
function StrategyView({ blenderCode, displayName, onBack }) {
  const [seed, setSeed] = useState(null);
  const [loadingSeed, setLoadingSeed] = useState(true);
  const [modelTargets, setModelTargets] = useState({});
  const [addedFunds, setAddedFunds] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [shownCodes, setShownCodes] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [showMaster, setShowMaster] = useState(false);
  const [vehSearch, setVehSearch] = useState('');
  const [showAddFund, setShowAddFund] = useState(false);
  const [showOv, setShowOv] = useState(false);
  const [matrixKey, setMatrixKey] = useState(0);
  const [vehEdits, setVehEdits] = useState({});      // {pc: {key: value}} — uncommitted sub exceptions
  const [ovReason, setOvReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  useEffect(() => {
    setLoadingSeed(true); setVehEdits({}); setOvReason('');
    tradingAPI.getSeed(blenderCode).then((d) => {
      setSeed(d);
      const t = {}; (d.lines || []).forEach((l) => { t[l.position_key] = Number(l.target_pct) || 0; });
      setModelTargets(t);
    }).catch(() => setSeed({ lines: [], error: true })).finally(() => setLoadingSeed(false));
    tradingAPI.getVehicles(blenderCode).then((d) => setVehicles(d.vehicles || [])).catch(() => setVehicles([]));
  }, [blenderCode]);

  useEffect(() => {
    if (shownCodes.length === 0) { setMatrix(null); return; }
    tradingAPI.getMatrix(blenderCode, shownCodes).then(setMatrix).catch(() => setMatrix(null));
  }, [blenderCode, shownCodes, matrixKey]);

  const rows = useMemo(() => {
    const map = new Map();
    (seed?.lines || []).forEach((l) => map.set(l.position_key, { ...l, key: l.position_key }));
    (matrix?.funds || []).forEach((f) => { if (!map.has(f.position_key)) map.set(f.position_key, { ...f, key: f.position_key }); });
    addedFunds.forEach((f) => { if (!map.has(f.position_key)) map.set(f.position_key, { ...f, key: f.position_key, added: true }); });
    return groupByAssetClass(Array.from(map.values()).map((r) => ({ ...r, sort_order: r.sort_order ?? 0 })));
  }, [seed, matrix, addedFunds]);

  const existingKeys = useMemo(() => { const s = new Set(); rows.forEach(([, g]) => g.forEach((r) => s.add(r.position_key))); return s; }, [rows]);
  const shownVehicles = matrix?.vehicles || [];
  const modelTotal = useMemo(() => Object.values(modelTargets).reduce((a, b) => a + (Number(b) || 0), 0), [modelTargets]);
  const sumOk = Math.abs(modelTotal - 100) <= 0.05;
  // Recorded overrides (deliberate sub exceptions) + uncommitted cell edits
  const totalOverrides = useMemo(() => Object.values(matrix?.overrides || {})
    .reduce((a, m) => a + Object.keys(m).length, 0), [matrix]);
  const ovByVehicle = useMemo(() => {
    const m = {};
    Object.entries(matrix?.overrides || {}).forEach(([pc, o]) => { m[pc] = Object.keys(o).length; });
    return m;
  }, [matrix]);
  const pendingEdits = useMemo(() => {
    const out = [];
    Object.entries(vehEdits).forEach(([pc, m]) => Object.entries(m).forEach(([k, v]) => {
      if (v === '' || v === null) return;
      const resolved = matrix?.weights?.[pc]?.[k] ?? 0;
      if (Math.abs(Number(v) - resolved) > 0.0049) out.push({ pc, k, v: Number(v) });
    }));
    return out;
  }, [vehEdits, matrix]);

  const commitOverrides = async () => {
    if (!pendingEdits.length || !ovReason.trim()) return;
    setSaveMsg(null);
    try {
      // a fund not in the stored model is an ADD (extra fund for this sub), not a reweight
      const modelKeys = new Set((seed?.lines || []).map((l) => l.position_key));
      const list = pendingEdits.map(({ pc, k, v }) => ({
        portfolio_code: pc, position_key: k,
        override_type: v === 0 ? 'exclude' : (modelKeys.has(k) ? 'reweight' : 'add'),
        apir_code: k, target_pct: v === 0 ? null : v,
        reason_note: ovReason.trim(), active: true,
      }));
      await tradingAPI.saveOverrides(blenderCode, list);
      setVehEdits({}); setOvReason(''); setMatrixKey((x) => x + 1);
      setSaveMsg({ ok: true, text: `Recorded ${list.length} sub exception${list.length === 1 ? '' : 's'}` });
    } catch (e) {
      setSaveMsg({ ok: false, text: e?.response?.data?.detail || 'Override save failed' });
    }
  };
  const removeOverride = async (id) => {
    try { await tradingAPI.deleteOverride(id); setMatrixKey((x) => x + 1); }
    catch (e) { setSaveMsg({ ok: false, text: 'Delete failed' }); }
  };
  const vehiclesByPlatform = useMemo(() => {
    const q = vehSearch.trim().toLowerCase();
    const list = !q ? vehicles : vehicles.filter((v) => `${v.portfolio_code} ${v.portfolio_name} ${v.platform} ${v.client_domain}`.toLowerCase().includes(q));
    const g = {}; list.forEach((v) => (g[v.platform || 'Other'] = g[v.platform || 'Other'] || []).push(v));
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [vehicles, vehSearch]);

  const toggleVehicle = (code) => setShownCodes((prev) => prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]);
  const addFund = (f) => { setAddedFunds((prev) => [...prev, f]); setModelTargets((prev) => ({ ...prev, [f.position_key]: 0 })); };

  const saveDraft = async () => {
    setSaving(true); setSaveMsg(null);
    try {
      const lines = [];
      rows.forEach(([, g]) => g.forEach((r) => {
        const val = Number(modelTargets[r.key]);
        if (!Number.isFinite(val) || val === 0) return;
        lines.push({ position_key: r.key, apir_code: r.apir_code, asset_class_group: r.asset_class_group,
          sub_asset_class: r.sub_asset_class, fund_name: r.fund_name, target_pct: val, sort_order: r.sort_order });
      }));
      const res = await tradingAPI.saveDraft(blenderCode, { notes: '', seed_portfolio_code: seed.seed_portfolio_code, seed_as_at: seed.as_at, lines });
      setSaveMsg({ ok: true, text: `Saved model draft v${res.version} — total ${fmt(res.target_total, 1)}%` });
      setMatrixKey((x) => x + 1);
    } catch (e) { setSaveMsg({ ok: false, text: e?.response?.data?.detail || 'Save failed' }); } finally { setSaving(false); }
  };

  return (
    <>
      <header className="trading-header">
        <div>
          <h2><button className="trading-back" onClick={onBack}>‹ Suite</button> {displayName || blenderCode}</h2>
          <div className="trading-header-sub">
            <span className="trading-code">{blenderCode}</span>
            <button className="trading-link" onClick={() => setShowMaster((v) => !v)}>Vehicles ({shownCodes.length}/{vehicles.filter((v) => v.is_active !== false).length}) {showMaster ? '▲' : '▼'}</button>
            <button className="trading-link" onClick={() => setShowAddFund((v) => !v)}>{showAddFund ? 'Close add fund' : '+ Add fund'}</button>
            {shownCodes.length > 0 && matrix && (
              <button className="trading-link" onClick={() => setShowOv((v) => !v)}>
                Sub exceptions ({totalOverrides} recorded) {showOv ? '▲' : '▼'}
              </button>
            )}
            {seed?.seed_portfolio_code && <span className="trading-muted">model: {seed.seed_portfolio_code} · as at {seed.as_at}</span>}
          </div>
        </div>
        <div className="trading-actions">
          <button className="trading-btn primary" onClick={saveDraft} disabled={saving || loadingSeed}>{saving ? 'Saving…' : 'Save model draft'}</button>
        </div>
      </header>

      {showMaster && (
        <div className="trading-master">
          <div className="trading-master-head">
            <input placeholder="Filter vehicles…" value={vehSearch} onChange={(e) => setVehSearch(e.target.value)} />
            <button className="trading-link" onClick={() => setShownCodes(vehicles.filter((v) => v.is_active !== false).map((v) => v.portfolio_code))}>Show all</button>
            <button className="trading-link" onClick={() => setShownCodes([])}>Hide all</button>
          </div>
          <div className="trading-master-list">
            {vehiclesByPlatform.map(([plat, vs]) => (
              <div key={plat} className="trading-master-group">
                <div className="trading-master-plat">{plat}</div>
                {vs.map((v) => {
                  const dead = v.is_active === false;
                  return (
                    <label key={v.portfolio_code} className="trading-master-item"
                      style={dead ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
                      title={dead ? 'Marked dead in Portfolio Mapping — excluded from targets and trade sheets' : undefined}>
                      <input type="checkbox" disabled={dead}
                        checked={!dead && shownCodes.includes(v.portfolio_code)}
                        onChange={() => !dead && toggleVehicle(v.portfolio_code)} />
                      <span className="trading-code">{v.portfolio_code}</span>
                      <span className="trading-mname">{v.portfolio_name}</span>
                      {dead && <span className="trading-vdom" style={{ color: '#dc3545', fontWeight: 600 }}>DEAD</span>}
                      {v.client_domain && <span className="trading-vdom">{v.client_domain}</span>}
                    </label>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {showAddFund && <AddFundPanel onAdd={addFund} existingKeys={existingKeys} onClose={() => setShowAddFund(false)} />}
      {showOv && matrix && (
        <div className="trading-div-panel">
          {totalOverrides === 0 ? <div className="trading-muted">No sub exceptions recorded — every vehicle follows the model exactly. Edit a vehicle's cell to record one.</div> : (
            Object.entries(matrix.overrides || {}).filter(([, m]) => Object.keys(m).length).map(([pc, m]) => (
              <div key={pc} className="trading-div-veh">
                <div className="trading-div-vhead"><strong>{pc}</strong>
                  <span className="trading-muted">{(matrix.vehicles || []).find((v) => v.portfolio_code === pc)?.portfolio_name || ''}</span></div>
                {Object.entries(m).map(([k, o]) => (
                  <div key={k} className="trading-div-item">
                    <span className={`div-tag ${o.type === 'fund_substitution' ? 'substitution' : o.type === 'exclude' ? 'model_only' : o.type === 'add' ? 'vehicle_only' : 'reweight'}`}>{o.type.replace('_', ' ')}</span>
                    {o.source === 'platform' && (
                      <span className="veh-newtag" title="Derived from the platform unit-class mapping — manage it in the Unit class mapping tab">unit class map</span>
                    )}
                    <span className="div-side">{(rows.flatMap(([, g]) => g).find((r) => r.key === k)?.fund_name) || k}</span>
                    {o.target_pct != null && <span>{fmt(o.target_pct, 2)}%</span>}
                    <span className="trading-muted">{o.reason}</span>
                    {o.source !== 'platform' && (
                      <button className="trading-x sm" title="Remove exception (revert to model)" onClick={() => removeOverride(o.id)}>✕</button>
                    )}
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      )}
      {pendingEdits.length > 0 && (
        <div className="ov-commit">
          <strong>{pendingEdits.length} sub exception{pendingEdits.length === 1 ? '' : 's'} pending</strong>
          <span className="trading-muted">
            {pendingEdits.slice(0, 3).map(({ pc, k, v }) => `${pc}: ${k} → ${v === 0 ? 'exclude' : fmt(v, 2)}`).join(' · ')}
            {pendingEdits.length > 3 ? ` · +${pendingEdits.length - 3} more` : ''}
          </span>
          <input className="cr-reason" placeholder="Reason (required)…" value={ovReason} onChange={(e) => setOvReason(e.target.value)} />
          <button className="trading-btn primary sm" disabled={!ovReason.trim()} onClick={commitOverrides}>Record</button>
          <button className="trading-btn sm" onClick={() => { setVehEdits({}); setOvReason(''); }}>Discard</button>
        </div>
      )}
      {saveMsg && <div className={`trading-save-msg ${saveMsg.ok ? 'ok' : 'err'}`}>{saveMsg.text}</div>}

      {loadingSeed ? <div className="trading-muted">Loading target…</div>
       : seed?.no_target && !addedFunds.length ? (
        <div className="trading-save-msg ok">
          No stored target for this strategy yet — the model starts blank. Author it in the suite view
          (or ⇩ import Shadow there), or use <strong>+ Add fund</strong> here to build it from scratch.
          The vehicle columns always show the platforms' live Shadow holdings for comparison.
        </div>
      ) : seed?.error || (!seed?.lines?.length && !Object.keys(rows).length) ? (
        <div className="trading-muted">No target lines for this strategy.</div>
      ) : (
        <div className="trading-grid-wrap">
          <table className="trading-grid matrix">
            <thead>
              <tr>
                <th className="l sticky-l">Fund</th>
                <th className="model-col">Model (new target)</th>
                {shownVehicles.map((v) => (
                  <th key={v.portfolio_code} className="veh-col" title={`${v.portfolio_name || ''} · ${v.platform || ''}`}>
                    <div className="veh-name">{v.portfolio_code}</div>
                    <div className="veh-plat">{v.platform || ''}</div>
                    {ovByVehicle[v.portfolio_code] > 0 && <div className="veh-flag" title={`${ovByVehicle[v.portfolio_code]} recorded exception(s)`}>◆ {ovByVehicle[v.portfolio_code]}</div>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(([ac, groupRows], gi) => (
                <React.Fragment key={ac}>
                  <tr className="ac-header">
                    <td className="l sticky-l"><span className="ac-dot" style={{ background: acColour(ac, gi) }} />{ac}</td>
                    <td className="model-col ac-total">{fmt(groupRows.reduce((s, r) => s + (Number(modelTargets[r.key]) || 0), 0), 1)}%</td>
                    {shownVehicles.map((v) => (
                      <td key={v.portfolio_code} className="veh-col ac-total">{fmt(groupRows.reduce((s, r) => s + (Number(matrix.weights[v.portfolio_code]?.[r.key]) || 0), 0), 1)}%</td>
                    ))}
                  </tr>
                  {groupRows.map((r) => (
                    <tr key={r.key} className="holding-row">
                      <td className="l sticky-l"><div className="trading-fund">{r.fund_name || r.apir_code}</div><div className="trading-apir">{r.apir_code || r.key}</div></td>
                      <td className="model-col">
                        <input className="trading-input" type="number" step="0.1" value={modelTargets[r.key] ?? ''} placeholder="—" onKeyDown={gridNav}
                          onChange={(e) => { const val = e.target.value; setModelTargets((prev) => ({ ...prev, [r.key]: val === '' ? '' : parseFloat(val) || 0 })); }} />
                      </td>
                      {shownVehicles.map((v) => {
                        const pc = v.portfolio_code;
                        const resolved = matrix.weights[pc]?.[r.key];
                        const ov = matrix.overrides?.[pc]?.[r.key];
                        const edited = vehEdits[pc]?.[r.key];
                        const val = edited !== undefined ? edited : (resolved ?? '');
                        return (
                          <td key={pc} className={`veh-col${ov ? ' ov' : ''}${edited !== undefined ? ' edited' : ''}`}
                              title={ov ? `${ov.type.replace('_', ' ')} — ${ov.reason}` : 'Edit to record a sub exception'}>
                            <input className="trading-input" type="number" step="0.1" value={val} placeholder="—" onKeyDown={gridNav}
                              onChange={(e) => setVehEdits((prev) => ({ ...prev,
                                [pc]: { ...(prev[pc] || {}), [r.key]: e.target.value === '' ? '' : (parseFloat(e.target.value) || 0) } }))} />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              <tr className="trading-total-row">
                <td className="l sticky-l">Total</td>
                <td className={`model-col ${sumOk ? 'ok' : 'err'}`}>{fmt(modelTotal, 1)}%</td>
                {shownVehicles.map((v) => {
                  const pc = v.portfolio_code;
                  const merged = { ...(matrix.weights[pc] || {}) };
                  Object.entries(vehEdits[pc] || {}).forEach(([k, val]) => { if (val !== '') merged[k] = Number(val) || 0; });
                  const tot = Object.values(merged).reduce((a, b) => a + (Number(b) || 0), 0);
                  const ok = Math.abs(tot - 100) <= 0.05;
                  return <td key={pc} className={`veh-col ${ok ? 'ok' : 'err'}`}>{fmt(tot, 1)}%</td>;
                })}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ═══════════════ Audit trail: timeline / evolution / compare ═══════════════ */
const STATUS_COLOURS = { draft: '#f0a500', pending_approval: '#599BE3', approved: '#91CF9B', superseded: '#6c757d' };
const statusColour = (s) => STATUS_COLOURS[s] || '#6c757d';

function StatusChip({ status }) {
  return <span className="audit-chip" style={{ background: `${statusColour(status)}26`, color: statusColour(status) === '#91CF9B' ? '#2e7d32' : statusColour(status) }}>{status.replace('_', ' ')}</span>;
}

/* Stacked asset-class bar (100% wide) */
function AcgBar({ acg, height = 18 }) {
  const entries = Object.entries(acg || {}).sort((x, y) => acRank(x[0]) - acRank(y[0]));
  const total = entries.reduce((s, [, v]) => s + v, 0) || 1;
  return (
    <div className="acgbar" style={{ height }}>
      {entries.map(([ac, v], i) => (
        <span key={ac} className="acgbar-seg" title={`${ac}: ${v.toFixed(1)}%`}
          style={{ width: `${(v / total) * 100}%`, background: acColour(ac, i) }} />
      ))}
    </div>
  );
}

/* Registry modal: Portfolio Registry details for a strategy (audit swimlane click) */
function RegistryModal({ blenderCode, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    if (blenderCode) tradingAPI.getRegistryInfo(blenderCode).then(setData).catch(() => setData({ error: true }));
  }, [blenderCode]);
  if (!blenderCode) return null;

  const pct = (v, dp = 1) => (v == null ? '—' : `${(v * 100).toFixed(dp)}%`);
  const objective = (e) => (e.return_objective == null ? '—'
    : e.objective_type === 'cpi' ? `CPI + ${(e.return_objective * 100).toFixed(1)}%`
      : e.objective_type === 'cash' ? `Cash + ${(e.return_objective * 100).toFixed(1)}%`
        : `${(e.return_objective * 100).toFixed(1)}% absolute`);

  return (
    <div className="ts-modal-overlay" onClick={onClose}>
      <div className="ts-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ts-modal-head">
          <div>
            <h3>Portfolio registry <span className="trading-muted">{blenderCode}</span></h3>
            {data && !data.error && (
              <div className="trading-muted ts-modal-sub">
                {data.entries.length} registry entr{data.entries.length === 1 ? 'y' : 'ies'} · {data.vehicle_count} mapped sub-portfolios
              </div>
            )}
          </div>
          <button className="trading-x ts-close" onClick={onClose}>✕</button>
        </div>
        {!data ? <div className="trading-muted">Loading…</div>
          : data.error ? <div className="trading-muted">Could not load registry info.</div>
            : !data.entries.length ? <div className="trading-muted">No registry entry for this blender code.</div> : (
              <div className="ts-modal-body">
                {data.entries.map((e, i) => (
                  <div key={e.portfolio_code} className="reg-entry">
                    <div className="reg-entry-head">
                      <strong>{e.display_name}</strong>
                      <span className="trading-code">{e.portfolio_code}</span>
                      {i === 0 && <span className="veh-newtag approved">canonical</span>}
                      {e.tailored && <span className="veh-newtag">tailored</span>}
                      {!e.is_active && <span className="audit-chip" style={{ background: '#eee', color: '#666' }}>inactive</span>}
                    </div>
                    <div className="reg-fields">
                      <div><span>Suite</span>{e.suite || '—'}</div>
                      <div><span>Growth exposure</span>{pct(e.growth_exposure, 0)}{e.growth_range ? ` (band ${e.growth_range})` : ''}</div>
                      <div><span>Objective</span>{objective(e)}</div>
                      <div><span>Income yield</span>{pct(e.income_yield)}</div>
                      <div><span>Time horizon</span>{e.time_horizon != null ? `${e.time_horizon} yrs` : '—'}</div>
                      <div><span>Inception</span>{e.inception_date || '—'}</div>
                      <div><span>Reporting</span>{e.reporting_frequency || '—'}</div>
                      <div><span>SRM override</span>{e.srm_override ?? '—'}</div>
                      <div><span>Benchmark override</span>{e.benchmark_override || '—'}</div>
                      <div><span>Show performance</span>{e.show_performance ? 'yes' : 'no'}</div>
                      <div><span>Visible in apps</span>{e.visible_in_apps == null ? 'all' : (e.visible_in_apps.join(', ') || 'none')}</div>
                      <div><span>Simulation</span>{e.simulation_name || '—'}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
      </div>
    </div>
  );
}

/* Timeline swimlane grouped by series (suite): each series is one collapsed lane
   showing ALL its activity; click the band to expand into per-strategy lanes.
   Built to stay readable with many portfolios. */
function TimelineSwimlane({ events, onPick, picked, onLabel }) {
  const [open, setOpen] = useState(() => new Set());
  const groups = useMemo(() => {
    const bySuite = new Map();
    events.forEach((e) => {
      const suite = e.suite || 'Other';
      if (!bySuite.has(suite)) bySuite.set(suite, new Map());
      const g = bySuite.get(suite);
      if (!g.has(e.blender_code)) g.set(e.blender_code, []);
      g.get(e.blender_code).push(e);
    });
    return [...bySuite.entries()].map(([suite, m]) => ({
      suite,
      strategies: [...m.entries()].sort(([a], [b]) => a.localeCompare(b)),
      events: [...m.values()].flat(),
    })).sort((a, b) => (a.suite === 'Other') - (b.suite === 'Other') || a.suite.localeCompare(b.suite));
  }, [events]);
  if (!events.length) return null;
  const toggle = (suite) => setOpen((p) => {
    const n = new Set(p);
    if (n.has(suite)) n.delete(suite); else n.add(suite);
    return n;
  });

  const times = events.map((e) => new Date(e.created_at).getTime());
  const min = Math.min(...times), max = Math.max(...times);
  const span = Math.max(max - min, 1);
  const W = 860, LP = 150, GH = 24, RH = 19;
  const x = (t) => LP + ((t - min) / span) * (W - LP - 30);

  // lay the rows out: a band per series, then (when expanded) a lane per strategy
  let yCur = 2;
  const laid = [];
  groups.forEach((g) => {
    laid.push({ kind: 'group', g, y: yCur, h: GH }); yCur += GH;
    if (open.has(g.suite)) {
      g.strategies.forEach(([bc, evs]) => { laid.push({ kind: 'strat', bc, evs, y: yCur, h: RH }); yCur += RH; });
      yCur += 4;
    }
  });
  const H = yCur + 20;

  const dot = (e, cy, r = 5) => (
    <circle key={e.target_set_id} cx={x(new Date(e.created_at).getTime())} cy={cy}
      r={picked === e.target_set_id ? r + 2.5 : r} fill={statusColour(e.status)}
      stroke={picked === e.target_set_id ? '#072040' : '#fff'} strokeWidth={1.2}
      style={{ cursor: 'pointer' }} onClick={() => onPick(e.target_set_id)}>
      <title>{`${e.blender_code} v${e.version} — ${e.status}\n${e.created_at?.slice(0, 16)}\n${e.author_email || ''}`}</title>
    </circle>
  );

  return (
    <svg className="audit-swim" viewBox={`0 0 ${W} ${H}`} style={{ width: '100%' }}>
      {laid.map((r) => (r.kind === 'group' ? (
        <g key={`g:${r.g.suite}`}>
          <rect x={2} y={r.y + 1} width={W - 4} height={r.h - 3} rx={5} className="audit-swim-band"
            style={{ cursor: 'pointer' }} onClick={() => toggle(r.g.suite)}>
            <title>{`${r.g.strategies.length} portfolio${r.g.strategies.length === 1 ? '' : 's'} · ${r.g.events.length} version${r.g.events.length === 1 ? '' : 's'} — click to ${open.has(r.g.suite) ? 'collapse' : 'expand'}`}</title>
          </rect>
          <text x={8} y={r.y + r.h / 2 + 3.5} className="audit-swim-group" style={{ cursor: 'pointer', pointerEvents: 'none' }}>
            {open.has(r.g.suite) ? '▾' : '▸'} {r.g.suite}
            <tspan className="audit-swim-count"> · {r.g.strategies.length}</tspan>
          </text>
          {!open.has(r.g.suite) && (
            <>
              <line x1={LP} y1={r.y + r.h / 2} x2={W - 20} y2={r.y + r.h / 2} className="audit-swim-line" />
              {r.g.events.map((e) => dot(e, r.y + r.h / 2 - 1, 4.5))}
            </>
          )}
        </g>
      ) : (
        <g key={`s:${r.bc}`}>
          <text x={20} y={r.y + r.h / 2 + 3.5} className="audit-swim-label clickable"
            style={{ cursor: 'pointer' }} onClick={() => onLabel && onLabel(r.bc)}>
            {r.bc}
            <title>Open portfolio registry details</title>
          </text>
          <line x1={LP} y1={r.y + r.h / 2} x2={W - 20} y2={r.y + r.h / 2} className="audit-swim-line" />
          {r.evs.map((e) => dot(e, r.y + r.h / 2))}
        </g>
      )))}
      <text x={LP} y={H - 5} className="audit-swim-axis">{new Date(min).toLocaleDateString()}</text>
      <text x={W - 20} y={H - 5} textAnchor="end" className="audit-swim-axis">{new Date(max).toLocaleDateString()}</text>
    </svg>
  );
}

/* ─────────── Target-set modal: the stored weights behind a timeline event ─────────── */
function TargetSetModal({ event, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actErr, setActErr] = useState(null);
  useEffect(() => {
    setDetail(null); setActErr(null);
    if (event) tradingAPI.getTargetSet(event.target_set_id).then(setDetail).catch(() => setDetail({ error: true }));
  }, [event]);
  if (!event) return null;

  const act = async (decision, label) => {
    if (!window.confirm(`${label} for ${event.blender_code} v${event.version}?`)) return;
    setBusy(true); setActErr(null);
    try {
      await tradingAPI.approvalDecision(event.target_set_id, decision, '');
      if (onChanged) onChanged();
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setActErr(typeof d === 'string' ? d : 'Action failed');
    } finally { setBusy(false); }
  };

  const lines = detail?.lines || [];
  const acg = {};
  lines.forEach((l) => { const k = l.asset_class_group || 'Other'; acg[k] = (acg[k] || 0) + (l.target_pct || 0); });
  const total = lines.reduce((s, l) => s + (l.target_pct || 0), 0);

  return (
    <div className="ts-modal-overlay" onClick={onClose}>
      <div className="ts-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ts-modal-head">
          <div>
            <h3>{event.display_name} <span className="trading-muted">{event.blender_code} · v{event.version}</span> <StatusChip status={event.status} /></h3>
            <div className="trading-muted ts-modal-sub">
              {event.created_at?.slice(0, 16).replace('T', ' ')} · {event.author_email}
              {event.approver_email ? ` · approved by ${event.approver_email}` : ''}
              {event.notes ? ` · ${event.notes}` : ''}
            </div>
          </div>
          <button className="trading-x ts-close" onClick={onClose}>✕</button>
        </div>
        {!detail ? <div className="trading-muted">Loading…</div> : detail.error ? <div className="trading-muted">Could not load target set.</div> : (
          <>
            <AcgBar acg={acg} height={20} />
            <div className="ts-modal-body">
              <table className="rules-table ts-table">
                <thead><tr><th className="l">Fund</th><th className="l bulk-apir-col">APIR</th><th>Weight</th></tr></thead>
                <tbody>
                  {groupByAssetClass(lines.map((l) => ({ ...l, position_key: l.position_key || l.apir_code }))).map(([ac, group], gi) => (
                    <React.Fragment key={ac}>
                      <tr className="ac-header">
                        <td className="l" colSpan={2}><span className="ac-dot" style={{ background: acColour(ac, gi) }} />{ac}</td>
                        <td className="ts-acg-total">{fmt(group.reduce((s, l) => s + (l.target_pct || 0), 0), 1)}%</td>
                      </tr>
                      {group.map((l) => (
                        <tr key={l.position_key}>
                          <td className="l bulk-name" title={l.fund_name}>{l.fund_name}</td>
                          <td className="l bulk-apir-col">{l.apir_code || l.position_key}</td>
                          <td>{fmt(l.target_pct, 2)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  <tr className="trading-total-row"><td className="l" colSpan={2}>Total</td><td>{fmt(total, 1)}%</td></tr>
                </tbody>
              </table>
            </div>
            <div className="ad-actions ts-modal-actions">
              {actErr && <span className="trading-muted" style={{ color: 'var(--danger)' }}>{actErr}</span>}
              <button className="trading-btn sm" disabled={busy}
                title="Copy this version's weights into a new draft (rollback path)"
                onClick={async () => {
                  setBusy(true); setActErr(null);
                  try {
                    const r = await tradingAPI.restoreTargetSet(event.target_set_id);
                    if (onChanged) onChanged();
                    onClose();
                    window.alert(`Restored v${r.restored_from} as new draft v${r.version} for ${r.blender_code}`);
                  } catch (e2) { setActErr('Restore failed'); } finally { setBusy(false); }
                }}>Restore as draft</button>
              {event.status === 'pending_approval' && (
                <button className="trading-btn sm" disabled={busy}
                  onClick={() => act('withdraw', 'Withdraw this submission (back to draft)')}>Withdraw submission</button>
              )}
              {event.status === 'approved' && (
                <button className="trading-btn sm" disabled={busy}
                  title="Approved → draft; the previous superseded version (if any) is reinstated"
                  onClick={() => act('revoke', 'Withdraw approval (previous approved version is reinstated)')}>Withdraw approval</button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ─────────── Bulk compare: many portfolios, chart/table, three aggregation levels ─────────── */
function BulkCompare() {
  const [suites, setSuites] = useState([]);
  const [sel, setSel] = useState([]);            // [{bc, dn, suite}]
  const [data, setData] = useState(null);
  const [level, setLevel] = useState('acg');     // acg | sac | fund
  const [view, setView] = useState('chart');     // chart | table
  const [expanded, setExpanded] = useState({});  // {bc: 'loading' | {vehicles, skipped_no_data}}
  const [divergentOnly, setDivergentOnly] = useState(false);  // hide subs matching the model

  const toggleVehicles = (bc) => {
    if (expanded[bc]) { setExpanded((prev) => { const n = { ...prev }; delete n[bc]; return n; }); return; }
    setExpanded((prev) => ({ ...prev, [bc]: 'loading' }));
    tradingAPI.getAuditVehicles(bc)
      .then((d) => setExpanded((prev) => (prev[bc] ? { ...prev, [bc]: d } : prev)))
      .catch(() => setExpanded((prev) => { const n = { ...prev }; delete n[bc]; return n; }));
  };

  useEffect(() => {
    tradingAPI.listSuites().then((d) => setSuites(d.suites || [])).catch(() => setSuites([]));
  }, []);

  useEffect(() => {
    if (!sel.length) { setData(null); return; }
    tradingAPI.getAuditBulk(sel.map((s) => s.bc)).then(setData).catch(() => setData(null));
  }, [sel]);

  const addSuite = (name) => {
    const s = suites.find((x) => x.name === name);
    if (!s) return;
    setSel((prev) => {
      const have = new Set(prev.map((p) => p.bc));
      const add = (s.portfolios || []).filter((p) => p.blender_code && !have.has(p.blender_code))
        .map((p) => ({ bc: p.blender_code, dn: p.display_name, suite: s.name }));
      return [...prev, ...add];
    });
  };
  const removeSuite = (name) => setSel((prev) => prev.filter((p) => p.suite !== name));
  const addOne = (bc) => {
    for (const s of suites) {
      const p = (s.portfolios || []).find((x) => x.blender_code === bc);
      if (p) { setSel((prev) => prev.some((y) => y.bc === bc) ? prev : [...prev, { bc, dn: p.display_name, suite: s.name }]); return; }
    }
  };
  const removeOne = (bc) => setSel((prev) => prev.filter((p) => p.bc !== bc));

  const ports = (data?.portfolios || []).filter((p) => !p.missing);
  const missing = (data?.portfolios || []).filter((p) => p.missing);
  const sacName = (code) => data?.sac_names?.[code] || code;

  // Flat display list: each master, followed (when expanded) by its sub-portfolios.
  const items = useMemo(() => {
    const out = [];
    ports.forEach((p) => {
      const ex = expanded[p.blender_code];
      out.push({
        key: p.blender_code, sub: false, bc: p.blender_code, src: p,
        label: p.display_name,
        meta: `v${p.version} ${p.status} · growth ${Math.round(p.growth)}%`,
        exState: ex,
      });
      if (ex && ex !== 'loading') {
        let vs = ex.vehicles || [];
        const identical = vs.filter((v) => v.matches_model).length;
        if (divergentOnly) vs = vs.filter((v) => !v.matches_model);
        vs.forEach((v) => out.push({
          key: `${p.blender_code}::${v.portfolio_code}`, sub: true, bc: p.blender_code, src: v,
          label: v.portfolio_code,
          meta: `${v.platform || ''}${v.exception_count
            ? ` · ${v.exception_count} exception${v.exception_count === 1 ? '' : 's'} · Δ ${v.divergence?.toFixed(1)}%`
            : ' · = model'}`,
          matches: v.matches_model,
        }));
        if (divergentOnly && identical > 0) {
          out.push({ key: `${p.blender_code}::note`, note: true, bc: p.blender_code,
                     label: `${identical} sub-portfolio${identical === 1 ? '' : 's'} identical to the model hidden` });
        }
      }
    });
    return out;
  }, [ports, expanded, divergentOnly]);

  // sub-asset-class / fund → parent asset class (for consistent colouring at every level)
  const parentOf = useMemo(() => {
    const m = {};
    items.forEach((it) => ((it.src?.funds) || []).forEach((f) => {
      if (f.sub_asset_class) m[`sac:${f.sub_asset_class}`] = f.asset_class_group;
      m[`fund:${f.position_key}`] = f.asset_class_group;
    }));
    return m;
  }, [items]);

  // {item-key → {label-key → pct}} at the chosen level, + ordered union of keys
  const series = useMemo(() => {
    const per = {};
    const keys = new Set();
    const fundNames = {};
    items.filter((it) => !it.note).forEach((it) => {
      const p = it.src;
      let m = {};
      if (level === 'acg') m = p.acg || {};
      else if (level === 'sac') m = p.sac || {};
      else (p.funds || []).forEach((f) => { m[f.position_key] = (m[f.position_key] || 0) + f.pct; fundNames[f.position_key] = f.fund_name; });
      per[it.key] = m;
      Object.entries(m).forEach(([k, v]) => { if (Math.abs(v) > 0.005) keys.add(k); });
    });
    const parent = (k) => (level === 'acg' ? k : parentOf[`${level}:${k}`] || 'Other');
    const label = (k) => (level === 'acg' ? k : level === 'sac' ? sacName(k) : (fundNames[k] || k));
    const ordered = [...keys].sort((a, b) => (acRank(parent(a)) - acRank(parent(b))) || label(a).localeCompare(label(b)));
    return { per, keys: ordered, parent, label };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, level, parentOf, data]);

  const legend = useMemo(() => [...new Set(series.keys.map(series.parent))], [series]);
  const bySuite = useMemo(() => {
    const g = {};
    sel.forEach((p) => (g[p.suite] = g[p.suite] || []).push(p));
    return Object.entries(g);
  }, [sel]);

  return (
    <>
      <div className="bulk-picker">
        <div className="rules-scope">
          <select value="" onChange={(e) => { if (e.target.value) addSuite(e.target.value); e.target.value = ''; }}>
            <option value="">+ Add group (suite)…</option>
            {suites.map((s) => <option key={s.name} value={s.name}>{s.name} ({s.portfolio_count})</option>)}
          </select>
          <select value="" onChange={(e) => { if (e.target.value) addOne(e.target.value); e.target.value = ''; }}>
            <option value="">+ Add portfolio…</option>
            {suites.map((s) => (
              <optgroup key={s.name} label={s.name}>
                {(s.portfolios || []).filter((p) => p.blender_code).map((p) => (
                  <option key={p.portfolio_code} value={p.blender_code}>{p.display_name}</option>
                ))}
              </optgroup>
            ))}
          </select>
          {sel.length > 0 && <button className="trading-link warn" onClick={() => setSel([])}>Clear all</button>}
        </div>
        {bySuite.length > 0 && (
          <div className="bulk-chips">
            {bySuite.map(([suite, items]) => (
              <div key={suite} className="bulk-group">
                <span className="bulk-group-name">{suite}
                  <button className="bulk-x" title="Remove group" onClick={() => removeSuite(suite)}>✕</button>
                </span>
                {items.map((p) => (
                  <span key={p.bc} className="bulk-chip">{p.dn}
                    <button className="bulk-x" onClick={() => removeOne(p.bc)}>✕</button>
                  </span>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {!sel.length ? <div className="trading-muted">Add a suite or individual portfolios to compare.</div> : !data ? <div className="trading-muted">Loading…</div> : (
        <>
          <div className="bulk-controls">
            <div className="tilt-toggle" title="Aggregation level for the comparison">
              {[['acg', 'Asset class'], ['sac', 'Sub-asset class'], ['fund', 'Fund']].map(([k, lbl]) => (
                <button key={k} className={level === k ? 'active' : ''} onClick={() => setLevel(k)}>{lbl}</button>
              ))}
            </div>
            <div className="tilt-toggle" title="Stacked bars for shape comparison, or a matrix with a dispersion (Range) column">
              {[['chart', 'Chart'], ['table', 'Table']].map(([k, lbl]) => (
                <button key={k} className={view === k ? 'active' : ''} onClick={() => setView(k)}>{lbl}</button>
              ))}
            </div>
            {Object.keys(expanded).length > 0 && (
              <label className="bulk-divonly" title="Hide sub-portfolios whose allocation matches the model (unit classes paired)">
                <input type="checkbox" checked={divergentOnly} onChange={(e) => setDivergentOnly(e.target.checked)} />
                divergent only
              </label>
            )}
          </div>
          {missing.length > 0 && (
            <div className="bulk-note">Not shown (no stored target — registry placeholders / unseeded): {missing.map((p) => p.display_name).join(', ')}</div>
          )}

          {view === 'chart' ? (
            <>
              <div className="audit-legend bulk-legend">
                {legend.map((ac, i) => <span key={ac}><span className="sw" style={{ background: acColour(ac, i), borderRadius: 2 }} />{ac}</span>)}
              </div>
              <div className="bulk-chart">
                {items.map((it) => {
                  if (it.note) {
                    return <div key={it.key} className="bulk-note">└ {it.label}</div>;
                  }
                  const m = series.per[it.key] || {};
                  const segs = series.keys.filter((k) => (m[k] || 0) > 0.005);
                  const total = segs.reduce((s, k) => s + m[k], 0) || 1;
                  return (
                    <div key={it.key} className={`evo-row${it.sub ? ' sub' : ''}`}>
                      <div className="evo-label"
                           title={it.sub ? `${it.src.portfolio_name || it.src.portfolio_code} · ${it.src.platform || ''}`
                             : `${it.src.display_name} (${it.src.blender_code})`}>
                        <strong>{it.label}</strong>
                        <div className="trading-muted">{it.meta}</div>
                        {!it.sub && (
                          <button className="bulk-expand" onClick={() => toggleVehicles(it.bc)}>
                            {it.exState === 'loading' ? '…' : it.exState
                              ? `▾ hide ${it.exState.vehicles?.length || 0} sub-portfolios`
                              : '▸ sub-portfolios'}
                          </button>
                        )}
                      </div>
                      <div className="acgbar" style={{ height: it.sub ? 16 : 24 }}>
                        {segs.map((k) => (
                          <span key={k} className="acgbar-seg"
                            title={`${series.label(k)}${level === 'fund' ? ` (${k})` : ''}: ${m[k].toFixed(1)}%`}
                            style={{ width: `${(m[k] / total) * 100}%`, background: acColour(series.parent(k), acRank(series.parent(k))) }} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="trading-grid-wrap">
              <table className="rules-table bulk-table">
                <thead>
                  <tr>
                    <th className="l sticky-l">{level === 'acg' ? 'Asset class' : level === 'sac' ? 'Sub-asset class' : 'Fund'}</th>
                    {level === 'fund' && <th className="l bulk-apir-col">APIR</th>}
                    {items.filter((it) => !it.note).map((it) => (
                      <th key={it.key} className={it.sub ? 'bulk-subcol' : ''}
                          title={it.sub ? `${it.src.portfolio_name || it.src.portfolio_code} · ${it.src.platform || ''}`
                            : `${it.src.display_name} (${it.src.blender_code})`}>
                        {it.label}
                        <div className="trading-muted">{it.sub ? (it.src.platform || '') : `v${it.src.version}`}</div>
                        {it.sub && it.src.divergence != null && <div className="bulk-divtag">Δ {it.src.divergence.toFixed(1)}</div>}
                        {!it.sub && (
                          <button className="bulk-expand light" onClick={() => toggleVehicles(it.bc)}>
                            {it.exState === 'loading' ? '…' : it.exState ? '▾ hide' : '▸ subs'}
                          </button>
                        )}
                      </th>
                    ))}
                    <th>Range</th>
                  </tr>
                </thead>
                <tbody>
                  {series.keys.map((k) => {
                    const cols = items.filter((it) => !it.note);
                    const vals = cols.map((it) => series.per[it.key]?.[k] || 0);
                    const rng = vals.length ? Math.max(...vals) - Math.min(...vals) : 0;
                    return (
                      <tr key={k}>
                        <td className="l sticky-l bulk-name" title={series.label(k)}>
                          <span className="ac-dot" style={{ background: acColour(series.parent(k), acRank(series.parent(k))) }} />
                          {series.label(k)}
                        </td>
                        {level === 'fund' && <td className="l bulk-apir-col">{k}</td>}
                        {vals.map((v, i) => (
                          <td key={cols[i].key} className={cols[i].sub ? 'bulk-subcol' : ''}>
                            {v > 0.005 ? fmt(v, 1) : <span className="trading-muted">–</span>}
                          </td>
                        ))}
                        <td className="bulk-range">{fmt(rng, 1)}</td>
                      </tr>
                    );
                  })}
                  <tr className="trading-total-row">
                    <td className="l sticky-l">Total</td>
                    {level === 'fund' && <td className="l bulk-apir-col" />}
                    {items.filter((it) => !it.note).map((it) => <td key={it.key} className={it.sub ? 'bulk-subcol' : ''}>{fmt(Object.values(series.per[it.key] || {}).reduce((s, v) => s + v, 0), 1)}%</td>)}
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
}

function AuditTrail({ initial }) {
  const [tab, setTab] = useState(initial?.tab || 'timeline');
  const [events, setEvents] = useState([]);
  const [picked, setPicked] = useState(null);
  const [modalEvent, setModalEvent] = useState(null);
  const [regBc, setRegBc] = useState(null);
  const openEvent = (id) => {
    setPicked(id);
    setModalEvent(events.find((x) => x.target_set_id === id) || null);
  };
  // evolution
  const [evoBc, setEvoBc] = useState(initial?.tab === 'evolution' ? (initial.bc || '') : '');
  const [evo, setEvo] = useState(null);
  // compare
  const [selA, setSelA] = useState('');
  const [selB, setSelB] = useState('');
  const [cmp, setCmp] = useState(null);

  const loadEvents = useCallback(() => {
    tradingAPI.getAuditHistory().then((d) => setEvents(d.events || [])).catch(() => setEvents([]));
  }, []);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  const strategies = useMemo(() => {
    const m = new Map();
    events.forEach((e) => m.set(e.blender_code, e.display_name));
    return [...m.entries()];
  }, [events]);

  useEffect(() => {
    if (!evoBc) { setEvo(null); return; }
    tradingAPI.getAuditEvolution(evoBc).then(setEvo).catch(() => setEvo(null));
  }, [evoBc]);

  useEffect(() => {
    if (!selA || !selB) { setCmp(null); return; }
    tradingAPI.getAuditCompare(selA, selB).then(setCmp).catch(() => setCmp(null));
  }, [selA, selB]);

  const setLabel = (e) => `${e.display_name} · v${e.version} (${e.status}, ${e.created_at?.slice(0, 10)})`;
  const sortedEvents = useMemo(() => [...events].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')), [events]);

  return (
    <>
      <header className="trading-header"><div><h2>Audit trail</h2>
        <div className="trading-header-sub"><span className="trading-muted">Every target created — who, when, and how allocations changed</span></div></div></header>

      <div className="rules-scope-tabs audit-tabs">
        {[['timeline', 'Timeline', 'Every target set ever created, on a time axis'],
          ['evolution', 'Evolution', 'One portfolio across its versions — allocation shifts and fund-level changes'],
          ['compare', 'Compare', 'Any two versions or portfolios side by side'],
          ['bulk', 'Bulk compare', 'Many portfolios at once — charts or tables at any taxonomy level']].map(([k, lbl, tip]) => (
            <button key={k} className={tab === k ? 'active' : ''} title={tip} onClick={() => setTab(k)}>{lbl}</button>
          ))}
      </div>

      {tab === 'bulk' && <BulkCompare />}

      {tab === 'timeline' && (
        !events.length ? <div className="trading-muted">No target sets created yet.</div> : (
          <>
            <div className="audit-legend">
              {Object.entries(STATUS_COLOURS).map(([s, c]) => (
                <span key={s}><span className="sw" style={{ background: c }} />{s.replace('_', ' ')}</span>
              ))}
            </div>
            <TimelineSwimlane events={events} onPick={openEvent} picked={picked} onLabel={setRegBc} />
            <div className="audit-table-wrap">
            <table className="rules-table audit-table">
              <thead><tr><th className="l">Created</th><th className="l">Portfolio</th><th>Version</th><th>Status</th>
                <th className="l" title="When these targets take effect in the Dashboard, Portfolio Viewer and Lab. A future date means the approved targets are held until then.">Effective</th>
                <th className="l">Author</th><th>Funds</th><th>Total</th><th className="l">Notes</th></tr></thead>
              <tbody>
                {sortedEvents.map((e) => (
                  <tr key={e.target_set_id} className={picked === e.target_set_id ? 'picked' : ''} onClick={() => openEvent(e.target_set_id)}>
                    <td className="l">{e.created_at?.slice(0, 16).replace('T', ' ')}</td>
                    <td className="l"><strong>{e.display_name}</strong> <span className="trading-muted">{e.blender_code}</span></td>
                    <td>v{e.version}</td>
                    <td><StatusChip status={e.status} /></td>
                    <td className="l">{e.effective_date
                      ? <EffectiveDateBadge date={e.effective_date} size="sm" />
                      : <span className="trading-muted" title="Effective as soon as approved">on approval</span>}</td>
                    <td className="l">{e.author_email}{e.approver_email ? <div className="trading-muted">✓ {e.approver_email}</div> : null}</td>
                    <td>{e.line_count}</td>
                    <td>{fmt(e.total, 1)}%</td>
                    <td className="l audit-notes">{e.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </>
        )
      )}

      {tab === 'evolution' && (
        <>
          <div className="rules-scope">
            <select value={evoBc} onChange={(e) => setEvoBc(e.target.value)}>
              <option value="">Select portfolio…</option>
              {strategies.map(([bc, dn]) => <option key={bc} value={bc}>{dn} ({bc})</option>)}
            </select>
          </div>
          {evo && evo.versions?.length > 0 && (
            <>
              <h4 className="audit-h4">Asset allocation by version</h4>
              <div className="evo-bars">
                {evo.versions.map((v) => (
                  <div key={v.version} className="evo-row">
                    <div className="evo-label">
                      <strong>v{v.version}</strong> <StatusChip status={v.status} />
                      <div className="trading-muted">{v.created_at} · growth {fmt(v.growth, 0)}%</div>
                      <button className="bulk-expand" title="Copy this version's weights into a new draft"
                        onClick={async () => {
                          try {
                            const r = await tradingAPI.restoreTargetSet(v.target_set_id);
                            loadEvents();
                            tradingAPI.getAuditEvolution(evoBc).then(setEvo).catch(() => {});
                            window.alert(`Restored v${r.restored_from} as new draft v${r.version}`);
                          } catch (e) { window.alert('Restore failed'); }
                        }}>↺ restore as draft</button>
                    </div>
                    <AcgBar acg={v.acg} height={22} />
                  </div>
                ))}
              </div>
              <h4 className="audit-h4">Fund weights by version <span className="trading-muted">(shading = change vs previous version)</span></h4>
              <div className="trading-grid-wrap">
                <table className="rules-table evo-matrix">
                  <thead><tr><th className="l">Fund</th>{evo.versions.map((v) => <th key={v.version}>v{v.version}<div className="trading-muted">{v.created_at}</div></th>)}</tr></thead>
                  <tbody>
                    {groupByAssetClass(evo.funds).map(([ac, group], gi) => (
                      <React.Fragment key={ac}>
                        <tr className="ac-header"><td className="l" colSpan={1 + evo.versions.length}><span className="ac-dot" style={{ background: acColour(ac, gi) }} />{ac}</td></tr>
                        {group.map((f) => (
                          <tr key={f.position_key}>
                            <td className="l">{f.fund_name}</td>
                            {evo.versions.map((v, vi) => {
                              const w = v.weights[f.position_key];
                              const prev = vi > 0 ? evo.versions[vi - 1].weights[f.position_key] : undefined;
                              const d = (w ?? 0) - (prev ?? 0);
                              const cls = vi === 0 || prev === undefined ? '' : d > 0.005 ? 'up' : d < -0.005 ? 'down' : '';
                              return (
                                <td key={v.version} className={`evo-cell ${cls}`} title={vi > 0 && cls ? `${d > 0 ? '+' : ''}${d.toFixed(2)} vs v${evo.versions[vi - 1].version}` : ''}>
                                  {w != null ? fmt(w, 1) : <span className="trading-muted">–</span>}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {evo && !evo.versions?.length && <div className="trading-muted">No versions stored for this portfolio.</div>}
        </>
      )}

      {tab === 'compare' && (
        <>
          <div className="rules-scope">
            <select value={selA} onChange={(e) => setSelA(e.target.value)}>
              <option value="">Portfolio A…</option>
              {sortedEvents.map((e) => <option key={e.target_set_id} value={e.target_set_id}>{setLabel(e)}</option>)}
            </select>
            <span className="trading-muted">vs</span>
            <select value={selB} onChange={(e) => setSelB(e.target.value)}>
              <option value="">Portfolio B…</option>
              {sortedEvents.map((e) => <option key={e.target_set_id} value={e.target_set_id}>{setLabel(e)}</option>)}
            </select>
          </div>
          {cmp && (
            <>
              <div className="cmp-cards">
                <div className="cmp-card"><div className="cmp-num">{fmt(cmp.overlap, 1)}%</div><div className="cmp-lbl">holdings overlap</div></div>
                <div className="cmp-card"><div className="cmp-num">{fmt(cmp.weight_moved, 1)}%</div><div className="cmp-lbl">weight difference</div></div>
                <div className="cmp-card"><div className="cmp-num">{fmt(cmp.a.growth, 0)}% / {fmt(cmp.b.growth, 0)}%</div><div className="cmp-lbl">growth A / B</div></div>
                <div className="cmp-card"><div className="cmp-num">{cmp.a.line_count} / {cmp.b.line_count}</div><div className="cmp-lbl">holdings A / B</div></div>
              </div>
              <div className="cmp-sides">
                <div><div className="cmp-side-label">A · {cmp.a.blender_code} v{cmp.a.version}</div><AcgBar acg={cmp.a.acg} /></div>
                <div><div className="cmp-side-label">B · {cmp.b.blender_code} v{cmp.b.version}</div><AcgBar acg={cmp.b.acg} /></div>
              </div>
              <h4 className="audit-h4">Asset-class comparison</h4>
              <div className="cmp-acg">
                {[...new Set([...Object.keys(cmp.a.acg), ...Object.keys(cmp.b.acg)])].sort((x, y) => acRank(x) - acRank(y)).map((ac, i) => {
                  const va = cmp.a.acg[ac] || 0, vb = cmp.b.acg[ac] || 0;
                  const mx = Math.max(...Object.values(cmp.a.acg), ...Object.values(cmp.b.acg), 1);
                  return (
                    <div key={ac} className="cmp-acg-row">
                      <div className="cmp-acg-name"><span className="ac-dot" style={{ background: acColour(ac, i) }} />{ac}</div>
                      <div className="cmp-acg-bars">
                        <div className="cmp-acg-bar a" style={{ width: `${(va / mx) * 100}%` }} /><span className="cmp-acg-val">{fmt(va, 1)}</span>
                      </div>
                      <div className="cmp-acg-bars">
                        <div className="cmp-acg-bar b" style={{ width: `${(vb / mx) * 100}%` }} /><span className="cmp-acg-val">{fmt(vb, 1)}</span>
                      </div>
                      <div className={`cmp-acg-delta ${vb - va > 0.05 ? 'up' : vb - va < -0.05 ? 'down' : ''}`}>{vb - va >= 0 ? '+' : ''}{fmt(vb - va, 1)}</div>
                    </div>
                  );
                })}
              </div>
              <h4 className="audit-h4">Fund-level differences <span className="trading-muted">(B − A, largest first)</span></h4>
              <div className="cmp-funds">
                {cmp.lines.filter((l) => Math.abs(l.delta) > 0.005).sort((x, y) => Math.abs(y.delta) - Math.abs(x.delta)).map((l) => {
                  const mx = Math.max(...cmp.lines.map((x) => Math.abs(x.delta)), 1);
                  return (
                    <div key={l.position_key} className="cmp-fund-row">
                      <div className="cmp-fund-name" title={l.fund_name}><span className="ac-dot" style={{ background: acColour(l.asset_class_group, acRank(l.asset_class_group)) }} />{l.fund_name}</div>
                      <div className="cmp-fund-vals">{fmt(l.a, 1)} → {fmt(l.b, 1)}</div>
                      <div className="tiltbar-track cmp-fund-track">
                        <span className="tiltbar-fill" style={{
                          background: acColour(l.asset_class_group, acRank(l.asset_class_group)),
                          ...(l.delta >= 0 ? { left: '50%', width: `${(Math.abs(l.delta) / mx) * 50}%` } : { right: '50%', width: `${(Math.abs(l.delta) / mx) * 50}%` }),
                        }} />
                      </div>
                      <div className={`cmp-acg-delta ${l.delta > 0 ? 'up' : 'down'}`}>{l.delta >= 0 ? '+' : ''}{fmt(l.delta, 1)}</div>
                    </div>
                  );
                })}
                {!cmp.lines.some((l) => Math.abs(l.delta) > 0.005) && <div className="trading-muted">Identical allocations.</div>}
              </div>
            </>
          )}
        </>
      )}

      <TargetSetModal event={modalEvent} onClose={() => setModalEvent(null)} onChanged={loadEvents} />
      <RegistryModal blenderCode={regBc} onClose={() => setRegBc(null)} />
    </>
  );
}

/* ═══════════════ Pre-trade compliance rules: scope to global / platform / master / sub-portfolio ═══════════════ */
const LEVEL_NUM = { global: 0, platform: 1, master: 2, vehicle: 3 };
const ruleLevel = (r) => (r.portfolio_code ? 3 : r.blender_code ? 2 : r.platform ? 1 : 0);

function resolveEffective(rules, level, bc, pc, plat, vehPlat) {
  const applicable = rules.filter((r) => {
    if (r.portfolio_code) return level === 'vehicle' && r.portfolio_code === pc;
    if (r.blender_code) return (level === 'master' || level === 'vehicle') && r.blender_code === bc;
    if (r.platform) {
      // platform rules apply on the platform tab and to a vehicle on that platform
      // (a master spans platforms, so they don't surface at master level)
      return (level === 'platform' && r.platform === plat)
        || (level === 'vehicle' && !!vehPlat && r.platform === vehPlat);
    }
    return true;                                                       // global
  });
  const best = {};
  applicable.forEach((r) => {
    const key = `${r.code}::${r.param || ''}`;
    if (!best[key] || ruleLevel(r) > ruleLevel(best[key])) best[key] = r;
  });
  return Object.values(best).sort((a, b) => a.code.localeCompare(b.code));
}

function ComplianceRulesManager() {
  const [rules, setRules] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [sacs, setSacs] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [level, setLevel] = useState('global');
  const [plat, setPlat] = useState('');
  const [bc, setBc] = useState('');
  const [pc, setPc] = useState('');
  const [form, setForm] = useState({ code: '', threshold: '', min: '', params: [], severity: '' });
  const [msg, setMsg] = useState(null);

  const refresh = useCallback(() => {
    tradingAPI.listComplianceRules().then((d) => setRules(d.rules || [])).catch(() => setRules([]));
  }, []);
  useEffect(() => {
    refresh();
    tradingAPI.getComplianceCatalogue().then((d) => { setCatalogue(d.catalogue || []); setSacs(d.sacs || []); setPlatforms(d.platforms || []); }).catch(() => {});
    tradingAPI.listStrategies().then((d) => setStrategies(d.strategies || [])).catch(() => {});
  }, [refresh]);
  useEffect(() => {
    if (!bc) { setVehicles([]); return; }
    tradingAPI.getVehicles(bc).then((d) => setVehicles(d.vehicles || [])).catch(() => setVehicles([]));
  }, [bc]);

  const sacName = useCallback((code) => (sacs.find((s) => s.code === code)?.name) || code, [sacs]);
  const bucketLabel = (param) => (param || '').split(',').filter(Boolean).map(sacName).join(' + ');

  const vehPlat = level === 'vehicle' ? (vehicles.find((v) => v.portfolio_code === pc)?.platform || '') : '';
  const effective = useMemo(() => resolveEffective(rules, level, bc, pc, plat, vehPlat),
    [rules, level, bc, pc, plat, vehPlat]);
  const curLevelNum = LEVEL_NUM[level];
  const ownLevel = ruleLevel;
  const scopeReady = level === 'global' || (level === 'platform' && plat)
    || (level === 'master' && bc) || (level === 'vehicle' && bc && pc);

  const pickType = (code) => {
    const cat = catalogue.find((c) => c.code === code);
    setForm({
      code,
      threshold: (cat?.bounds === 'band' ? cat?.default_max : cat?.default_threshold) ?? '',
      min: cat?.bounds === 'band' ? (cat?.default_min ?? '') : '',
      params: [], severity: cat?.default_severity || 'soft',
    });
  };
  const cat = catalogue.find((c) => c.code === form.code);
  const toggleParam = (code) => setForm((f) => ({
    ...f, params: f.params.includes(code) ? f.params.filter((x) => x !== code) : [...f.params, code],
  }));

  const createRule = async () => {
    if (!form.code) return;
    setMsg(null);
    try {
      const isBand = cat?.bounds === 'band';
      const param = cat?.param_kind === 'sac_multi' ? form.params.join(',') : null;
      await tradingAPI.createComplianceRule({
        code: form.code,
        threshold: form.threshold === '' ? null : parseFloat(form.threshold),
        min_threshold: isBand && form.min !== '' ? parseFloat(form.min) : null,
        param, severity: form.severity || null,
        platform: level === 'platform' ? plat : null,
        blender_code: level === 'master' ? bc : null,
        portfolio_code: level === 'vehicle' ? pc : null, active: true });
      setForm({ code: '', threshold: '', min: '', params: [], severity: '' });
      setMsg({ ok: true, text: 'Rule created' }); refresh();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Create failed' });
    }
  };
  const patchRule = async (r, patch) => {
    try { await tradingAPI.updateComplianceRule(r.id, patch); refresh(); }
    catch (e) { setMsg({ ok: false, text: 'Update failed' }); }
  };
  const removeRule = async (r) => {
    try { await tradingAPI.deleteComplianceRule(r.id); refresh(); }
    catch (e) { setMsg({ ok: false, text: 'Delete failed' }); }
  };
  // create a scoped override of an inherited rule at the current level
  const overrideHere = async (r) => {
    setMsg(null);
    try {
      await tradingAPI.createComplianceRule({
        code: r.code, threshold: r.threshold, min_threshold: r.min_threshold ?? null,
        param: r.param || null, severity: r.severity,
        platform: level === 'platform' ? plat : null,
        blender_code: level === 'master' ? bc : null,
        portfolio_code: level === 'vehicle' ? pc : null, active: true });
      refresh();
    } catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || 'Override failed' }); }
  };

  const scopeLabel = level === 'global' ? 'All portfolios'
    : level === 'platform' ? (plat ? `all portfolios on ${plat}` : '—')
      : level === 'master' ? (strategies.find((s) => s.blender_code === bc)?.display_name || bc || '—')
        : (vehicles.find((v) => v.portfolio_code === pc)?.portfolio_name || pc || '—');

  return (
    <>
      <header className="trading-header"><div><h2>Pre-trade compliance rules</h2>
        <div className="trading-header-sub"><span className="trading-muted">Scope rules to all portfolios, a platform, a master, or a single sub-portfolio</span></div></div></header>
      {msg && <div className={`trading-save-msg ${msg.ok ? 'ok' : 'err'}`}>{msg.text}</div>}

      <div className="rules-scope">
        <div className="rules-scope-tabs" title="Most specific rule wins: sub-portfolio beats master beats platform beats global">
          {[['global', 'Global', 'Applies to every portfolio unless overridden'],
            ['platform', 'Platform', 'Applies to every portfolio available on one platform'],
            ['master', 'Master portfolio', 'Applies to one strategy and all its subs'],
            ['vehicle', 'Sub-portfolio', 'Applies to a single vehicle only']].map(([lv, lbl, tip]) => (
              <button key={lv} className={level === lv ? 'active' : ''} title={tip} onClick={() => { setLevel(lv); }}>
                {lbl}
              </button>
          ))}
        </div>
        {level === 'platform' && (
          <select value={plat} onChange={(e) => setPlat(e.target.value)}>
            <option value="">Select platform…</option>
            {platforms.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        )}
        {(level === 'master' || level === 'vehicle') && (
          <select value={bc} onChange={(e) => { setBc(e.target.value); setPc(''); }}>
            <option value="">Select master…</option>
            {strategies.map((s) => <option key={s.blender_code} value={s.blender_code}>{s.display_name} ({s.blender_code})</option>)}
          </select>
        )}
        {level === 'vehicle' && bc && (
          <select value={pc} onChange={(e) => setPc(e.target.value)}>
            <option value="">Select sub-portfolio…</option>
            {vehicles.map((v) => <option key={v.portfolio_code} value={v.portfolio_code}>{v.portfolio_name || v.portfolio_code} ({v.portfolio_code})</option>)}
          </select>
        )}
      </div>

      {!scopeReady ? (
        <>
          {(() => {
            const scoped = rules.filter((r) => (level === 'platform' ? !!r.platform
              : level === 'master' ? (r.blender_code && !r.portfolio_code)
                : !!r.portfolio_code));
            const noun = level === 'platform' ? 'platform' : level === 'master' ? 'master' : 'sub-portfolio';
            if (!scoped.length) {
              return <div className="trading-muted">
                No {noun}-scoped rules created yet — choose a {noun} above to add one.
              </div>;
            }
            const nameOf = (bc) => strategies.find((s) => s.blender_code === bc)?.display_name || bc;
            const sortKey = (r) => r.platform || r.portfolio_code || r.blender_code || '';
            return (
              <div className="rules-effective">
                <h4>All {noun}-scoped rules ({scoped.length})
                  <span className="trading-muted"> — click a row to manage that scope</span></h4>
                <table className="rules-table">
                  <thead><tr>
                    <th className="l">{level === 'platform' ? 'Platform' : level === 'master' ? 'Master' : 'Sub-portfolio'}</th>
                    <th className="l">Rule</th><th>Limit</th><th>Severity</th><th>Active</th><th /></tr></thead>
                  <tbody>
                    {scoped.sort((a, b) => sortKey(a).localeCompare(sortKey(b)) || a.code.localeCompare(b.code)).map((r) => {
                      const c = catalogue.find((x) => x.code === r.code) || {};
                      const isBand = c.bounds === 'band';
                      const isSac = c.param_kind === 'sac_multi';
                      return (
                        <tr key={r.id} className="own" style={{ cursor: 'pointer' }}
                            title="Open this scope"
                            onClick={() => {
                              if (level === 'platform') { setPlat(r.platform || ''); return; }
                              setBc(r.blender_code || ''); if (level === 'vehicle') setPc(r.portfolio_code || '');
                            }}>
                          <td className="l">
                            <strong>{level === 'platform' ? r.platform : level === 'master' ? nameOf(r.blender_code) : r.portfolio_code}</strong>
                            {level !== 'platform' && <span className="trading-code"> {r.blender_code}</span>}
                          </td>
                          <td className="l">{r.label}{r.param ? ` — ${isSac ? bucketLabel(r.param) : r.param}` : ''}</td>
                          <td>{c.needs_threshold === false ? '—'
                            : isBand ? `${r.min_threshold ?? '–'} – ${r.threshold ?? '–'}%` : (r.threshold ?? '—')}</td>
                          <td>{r.severity}</td>
                          <td>{r.active ? '✓' : '✗'}</td>
                          <td><button className="trading-x sm" title="Delete rule"
                            onClick={(e) => { e.stopPropagation(); removeRule(r); }}>✕</button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </>
      ) : (
        <>
          <div className="rules-effective">
            <h4>Rules applying to <span className="rules-scopename">{scopeLabel}</span></h4>
            <table className="rules-table">
              <thead><tr><th className="l">Rule</th><th>Limit</th><th>Severity</th><th>Scope</th><th>Active</th><th></th></tr></thead>
              <tbody>
                {effective.map((r) => {
                  const own = ownLevel(r) === curLevelNum;
                  const from = ['global', 'platform', 'master', 'sub'][ownLevel(r)];
                  const c = catalogue.find((x) => x.code === r.code) || {};
                  const isBand = c.bounds === 'band';
                  const isSac = c.param_kind === 'sac_multi';
                  return (
                    <tr key={r.id} className={own ? 'own' : 'inherited'}>
                      <td className="l"><strong>{r.label}</strong>{r.param ? ` — ${isSac ? bucketLabel(r.param) : r.param}` : ''}</td>
                      <td>{c.needs_threshold === false ? '—' : isBand ? (own ? (
                        <span className="band-edit">
                          <input className="trading-input" type="number" step="0.1" placeholder="min" defaultValue={r.min_threshold ?? ''}
                            onBlur={(e) => patchRule(r, { min_threshold: e.target.value === '' ? null : parseFloat(e.target.value) })} />
                          <span>–</span>
                          <input className="trading-input" type="number" step="0.1" placeholder="max" defaultValue={r.threshold ?? ''}
                            onBlur={(e) => patchRule(r, { threshold: e.target.value === '' ? null : parseFloat(e.target.value) })} />
                        </span>
                      ) : `${r.min_threshold ?? '–'} – ${r.threshold ?? '–'}%`) : (own ? (
                        <input className="trading-input" type="number" step="0.1" defaultValue={r.threshold ?? ''}
                          onBlur={(e) => patchRule(r, { threshold: e.target.value === '' ? null : parseFloat(e.target.value) })} />
                      ) : (r.threshold ?? '—'))}</td>
                      <td>{own ? (
                        <select value={r.severity} onChange={(e) => patchRule(r, { severity: e.target.value })}>
                          <option value="hard">hard</option><option value="soft">soft</option>
                        </select>
                      ) : r.severity}</td>
                      <td><span className={`scope-tag ${from}`}>{from}</span></td>
                      <td>{own ? <input type="checkbox" checked={r.active} onChange={(e) => patchRule(r, { active: e.target.checked })} /> : (r.active ? '✓' : '✗')}</td>
                      <td>{own
                        ? <button className="trading-x sm" onClick={() => removeRule(r)} title="Delete rule">✕</button>
                        : <button className="trading-link" onClick={() => overrideHere(r)} title="Create a scoped override here">override</button>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="rules-add">
            <h4>Add a rule at this scope</h4>
            <div className="rules-add-form">
              <select value={form.code} onChange={(e) => pickType(e.target.value)}>
                <option value="">Rule type…</option>
                {catalogue.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
              </select>
              {cat?.bounds === 'band' ? (
                <>
                  <input className="trading-input wide" type="number" step="0.1" placeholder="Min % (blank = none)"
                    value={form.min} onChange={(e) => setForm((f) => ({ ...f, min: e.target.value }))} />
                  <input className="trading-input wide" type="number" step="0.1" placeholder="Max % (blank = none)"
                    value={form.threshold} onChange={(e) => setForm((f) => ({ ...f, threshold: e.target.value }))} />
                </>
              ) : (cat && cat.needs_threshold !== false && (
                <input className="trading-input wide" type="number" step="0.1" placeholder={cat.threshold_label || 'Threshold'}
                  value={form.threshold} onChange={(e) => setForm((f) => ({ ...f, threshold: e.target.value }))} />
              ))}
              {cat && (
                <select value={form.severity} onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}>
                  <option value="hard">hard-stop</option><option value="soft">soft-flag</option>
                </select>
              )}
              <button className="trading-btn primary sm"
                disabled={!form.code || (cat?.param_kind === 'sac_multi' && !form.params.length) || (cat?.bounds === 'band' && form.min === '' && form.threshold === '')}
                onClick={createRule}>Add rule</button>
            </div>
            {cat?.param_kind === 'sac_multi' && (
              <div className="sac-picker">
                <div className="sac-picker-head">{cat.param_label}: <span className="trading-muted">{form.params.length ? form.params.map(sacName).join(' + ') : 'none selected'}</span></div>
                <div className="sac-picker-grid">
                  {sacs.map((s) => (
                    <label key={s.code} className={`sac-chip${form.params.includes(s.code) ? ' on' : ''}`}>
                      <input type="checkbox" checked={form.params.includes(s.code)} onChange={() => toggleParam(s.code)} />
                      {s.name} <span className="sac-code">{s.code}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {cat && <div className="rules-add-desc">{cat.description}</div>}
          </div>
        </>
      )}
    </>
  );
}

/* ═══════════════ Unit class mapping: per-platform default fund swaps ═══════════════ */
function UnitClassMapManager() {
  const [mappings, setMappings] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [form, setForm] = useState({ platform: '', model_apir: '', sub_apir: '', notes: '' });
  const [msg, setMsg] = useState(null);

  const refresh = useCallback(() => {
    tradingAPI.listUnitClassMap()
      .then((d) => { setMappings(d.mappings || []); setPlatforms(d.platforms || []); })
      .catch(() => setMappings([]));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const create = async () => {
    setMsg(null);
    try {
      await tradingAPI.createUnitClassMapping(form);
      setForm((f) => ({ ...f, model_apir: '', sub_apir: '', notes: '' }));
      setMsg({ ok: true, text: 'Mapping added' }); refresh();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Create failed' });
    }
  };
  const patch = async (m, body) => {
    try { await tradingAPI.updateUnitClassMapping(m.id, body); refresh(); }
    catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || 'Update failed' }); }
  };
  const remove = async (m) => {
    try { await tradingAPI.deleteUnitClassMapping(m.id); refresh(); }
    catch (e) { setMsg({ ok: false, text: 'Delete failed' }); }
  };

  const byPlatform = useMemo(() => {
    const g = {};
    mappings.forEach((m) => { (g[m.platform] = g[m.platform] || []).push(m); });
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [mappings]);

  return (
    <>
      <header className="trading-header"><div><h2>Unit class mapping</h2>
        <div className="trading-header-sub"><span className="trading-muted">
          Per-platform default fund swaps: wherever the master model holds the model APIR, every
          Drummond sub-portfolio on that platform trades the platform&apos;s own unit class instead.
          Applied automatically (cascade, validation, trade sheets) and shown as a derived exception —
          a manually recorded exception on a vehicle still wins. Tailored (client) suites are excluded:
          they trade the model fund as-is.
        </span></div></div></header>
      {msg && <div className={`trading-save-msg ${msg.ok ? 'ok' : 'err'}`}>{msg.text}</div>}

      <div className="rules-add" style={{ borderTop: 'none', marginTop: 0, paddingTop: 0 }}>
        <h4>Add a mapping</h4>
        <div className="rules-add-form">
          <select value={form.platform} onChange={(e) => setForm((f) => ({ ...f, platform: e.target.value }))}>
            <option value="">Platform…</option>
            {platforms.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <input className="trading-input wide" placeholder="Model APIR (master)" value={form.model_apir}
            onChange={(e) => setForm((f) => ({ ...f, model_apir: e.target.value.toUpperCase() }))} />
          <span title="is traded on this platform as">→</span>
          <input className="trading-input wide" placeholder="Platform APIR (sub)" value={form.sub_apir}
            onChange={(e) => setForm((f) => ({ ...f, sub_apir: e.target.value.toUpperCase() }))} />
          <input className="trading-input wide" placeholder="Note (optional)" value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          <button className="trading-btn primary sm"
            disabled={!form.platform || !form.model_apir.trim() || !form.sub_apir.trim()
              || form.model_apir.trim() === form.sub_apir.trim()}
            onClick={create}>Add mapping</button>
        </div>
        <div className="rules-add-desc">
          Consider also pairing the two codes in a fund unit-class group (Registered instruments) so
          divergence and analysis treat them as the same fund.
        </div>
      </div>

      {!mappings.length ? (
        <div className="trading-muted" style={{ marginTop: 14 }}>No mappings yet.</div>
      ) : (
        <div className="rules-effective" style={{ marginTop: 14 }}>
          <table className="rules-table">
            <thead><tr>
              <th className="l">Model fund (master)</th><th className="l">Platform unit class (subs)</th>
              <th className="l">Note</th><th>Active</th><th /></tr></thead>
            <tbody>
              {byPlatform.map(([plat, rows]) => (
                <React.Fragment key={plat}>
                  <tr className="chg-band"><td className="l" colSpan={5}>{plat}</td></tr>
                  {rows.map((m) => (
                    <tr key={m.id} className={m.active ? 'own' : 'inherited'}>
                      <td className="l">
                        {m.model_name || m.model_apir}
                        <span className="trading-code"> {m.model_apir}</span>
                      </td>
                      <td className="l">
                        {m.sub_name || m.sub_apir}
                        <span className="trading-code"> {m.sub_apir}</span>
                      </td>
                      <td className="l trading-muted">{m.notes}</td>
                      <td><input type="checkbox" checked={m.active}
                        title="Inactive mappings are ignored everywhere"
                        onChange={(e) => patch(m, { active: e.target.checked })} /></td>
                      <td><button className="trading-x sm" title="Delete mapping"
                        onClick={() => remove(m)}>✕</button></td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* ═══════════════ Bulk upload: spreadsheet → draft target sets ═══════════════ */
function BulkUpload() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);

  const upload = async () => {
    if (!file) return;
    setBusy(true); setRes(null); setErr(null);
    try {
      setRes(await tradingAPI.bulkUploadTargets(file));
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === 'string' ? d : d?.message || 'Upload failed');
    } finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    setErr(null);
    try {
      const blob = await tradingAPI.downloadBulkTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'trading_targets_template.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setErr('Template download failed'); }
  };

  return (
    <>
      <header className="trading-header"><div><h2>Bulk upload targets</h2>
        <div className="trading-header-sub"><span className="trading-muted">Spreadsheet → a draft target set per blender code</span></div></div></header>

      <div className="bulkup-format">
        <h4 className="audit-h4">Expected layout (.xlsx or .csv)</h4>
        <table className="rules-table bulkup-example">
          <thead><tr><th className="l">APIR</th><th>DS30</th><th>DS70</th><th>…</th></tr></thead>
          <tbody>
            <tr><td className="l">BGL0034AU</td><td>3.0</td><td>10.5</td><td /></tr>
            <tr><td className="l">SOL0001AU</td><td>3.0</td><td>8.0</td><td /></tr>
            <tr><td className="l">Cash (T+n)</td><td>7.0</td><td>1.0</td><td /></tr>
          </tbody>
        </table>
        <ul className="bulkup-notes">
          <li>Column headers after the APIR column must be <strong>blender codes</strong>; unrecognised columns are ignored and reported.</li>
          <li><strong>Blank columns are left untouched</strong> — fill in only the strategies you're updating.</li>
          <li>Weights in percent (a column summing to ~1 is auto-detected as fractions and scaled ×100). Blank/0 cells are skipped.</li>
          <li>Cash tokens (Cash (T+n), TP Cash, MP Cash, AUD…) fold into the neutral <strong>Platform Cash</strong> line.</li>
          <li>Each column becomes the next <strong>draft</strong> version for its strategy — review in the suite view, then Submit → Approve.</li>
        </ul>
      </div>

      <div className="bulkup-controls">
        <button className="trading-btn sm" onClick={downloadTemplate}>⇓ Download template (all blender codes)</button>
        <input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => { setFile(e.target.files?.[0] || null); setRes(null); setErr(null); }} />
        <button className="trading-btn primary sm" disabled={!file || busy} onClick={upload}>{busy ? 'Uploading…' : 'Upload as drafts'}</button>
      </div>

      {err && <div className="trading-save-msg err">{err}</div>}
      {res && (
        <>
          <table className="rules-table bulkup-result">
            <thead><tr><th className="l">Strategy</th><th>Draft</th><th>Lines</th><th>Total</th><th>Status</th></tr></thead>
            <tbody>
              {res.created.map((c) => (
                <tr key={c.blender_code}>
                  <td className="l"><strong>{c.blender_code}</strong></td>
                  <td>v{c.version}</td>
                  <td>{c.lines}</td>
                  <td>{fmt(c.total, 1)}%{c.scaled_from_fractions ? ' (scaled ×100)' : ''}</td>
                  <td>{c.sum_ok ? <span className="compliance-badge pass">✓ sums to 100</span> : <span className="compliance-badge flag">⚠ check total</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {res.skipped_empty?.length > 0 && (
            <div className="bulk-note">Left untouched (blank columns): {res.skipped_empty.join(', ')}</div>
          )}
          {res.unknown_columns?.length > 0 && (
            <div className="bulk-note">Ignored columns (not blender codes): {res.unknown_columns.join(', ')}</div>
          )}
          {res.unknown_apirs?.length > 0 && (
            <div className="bulk-note">APIRs not found in the AP List / reference data (saved with the code as the name): {res.unknown_apirs.join(', ')}</div>
          )}
        </>
      )}
    </>
  );
}

/* ═══════════════ Fund data manager: registered instruments + management costs / rebate register ═══════════════ */
const AGREEMENT_OPTS = ['', 'Yes', 'No', 'In progress', 'Indicative only', 'NA'];
const TAILORED_OPTS = ['', 'Yes', 'No', 'In progress', 'NA'];
const fmtVol = (v) => (v == null ? '' : Number(v).toLocaleString('en-AU', { maximumFractionDigits: 0 }));

// `tab` is 'instruments' | 'costs' — chosen from the two sidebar buttons.
function FundDataManager({ tab }) {
  // "Held funds only" collapses the register to funds in a current approved target or a
  // live draft / pending set of ANY strategy, across every suite. Defaults ON each time
  // the panel opens; untick to see the whole AP List.
  const [heldOnly, setHeldOnly] = useState(true);
  const toggleHeld = () => setHeldOnly((v) => !v);
  // "Has rebate" keeps only funds with a saved rebate value (any non-null, including 0).
  const [rebateOnly, setRebateOnly] = useState(false);
  const [attrs, setAttrs] = useState([]);
  const [custom, setCustom] = useState({ instruments: [] });
  const [msg, setMsg] = useState(null);

  const refresh = useCallback(() => {
    tradingAPI.listFundAttributes().then((d) => setAttrs(d.attributes || [])).catch(() => setAttrs([]));
    tradingAPI.listCustomInstruments().then(setCustom).catch(() => {});
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const saveAttr = async (row, field, value) => {
    const v = value === '' ? null : Number(value);
    const next = {
      mer_pct: field === 'mer_pct' ? v : row.mer_pct,
      rebate_pct: field === 'rebate_pct' ? v : row.rebate_pct,
    };
    // don't create rows for untouched reference-only funds (no existing override row)
    if (next.mer_pct == null && next.rebate_pct == null && row.id == null) return;
    try {
      await tradingAPI.saveFundAttributes([{ position_key: row.position_key, ...next }]);
      refresh();
    } catch (e) { setMsg({ ok: false, text: 'Save failed' }); }
  };
  // Register fields are sent on their own so a text edit never touches the fee / rebate values.
  const saveField = async (row, field, value) => {
    const cur = row[field] == null ? '' : String(row[field]);
    if (String(value ?? '') === cur) return;
    try {
      await tradingAPI.saveFundAttributes([{ position_key: row.position_key, [field]: value === '' ? null : value }]);
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setMsg({ ok: false, text: typeof d === 'string' ? d : 'Save failed' });
    }
  };
  const saveInstr = async (i, field, value) => {
    try {
      await tradingAPI.updateCustomInstrument(i.id, { [field]: value });
      refresh();
    } catch (e) { setMsg({ ok: false, text: 'Update failed' }); }
  };
  const clearAttr = async (row) => {
    try {
      // Clear only the manual fee — rebate is a separate manual value and is preserved.
      await tradingAPI.saveFundAttributes([{ position_key: row.position_key, mer_pct: null, rebate_pct: row.rebate_pct }]);
      refresh();
    } catch (e) { setMsg({ ok: false, text: 'Clear failed' }); }
  };
  const retire = async (i) => {
    try { await tradingAPI.retireCustomInstrument(i.id); refresh(); }
    catch (e) { setMsg({ ok: false, text: 'Retire failed' }); }
  };
  const reactivate = async (i) => {
    try {
      await tradingAPI.createCustomInstrument({
        code: i.code, name: i.name, instrument_type: i.instrument_type, platform: i.platform,
        asset_class_group: i.asset_class_group, sub_asset_class: i.sub_asset_class,
        mer_pct: i.mer_pct, notes: i.notes });
      refresh();
    } catch (e) { setMsg({ ok: false, text: 'Reactivate failed' }); }
  };

  const [q, setQ] = useState('');
  const heldCount = attrs.filter((r) => r.held).length;
  const approvedCount = attrs.filter((r) => (r.held_approved || []).length).length;
  const aplCount = attrs.filter((r) => r.in_apl).length;
  const rebateCount = attrs.filter((r) => r.rebate_pct != null).length;
  const needle = q.trim().toLowerCase();
  const shown = attrs.filter((r) => (!heldOnly || r.held)
    && (!rebateOnly || r.rebate_pct != null)
    && (!needle || (r.fund_name || '').toLowerCase().includes(needle) || (r.position_key || '').toLowerCase().includes(needle)));
  const heldTitle = (r) => {
    const a = r.held_approved || [], d = r.held_draft || [];
    if (!a.length && !d.length) return 'Not in any current approved target or live draft';
    return [a.length ? `Approved: ${a.join(', ')}` : null, d.length ? `Draft / pending: ${d.join(', ')}` : null,
      (r.held_suites || []).length ? `Suites: ${r.held_suites.join(', ')}` : null].filter(Boolean).join('\n');
  };

  return (
    <>
      <header className="trading-header"><div>
        <h2>{tab === 'costs' ? 'Management costs and rebates' : 'Registered instruments'}</h2>
        <div className="trading-header-sub"><span className="trading-muted">
          {tab === 'costs'
            ? 'Management Fees & Costs / rebate values plus the rebate agreement register — global, fund-level'
            : 'Codes registered by hand (sub-SMAs, cash tokens, ETFs) that the platform lists do not carry'}
        </span></div></div></header>
      {msg && <div className={`trading-save-msg ${msg.ok ? 'ok' : 'err'}`}>{msg.text}</div>}

      {tab === 'instruments' && (<>
        <h4 className="audit-h4">Registered instruments ({(custom.instruments || []).length})
          <span className="trading-muted"> — all fields editable (saved on change); the code itself is fixed</span></h4>
        {!(custom.instruments || []).length ? <div className="trading-muted">None registered — use "+ Register instrument" in the Add fund panel.</div> : (
          <table className="rules-table fund-data-table instr-table">
            <thead><tr><th className="l">Code</th><th className="l">Name</th><th className="l">Type</th><th className="l">Platform</th><th className="l">Asset class group</th><th className="l">SAC</th><th>Mgmt Fees &amp; Costs %</th><th className="l">Notes</th><th>Status</th><th /></tr></thead>
            <tbody>
              {(custom.instruments || []).map((i) => {
                const groupNames = Object.entries(custom.group_order || {}).sort((a, b) => a[1] - b[1]).map(([g]) => g);
                return (
                  <tr key={i.id} className={i.active ? '' : 'ts-row-skip'}>
                    <td className="l bulk-apir-col">{i.code}</td>
                    <td className="l"><input className="instr-input wide" defaultValue={i.name}
                      onBlur={(e) => e.target.value.trim() && e.target.value !== i.name && saveInstr(i, 'name', e.target.value)} /></td>
                    <td className="l"><select className="instr-input" value={i.instrument_type}
                      onChange={(e) => saveInstr(i, 'instrument_type', e.target.value)}>
                      {(custom.types || []).map((t) => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                    </select></td>
                    <td className="l"><select className="instr-input" value={i.platform}
                      onChange={(e) => saveInstr(i, 'platform', e.target.value)}>
                      {(custom.platforms || []).map((p) => <option key={p} value={p}>{p}</option>)}
                    </select></td>
                    <td className="l"><select className="instr-input" value={i.asset_class_group}
                      onChange={(e) => saveInstr(i, 'asset_class_group', e.target.value)}>
                      {!groupNames.includes(i.asset_class_group) && <option value={i.asset_class_group}>{i.asset_class_group}</option>}
                      {groupNames.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select></td>
                    <td className="l"><select className="instr-input" value={i.sub_asset_class}
                      onChange={(e) => saveInstr(i, 'sub_asset_class', e.target.value)}>
                      {!(custom.sacs || []).some((s) => s.code === i.sub_asset_class) && <option value={i.sub_asset_class}>{i.sub_asset_class}</option>}
                      {(custom.sacs || []).map((s) => <option key={s.code} value={s.code}>{s.name} ({s.code})</option>)}
                    </select></td>
                    <td><input className="trading-input" type="number" step="0.01" defaultValue={i.mer_pct ?? ''} placeholder="—"
                      onBlur={(e) => saveInstr(i, 'mer_pct', e.target.value === '' ? null : Number(e.target.value))} /></td>
                    <td className="l"><input className="instr-input wide" defaultValue={i.notes || ''} placeholder="—"
                      onBlur={(e) => e.target.value !== (i.notes || '') && saveInstr(i, 'notes', e.target.value)} /></td>
                    <td>{i.active ? <span className="compliance-badge pass">active</span> : <span className="compliance-badge flag">retired</span>}</td>
                    <td>{i.active
                      ? <button className="trading-link" title="Retire (kept for history; old lines still classify)" onClick={() => retire(i)}>retire</button>
                      : <button className="trading-link" onClick={() => reactivate(i)}>reactivate</button>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </>)}

      {tab === 'costs' && (<>
        <h4 className="audit-h4">Management Fees &amp; Costs / rebate register ({shown.length}{shown.length !== attrs.length ? ` of ${attrs.length}` : ''} funds)
          <span className="trading-muted"> — every fund in the AP List table ({aplCount}) plus any fund in a stored target; grey fee = FE fee-table default, typing saves a manual override. Register fields save on blur.</span>
        </h4>
        <div className="register-tools">
          <input className="trading-search register-search" placeholder="Search APIR or fund name…" value={q}
            onChange={(e) => setQ(e.target.value)} />
          <button className={`trading-link held-toggle${heldOnly ? ' on' : ''}`}
            title={`Show only funds in a current approved target or a live draft / pending set, across all suites (${heldCount} held, ${approvedCount} in an approved target)`}
            onClick={toggleHeld}>{heldOnly ? `◼ Held funds only (${heldCount})` : `◻ Held funds only (${heldCount} of ${attrs.length})`}</button>
          <button className={`trading-link held-toggle${rebateOnly ? ' on' : ''}`}
            title={`Show only funds with a rebate value saved (${rebateCount} of ${attrs.length})`}
            onClick={() => setRebateOnly((v) => !v)}>{rebateOnly ? `◼ Has rebate (${rebateCount})` : `◻ Has rebate (${rebateCount} of ${attrs.length})`}</button>
        </div>
        {!attrs.length ? <div className="trading-muted">No funds on the AP List or in any stored target yet.</div> : (
          <div className="fund-data-wrap">
            <table className="rules-table fund-data-table register-table">
              <thead><tr>
                <th className="l">Distributor</th><th className="l">Fund</th><th className="l">APIR</th><th className="l">Asset class</th>
                <th>Mgmt Fees &amp; Costs %</th><th>Rebate %</th><th>Net %</th><th>Source</th>
                <th>Agreement</th><th>Tailored</th><th>Agreement date</th><th>Current volume</th><th className="l">Owner</th><th className="l">Notes</th>
                <th>Held</th><th className="l">Updated</th><th />
              </tr></thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.position_key} className={r.held ? '' : 'ts-row-skip'}>
                    <td className="l"><input className="instr-input sm" defaultValue={r.distributor || ''} placeholder="—"
                      onBlur={(e) => saveField(r, 'distributor', e.target.value.trim())} /></td>
                    <td className="l bulk-name" title={r.in_apl ? r.fund_name : `${r.fund_name} — not on the AP List; shown because a target or register row references it`}>
                      {r.fund_name}{!r.in_apl && <span className="trading-muted"> (not on APL)</span>}</td>
                    <td className="l bulk-apir-col">{r.position_key}</td>
                    <td className="l"><input className="instr-input sm" defaultValue={r.asset_class || ''} placeholder={r.asset_class_ref || '—'}
                      title={r.asset_class_ref ? `From the target lines: ${r.asset_class_ref} — type to override` : 'No taxonomy on file — enter a label'}
                      onBlur={(e) => saveField(r, 'asset_class', e.target.value.trim())} /></td>
                    <td><input className="trading-input" type="number" step="0.01"
                      defaultValue={r.mer_pct ?? ''} placeholder={r.ref_mer != null ? String(r.ref_mer) : '—'}
                      title={r.ref_mer != null ? `FE fee-table default: ${r.ref_mer}% — type to override, clear to revert` : 'No FE fee-table value — enter a manual figure'}
                      onBlur={(e) => saveAttr(r, 'mer_pct', e.target.value)} /></td>
                    <td><input className="trading-input" type="number" step="0.01" defaultValue={r.rebate_pct ?? ''} placeholder="—"
                      onBlur={(e) => saveAttr(r, 'rebate_pct', e.target.value)} /></td>
                    <td>{r.effective_mer != null ? fmt(r.effective_mer - (r.rebate_pct || 0), 2) : '—'}</td>
                    <td>{r.source === 'manual' ? <span className="compliance-badge flag">manual</span>
                      : r.source === 'reference' ? <span className="compliance-badge pass">fee default</span>
                        : <span className="trading-muted">—</span>}</td>
                    <td><select className="instr-input status-select" value={r.agreement_status || ''}
                      onChange={(e) => saveField(r, 'agreement_status', e.target.value)}>
                      {AGREEMENT_OPTS.map((o) => <option key={o} value={o}>{o || '—'}</option>)}
                    </select></td>
                    <td><select className="instr-input status-select" value={r.tailored_status || ''}
                      onChange={(e) => saveField(r, 'tailored_status', e.target.value)}>
                      {TAILORED_OPTS.map((o) => <option key={o} value={o}>{o || '—'}</option>)}
                    </select></td>
                    <td><input className="instr-input date" type="date" defaultValue={r.agreement_date || ''}
                      onBlur={(e) => saveField(r, 'agreement_date', e.target.value)} /></td>
                    <td><input className="instr-input vol" type="text" inputMode="numeric" defaultValue={fmtVol(r.current_volume)} placeholder="—"
                      title="A$ held across platforms (maintained by hand)"
                      onBlur={(e) => { const v = e.target.value.replace(/[^0-9.-]/g, ''); saveField(r, 'current_volume', v === '' ? '' : Number(v)); }} /></td>
                    <td className="l"><input className="instr-input sm" defaultValue={r.drummond_owner || ''} placeholder="—"
                      onBlur={(e) => saveField(r, 'drummond_owner', e.target.value.trim())} /></td>
                    <td className="l"><input className="instr-input xw" defaultValue={r.notes || ''} placeholder="—" title={r.notes || ''}
                      onBlur={(e) => saveField(r, 'notes', e.target.value.trim())} /></td>
                    <td title={heldTitle(r)}>
                      {(r.held_approved || []).length ? <span className="compliance-badge pass">{r.held_approved.length} approved</span> : null}
                      {(r.held_draft || []).length ? <span className="compliance-badge flag">{r.held_draft.length} draft</span> : null}
                      {!r.held && <span className="compliance-badge neutral">not held</span>}
                    </td>
                    <td className="l trading-muted">{r.updated_at ? `${r.updated_at} · ${r.updated_by_email}` : ''}</td>
                    <td>{r.source === 'manual' && (
                      <button className="trading-x sm" title="Clear the manual fee — reverts to the FE fee-table default (rebate and register are kept)" onClick={() => clearAttr(r)}>✕</button>
                    )}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>)}
    </>
  );
}

/* ═══════════════ Generate trade sheets (P5): approved targets → platform order files ═══════════════ */
function TradeSheets() {
  const [suites, setSuites] = useState([]);
  const [sel, setSel] = useState(new Set());     // blender codes
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    tradingAPI.listSuites().then((d) => setSuites(d.suites || [])).catch(() => setSuites([]));
  }, []);

  const bcsOf = (s) => [...new Set((s.portfolios || []).map((p) => p.blender_code).filter(Boolean))];
  const toggleBc = (bc) => setSel((prev) => {
    const n = new Set(prev); if (n.has(bc)) n.delete(bc); else n.add(bc); return n;
  });
  const toggleSuite = (s) => setSel((prev) => {
    const n = new Set(prev);
    const bcs = bcsOf(s);
    const allIn = bcs.every((b) => n.has(b));
    bcs.forEach((b) => (allIn ? n.delete(b) : n.add(b)));
    return n;
  });

  const generate = async (dryRun = false) => {
    setBusy(true); setRes(null); setErr(null);
    try {
      setRes(await tradingAPI.generateTradeSheets([...sel], dryRun));
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === 'string' ? d : 'Generation failed');
    } finally { setBusy(false); }
  };

  const download = async () => {
    try {
      const blob = await tradingAPI.downloadTradeSheets(res.run_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `TradeSheets_${res.export_date}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setErr('Download failed'); }
  };

  const statusChip = (st) => (
    <span className={`compliance-badge ${st === 'generated' ? 'pass' : st === 'excluded' || st === 'unroutable' ? 'fail' : 'flag'}`}>{st}</span>
  );

  return (
    <>
      <header className="trading-header"><div><h2>Generate trade sheets</h2>
        <div className="trading-header-sub"><span className="trading-muted">
          Per-platform order templates from APPROVED targets, resolved per sub-portfolio. Stamped with today's date.
        </span></div></div></header>

      <div className="ts-selector">
        {suites.map((s) => {
          const bcs = bcsOf(s);
          const inSel = bcs.filter((b) => sel.has(b)).length;
          return (
            <div key={s.name} className="ts-suite">
              <label className="ts-suite-head">
                <input type="checkbox" checked={inSel === bcs.length && bcs.length > 0}
                  ref={(el) => { if (el) el.indeterminate = inSel > 0 && inSel < bcs.length; }}
                  onChange={() => toggleSuite(s)} />
                <strong>{s.name}</strong> <span className="trading-muted">({inSel}/{bcs.length})</span>
              </label>
              <div className="ts-suite-items">
                {(s.portfolios || []).filter((p) => p.blender_code).map((p) => (
                  <label key={p.portfolio_code} className="ts-item">
                    <input type="checkbox" checked={sel.has(p.blender_code)} onChange={() => toggleBc(p.blender_code)} />
                    {p.display_name} <span className="trading-code">{p.blender_code}</span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="bulkup-controls">
        <button className="trading-btn" disabled={!sel.size || busy} title="Show what would be generated without producing files"
          onClick={() => generate(true)}>
          {busy ? '…' : 'Preview (no files)'}
        </button>
        <button className="trading-btn primary" disabled={!sel.size || busy} onClick={() => generate(false)}>
          {busy ? 'Generating…' : `Generate for ${sel.size} strateg${sel.size === 1 ? 'y' : 'ies'}`}
        </button>
        {res && !res.dry_run && <button className="trading-btn" onClick={download}>⇓ Download ZIP ({res.file_count} files)</button>}
      </div>
      {err && <div className="trading-save-msg err">{err}</div>}

      {res && (
        <>
          <div className={`trading-save-msg ${res.dry_run ? 'err' : 'ok'}`} style={res.dry_run ? { background: 'rgba(240,165,0,0.12)', color: '#a06f00' } : undefined}>
            {res.dry_run
              ? `PREVIEW ONLY — no files produced, no run recorded. ${res.generated_vehicles} sub-portfolios would be written into ${res.file_count} files.`
              : `Run #${res.run_id} · stamped ${res.export_date} · ${res.generated_vehicles} sub-portfolios written into ${res.file_count} files. The ZIP mirrors the template folder structure and includes this report as CSV.`}
          </div>
          <table className="rules-table ts-report">
            <thead><tr><th className="l">Series</th><th className="l">Master</th><th className="l">Sub-portfolio</th><th className="l">Platform</th><th className="l">File</th><th>Status</th><th className="l">Detail</th></tr></thead>
            <tbody>
              {res.report.map((r, i) => (
                <tr key={i} className={r.status !== 'generated' ? 'ts-row-skip' : ''}>
                  <td className="l">{r.suite}</td>
                  <td className="l">{r.blender_code}</td>
                  <td className="l">{r.portfolio_code}</td>
                  <td className="l">{r.platform}</td>
                  <td className="l ts-file">{r.file}</td>
                  <td>{statusChip(r.status)}</td>
                  <td className="l trading-muted">{r.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}

/* ═══════════════ Cascade map: master → sub relationship, exceptions highlighted ═══════════════ */
const OV_TAG = { reweight: 'reweight', fund_substitution: 'substitution', exclude: 'model_only', add: 'vehicle_only' };

/* Cascade body: one master's sub tree — shared by the full panel and the per-portfolio modal */
function CascadeBody({ bc }) {
  const [data, setData] = useState(null);
  const [openSub, setOpenSub] = useState(null);

  useEffect(() => {
    setOpenSub(null);
    if (!bc) { setData(null); return; }
    tradingAPI.getCascade(bc).then(setData).catch(() => setData(null));
  }, [bc]);

  const platforms = useMemo(() => {
    const g = {};
    (data?.vehicles || []).forEach((v) => (g[v.platform] = g[v.platform] || []).push(v));
    return Object.entries(g).sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]));
  }, [data]);
  const exceptionSubs = (data?.vehicles || []).filter((v) => v.exceptions.length).length;

  if (!bc) return null;
  if (!data) return <div className="trading-muted">Loading cascade…</div>;
  return (
    <>
      <div className="trading-muted">
        {data.clean_count}/{data.vehicle_count} subs follow the model exactly
        {exceptionSubs ? ` · ${exceptionSubs} with exceptions` : ''}
      </div>
      {(
        <div className="cascade">
          <div className="cascade-master">
            <div className="cm-name">{data.display_name}</div>
            <div className="cm-code">{data.blender_code}</div>
            {data.target_source
              ? <span className={`veh-newtag ${data.target_source.startsWith('approved') ? 'approved' : ''}`}>{data.target_source}</span>
              : <span className="trading-muted">no stored target</span>}
            <div className="cm-meta">{data.line_count} funds · {data.vehicle_count} subs</div>
          </div>
          <div className="cascade-plats">
            {platforms.map(([plat, vs]) => (
              <div key={plat} className="cascade-branch">
                <div className="cascade-plat">{plat} <span className="trading-muted">({vs.length})</span></div>
                <div className="cascade-subs">
                  {vs.map((v) => {
                    const open = openSub === v.portfolio_code;
                    return (
                      <div key={v.portfolio_code}
                           className={`cascade-sub${v.exceptions.length ? ' has-ov' : ''}${open ? ' open' : ''}`}
                           title={v.portfolio_name || ''}
                           onClick={() => setOpenSub(open ? null : v.portfolio_code)}>
                        <div className="cs-row">
                          <span className="cs-code">{v.portfolio_code}</span>
                          {v.exceptions.length
                            ? <span className="cs-badge ov">◆ {v.exceptions.length}</span>
                            : <span className="cs-badge ok">= model</span>}
                        </div>
                        {v.portfolio_name && <div className="cs-name">{v.portfolio_name}</div>}
                        {v.client_domain && <div className="cs-domain">{v.client_domain}</div>}
                        {open && v.exceptions.length > 0 && (
                          <div className="cs-detail" onClick={(e) => e.stopPropagation()}>
                            {v.exceptions.map((o) => (
                              <div key={o.id ?? `${o.type}:${o.position_key}`} className="cs-ov">
                                <span className={`div-tag ${OV_TAG[o.type] || 'reweight'}`}>{o.type.replace('_', ' ')}</span>
                                {o.source === 'platform' && (
                                  <span className="veh-newtag" title="Derived from the platform unit-class mapping">unit class map</span>
                                )}
                                <span className="cs-ov-fund">{o.fund_name}</span>
                                {o.target_pct != null && <span className="cs-ov-w">{fmt(o.target_pct, 2)}%</span>}
                                <span className="trading-muted">{o.reason}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function CascadeMap({ initialBc }) {
  const [strategies, setStrategies] = useState([]);
  const [bc, setBc] = useState(initialBc || '');

  useEffect(() => {
    tradingAPI.listStrategies().then((d) => setStrategies(d.strategies || [])).catch(() => setStrategies([]));
  }, []);

  // Group by portfolio suite (registry); suite-less masters last under "Other".
  const bySuite = useMemo(() => {
    const groups = new Map();
    strategies.forEach((s) => {
      const suite = s.suite || 'Other';
      if (!groups.has(suite)) groups.set(suite, []);
      groups.get(suite).push(s);
    });
    groups.forEach((list) => list.sort((a, b) =>
      a.blender_code.localeCompare(b.blender_code, undefined, { numeric: true })));
    return [...groups.entries()].sort(([a], [b]) => {
      if (a === 'Other') return 1;
      if (b === 'Other') return -1;
      return a.localeCompare(b);
    });
  }, [strategies]);

  return (
    <>
      <header className="trading-header"><div><h2>Cascade map</h2>
        <div className="trading-header-sub"><span className="trading-muted">One master target — subs inherit it exactly, except where a recorded exception says otherwise</span></div></div></header>
      <div className="rules-scope">
        <select value={bc} onChange={(e) => setBc(e.target.value)}>
          <option value="">Select master portfolio…</option>
          {bySuite.map(([suite, list]) => (
            <optgroup key={suite} label={suite}>
              {list.map((s) => (
                <option key={s.blender_code} value={s.blender_code}>
                  {s.display_name} ({s.blender_code}{s.is_cascade ? ` · ${s.vehicle_count} subs` : ''})
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
      <CascadeBody bc={bc} />
    </>
  );
}

/* Cascade modal: just one portfolio's cascade map, opened from its column header */
function CascadeModal({ blenderCode, displayName, onClose }) {
  return (
    <div className="ts-modal-overlay" onClick={onClose}>
      <div className="ts-modal cascade-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ts-modal-head">
          <div>
            <h3>Cascade map <span className="trading-muted">{displayName || blenderCode}</span></h3>
            <div className="trading-muted ts-modal-sub">
              {blenderCode} — subs inherit the master exactly, except where a recorded exception says otherwise
            </div>
          </div>
          <button className="trading-x ts-close" onClick={onClose}>✕</button>
        </div>
        <div className="ts-modal-body">
          <CascadeBody bc={blenderCode} />
        </div>
      </div>
    </div>
  );
}

/* ═══════════════ Pending approvals (maker-checker): review + approve/reject ═══════════════ */
function PendingApprovals({ onCount, onOpenSuite }) {
  const [pending, setPending] = useState([]);
  const [selfApproval, setSelfApproval] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const refresh = useCallback(() => {
    tradingAPI.listPendingApprovals().then((d) => {
      setPending(d.pending || []);
      setSelfApproval(!!d.self_approval_enabled);
      if (onCount) onCount((d.pending || []).length);
    }).catch(() => setPending([]));
  }, [onCount]);

  useEffect(() => { refresh(); }, [refresh]);

  const open = (id) => {
    setOpenId(id); setDetail(null); setNote(''); setMsg(null);
    tradingAPI.getTargetSetCompliance(id).then(setDetail).catch(() => setDetail(null));
  };

  const decide = async (id, decision) => {
    setBusy(true); setMsg(null);
    try {
      await tradingAPI.approvalDecision(id, decision, note);
      setMsg({ ok: true, text: decision === 'approve' ? 'Approved & formalised'
        : decision === 'withdraw' ? 'Withdrawn — returned to draft' : 'Rejected — returned to author' });
      setOpenId(null); setDetail(null); refresh();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Decision failed' });
    } finally { setBusy(false); }
  };

  const [selIds, setSelIds] = useState(new Set());
  const toggleSel = (id) => setSelIds((prev) => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const batchDecide = async (decision) => {
    if (!selIds.size) return;
    if (!window.confirm(`${decision === 'approve' ? 'Approve' : 'Reject'} ${selIds.size} submission(s)?`)) return;
    setBusy(true); setMsg(null);
    const fails = [];
    // process OLDEST versions first so a newer approval always supersedes an older one
    const ordered = [...selIds].sort((a, b) => {
      const pa = pending.find((x) => x.target_set_id === a) || {};
      const pb = pending.find((x) => x.target_set_id === b) || {};
      return (pa.blender_code || '').localeCompare(pb.blender_code || '') || (pa.version || 0) - (pb.version || 0);
    });
    for (const id of ordered) {
      try { await tradingAPI.approvalDecision(id, decision, note); }
      catch (e) {
        const p = pending.find((x) => x.target_set_id === id);
        fails.push(`${p ? p.blender_code : id}: ${e?.response?.data?.detail || 'failed'}`);
      }
    }
    setBusy(false); setSelIds(new Set()); refresh();
    setMsg({ ok: fails.length === 0,
      text: `${decision === 'approve' ? 'Approved' : 'Rejected'} ${selIds.size - fails.length}/${selIds.size}`
        + (fails.length ? ` — failed: ${fails.join(' · ')}` : '') });
  };

  return (
    <>
      <header className="trading-header"><div><h2>Pending approvals</h2>
        <div className="trading-header-sub">
          <span className="trading-muted">{pending.length} awaiting a checker</span>
          {selIds.size > 0 && (
            <>
              <span className="trading-muted">· {selIds.size} selected</span>
              <button className="trading-btn primary sm" disabled={busy} onClick={() => batchDecide('approve')}>Approve selected</button>
              <button className="trading-btn sm" disabled={busy} onClick={() => batchDecide('reject')}>Reject selected</button>
            </>
          )}
        </div></div></header>
      {msg && <div className={`trading-save-msg ${msg.ok ? 'ok' : 'err'}`}>{msg.text}</div>}
      {!pending.length ? <div className="trading-muted">Nothing awaiting approval.</div> : (
        <div className="approvals-list">
          {pending.map((p) => (
            <div key={p.target_set_id} className="approval-card">
              <div className="approval-row" onClick={() => (openId === p.target_set_id ? setOpenId(null) : open(p.target_set_id))}>
                <input type="checkbox" checked={selIds.has(p.target_set_id)}
                  disabled={p.is_own && !selfApproval}
                  onClick={(e) => e.stopPropagation()} onChange={() => toggleSel(p.target_set_id)} />
                <span className="approval-name">{p.display_name || p.blender_code} <span className="trading-muted">v{p.version}</span></span>
                <span className="approval-code trading-code" title="Blender code">{p.blender_code}</span>
                <span className="trading-muted">by {p.author_email}</span>
                <EffectiveDateBadge date={p.effective_date} />
                <span className="approval-flags">
                  {p.hard_fail > 0 && <span className="compliance-badge fail">✗ {p.hard_fail}</span>}
                  {p.soft_flag > 0 && <span className="compliance-badge flag">⚠ {p.soft_flag}</span>}
                  {!p.hard_fail && !p.soft_flag && <span className="compliance-badge pass">✓ clear</span>}
                </span>
                {p.suite && onOpenSuite && (
                  <button className="trading-link approval-suite-link"
                    title={`Open the ${p.suite} suite to review and approve this in its wider series context`}
                    onClick={(e) => { e.stopPropagation(); onOpenSuite(p.suite); }}>↗ Series view</button>
                )}
                <span className="approval-toggle">{openId === p.target_set_id ? '▾' : '▸'}</span>
              </div>
              {openId === p.target_set_id && detail && (
                <div className="approval-detail">
                  {p.effective_date && (
                    <div className="eff-callout">
                      <EffectiveDateBadge date={p.effective_date} />
                      <span className="eff-callout-text">
                        Once approved, these targets are <strong>held</strong> until {p.effective_date}.
                        They will not update the Dashboard, Portfolio Viewer or Lab until that date is reached.
                      </span>
                    </div>
                  )}
                  <div className="ad-section">
                    <h5>Compliance</h5>
                    {(detail.results || []).filter((r) => r.status !== 'pass').length === 0
                      ? <div className="trading-muted">All checks pass.</div>
                      : detail.results.filter((r) => r.status !== 'pass').map((r, i) => (
                        <div key={i} className={`cr-item ${r.status}`}>
                          <span className={`cr-tag ${r.status}`}>{r.status === 'fail' ? 'HARD' : 'FLAG'}</span>
                          <span className="cr-detail">{r.detail}</span>
                          {r.override_reason && <span className="ad-reason">reason: {r.override_reason}</span>}
                        </div>
                      ))}
                  </div>
                  <div className="ad-section">
                    <h5>
                      Portfolio — proposed v{detail.version}
                      {detail.current_version != null && <span className="trading-muted"> vs approved v{detail.current_version}</span>}
                    </h5>
                    {(() => {
                      const groups = new Map();
                      (detail.lines || []).forEach((l) => {
                        const g = l.asset_class_group || 'Other';
                        if (!groups.has(g)) groups.set(g, []);
                        groups.get(g).push(l);
                      });
                      const delta = (l) => (l.current_pct == null ? null : l.target_pct - l.current_pct);
                      const fmtD = (d) => (d == null ? 'new' : d === 0 ? '·' : `${d > 0 ? '+' : ''}${fmt(d, 2)}`);
                      let totP = 0, totC = 0;
                      (detail.lines || []).forEach((l) => { totP += l.target_pct || 0; totC += l.current_pct || 0; });
                      return (
                        <table className="ad-portfolio">
                          <thead><tr><th className="l">Fund</th><th className="l">Code</th>
                            <th>Current</th><th>Proposed</th><th>Δ</th></tr></thead>
                          <tbody>
                            {[...groups.entries()].map(([g, ls]) => (
                              <React.Fragment key={g}>
                                <tr className="ad-group">
                                  <td className="l" colSpan={2}>{g}</td>
                                  <td>{fmt(ls.reduce((s, l) => s + (l.current_pct || 0), 0), 1)}</td>
                                  <td>{fmt(ls.reduce((s, l) => s + (l.target_pct || 0), 0), 1)}</td>
                                  <td />
                                </tr>
                                {ls.map((l, i) => {
                                  const d = delta(l);
                                  return (
                                    <tr key={i} className={l.removed ? 'ad-removed' : d ? 'ad-changed' : ''}>
                                      <td className="l">{l.fund_name || l.apir_code}{l.removed ? ' (removed)' : ''}</td>
                                      <td className="l trading-code">{l.apir_code || l.position_key}</td>
                                      <td>{l.current_pct == null ? '—' : fmt(l.current_pct, 2)}</td>
                                      <td>{fmt(l.target_pct, 2)}</td>
                                      <td className={d > 0 ? 'ad-up' : d < 0 ? 'ad-down' : ''}>{fmtD(d)}</td>
                                    </tr>
                                  );
                                })}
                              </React.Fragment>
                            ))}
                            <tr className="ad-total"><td className="l" colSpan={2}>Total</td>
                              <td>{fmt(totC, 1)}</td><td>{fmt(totP, 1)}</td><td /></tr>
                          </tbody>
                        </table>
                      );
                    })()}
                  </div>
                  <div className="ad-actions">
                    {p.suite && onOpenSuite && (
                      <button className="trading-btn sm" title={`Open the ${p.suite} suite to review this alongside the rest of the series`}
                        onClick={() => onOpenSuite(p.suite)}>↗ Series view</button>
                    )}
                    <input className="cr-reason" placeholder="Note (optional)…" value={note} onChange={(e) => setNote(e.target.value)} />
                    <button className="trading-btn sm" disabled={busy} title="Return to draft without a checker decision"
                      onClick={() => decide(p.target_set_id, 'withdraw')}>Withdraw</button>
                    {p.is_own && !selfApproval ? (
                      <span className="trading-muted">You authored this — another checker must approve.</span>
                    ) : (
                      <>
                        {p.is_own && selfApproval && <span className="veh-newtag" title="ALLOW_SELF_APPROVAL is on in trading/api.py">self-approval (testing)</span>}
                        <button className="trading-btn sm" disabled={busy} onClick={() => decide(p.target_set_id, 'reject')}>Reject</button>
                        <button className="trading-btn primary sm" disabled={busy} onClick={() => decide(p.target_set_id, 'approve')}>Approve</button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/* ═══════════════ Mandate checks: the Portfolio Viewer "Mandate Check" sheet, live per suite ═══════════════ */
const pct0 = (v) => (v == null || isNaN(v) ? '—' : Number(v).toFixed(2).replace(/\.?0+$/, ''));

// "lo%–hi%" from a cell's effective rule bounds, the way the sheet writes it. A bucket rule
// with no lower bound reads as 0% (weights cannot be negative); other rule types show the
// bound they carry (threshold = tolerance / floor / cap depending on the type).
function mcBand(cell, code = 'sac_bucket') {
  if (!cell || (cell.lo == null && cell.hi == null)) return code === 'growth_band' ? 'registry growth band' : '—';
  if (code === 'sum_to_100') return `100% ± ${pct0(cell.hi)}%`;
  if (code === 'cash_floor' || code === 'min_position') return `≥ ${pct0(cell.hi)}%`;
  if (code === 'max_holdings') return `≤ ${pct0(cell.hi)} funds`;
  if (code !== 'sac_bucket' && code !== 'currency_band') return `≤ ${pct0(cell.hi)}%`;
  if (cell.hi == null) return `≥ ${pct0(cell.lo)}%`;
  return `${pct0(cell.lo == null ? 0 : cell.lo)}%–${pct0(cell.hi)}%`;
}

function McVerdict({ cell }) {
  if (!cell || cell.status == null) {
    return <span className="mc-norule" title="No mandate rule for this bucket on this portfolio — exposure shown, nothing enforced">—</span>;
  }
  const ok = cell.status === 'pass';
  const tip = cell.detail || (ok ? 'Within the mandate range' : 'Outside the mandate range');
  return <span className={`compliance-badge ${ok ? 'pass' : cell.status}`} title={tip}>{ok ? 'OK' : 'CHECK'}</span>;
}

function MandateChecks({ suites, initialSuite }) {
  const names = useMemo(() => suites.map((s) => s.name), [suites]);
  const [suiteSel, setSuiteSel] = useState('');
  const [basis, setBasis] = useState('approved');          // approved | proposed
  const [showUnseeded, setShowUnseeded] = useState(false);
  const [data, setData] = useState({});                    // {suiteName: payload}
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);                  // `${suite}::${pc}::${key}` while a tick saves
  const [copied, setCopied] = useState(null);
  const [msg, setMsg] = useState(null);

  // Default to the suite open in the sidebar (first of a multi-suite selection), else the first suite.
  useEffect(() => {
    if (suiteSel || !names.length) return;
    setSuiteSel(initialSuite && names.includes(initialSuite) ? initialSuite : names[0]);
  }, [names, suiteSel, initialSuite]);

  const targets = useMemo(() => (suiteSel === 'ALL' ? names : (suiteSel ? [suiteSel] : [])), [suiteSel, names]);
  const targetKey = targets.join('|');
  useEffect(() => {
    if (!targets.length) return undefined;
    let alive = true;
    setLoading(true); setErr(null);
    Promise.all(targets.map((s) => tradingAPI.getMandateChecks(s, basis).then((d) => [s, d])))
      .then((pairs) => { if (alive) setData(Object.fromEntries(pairs)); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || e.message || 'Failed to load mandate checks'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [targetKey, basis]);   // eslint-disable-line react-hooks/exhaustive-deps

  const visibleCols = (d) => (d.columns || []).filter((c) => showUnseeded || c.seeded);

  // A sheet row belongs in the main (enforced) table when at least one visible portfolio
  // enforces it; the rest are not portal mandates and go to the "Not enforced" table below.
  const splitRows = (d, cols) => {
    const ruled = [], unruled = [];
    (d.rows || []).forEach((r) => {
      (cols.some((c) => r.cells[c.portfolio_code]?.rule_id != null) ? ruled : unruled).push(r);
    });
    return { ruled, unruled };
  };

  const toggleTick = async (suite, col, key, current) => {
    if (!col.target_set_id) return;
    const k = `${suite}::${col.portfolio_code}::${key}`;
    setBusy(k); setMsg(null);
    try {
      const r = await tradingAPI.setMandateAttestation({
        portfolio_code: col.portfolio_code, check_key: key, target_set_id: col.target_set_id, ticked: !current,
      });
      setData((prev) => {
        const d = prev[suite];
        if (!d) return prev;
        const att = { ...(d.attestations || {}) };
        const forPc = { ...(att[col.portfolio_code] || {}) };
        if (r.ticked) forPc[key] = { ticked: true, by: r.by, at: r.at, target_set_id: r.target_set_id, note: r.note };
        else delete forPc[key];
        att[col.portfolio_code] = forPc;
        return { ...prev, [suite]: { ...d, attestations: att } };
      });
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Tick failed' });
    } finally {
      setBusy(null);
    }
  };

  // The block as a flat list of rows — one source for both clipboard outputs (HTML table and
  // slide PNG). kind: head | label | range | manual | standing | section | plain.
  // cells: [{ text, verdict }] with verdict ∈ 'OK' | 'CHECK' | null.
  // The main table is STRICTLY what the portal enforces: the enforced sheet rows, then every
  // other enforced rule. ``includeAdditional`` appends the "Not enforced in the portal"
  // table — sheet rows with no rule (exposure only), the manual Max Issuer tick and the
  // standing Illiquid OK; the PowerPoint copy leaves it out.
  const buildBlockRows = (d, cols, includeAdditional = true) => {
    const ver = (c) => (!c || c.status == null ? null : c.status === 'pass' ? 'OK' : 'CHECK');
    const { ruled, unruled } = splitRows(d, cols);
    const out = [];
    out.push({ kind: 'head', label: 'Mandate Range Check', cells: cols.map((c) => ({ text: c.display_name })) });
    ruled.forEach((r) => {
      out.push({ kind: 'label', label: r.label, cells: cols.map((c) => {
        const cell = r.cells[c.portfolio_code]; const v = ver(cell);
        return { text: `${v || '—'} ${fmt(cell?.value, 1)}%`, verdict: v };
      }) });
      out.push({ kind: 'range', label: 'Mandate Range', cells: cols.map((c) => ({ text: mcBand(r.cells[c.portfolio_code]) })) });
    });
    // every other enforced rule, straight under the sheet rows — all portal-enforced
    (d.other_rows || []).forEach((r) => {
      out.push({ kind: 'label', label: r.label, cells: cols.map((c) => {
        const cell = r.cells[c.portfolio_code];
        if (!cell) return { text: '—', verdict: null };
        const v = cell.status == null ? null : cell.status === 'pass' ? 'OK' : 'CHECK';
        const val = cell.value == null ? '' : ` ${fmt(cell.value, 1)}${r.code === 'max_holdings' ? '' : '%'}`;
        return { text: `${v || '—'}${val}`, verdict: v };
      }) });
      out.push({ kind: 'range', label: 'Mandate Range', cells: cols.map((c) => ({ text: mcBand(r.cells[c.portfolio_code], r.code) })) });
    });
    if (includeAdditional) {
      out.push({ kind: 'section', label: 'Not enforced in the portal — manual and standing checks, and sheet rows with no rule (exposure only)', cells: [] });
      unruled.forEach((r) => {
        out.push({ kind: 'plain', label: r.label, cells: cols.map((c) => ({ text: `${fmt(r.cells[c.portfolio_code]?.value, 1)}%` })) });
      });
      (d.manual_checks || []).forEach((mc) => {
        out.push({ kind: 'manual', label: `${mc.label} (manual tick)`, cells: cols.map((c) => {
          const t = (d.attestations?.[c.portfolio_code] || {})[mc.key];
          return t ? { text: `OK ${t.at}`, verdict: 'OK' } : { text: '—', verdict: null };
        }) });
      });
      out.push({ kind: 'standing', label: `${d.illiquid.label} (standing)`, cells: cols.map(() => ({ text: 'OK', verdict: 'OK' })) });
    }
    return out;
  };
  const escHtml = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const verdictCss = (v) => (v === 'CHECK' ? 'color:#F26B43;font-weight:700;' : v === 'OK' ? 'color:#12706C;font-weight:700;' : '');
  const basisLabel = () => (basis === 'approved' ? 'Approved targets' : 'Proposed targets (live draft / pending where one exists, else approved)');

  // Inline-styled HTML (+ tab-separated plain text) so the block pastes into Excel / Word / Outlook.
  const copyBlock = async (suite) => {
    const d = data[suite];
    if (!d) return;
    const cols = visibleCols(d);
    const rows = buildBlockRows(d, cols);
    const th = 'padding:4px 8px;border:1px solid #D9DDE3;background:#012341;color:#fff;font-weight:600;text-align:center;';
    const tdL = 'padding:4px 8px;border:1px solid #D9DDE3;font-weight:600;background:#F2F3F2;text-align:left;';
    const tdR = 'padding:4px 8px;border:1px solid #D9DDE3;text-align:left;color:#3F4A59;';
    const tdC = 'padding:4px 8px;border:1px solid #D9DDE3;text-align:center;';
    const rowsH = rows.map((r) => {
      if (r.kind === 'head') {
        return `<tr><th style="${th}text-align:left">${escHtml(r.label)} — ${escHtml(suite)}</th>${r.cells.map((c) => `<th style="${th}">${escHtml(c.text)}</th>`).join('')}</tr>`;
      }
      if (r.kind === 'section') {
        return `<tr><td colspan="${cols.length + 1}" style="${tdR}background:#EDF5F4;font-weight:600">${escHtml(r.label)}</td></tr>`;
      }
      const lab = r.kind === 'range' ? tdR : tdL;
      return `<tr><td style="${lab}">${escHtml(r.label)}</td>${r.cells.map((c) => `<td style="${tdC}${verdictCss(c.verdict)}">${escHtml(c.text)}</td>`).join('')}</tr>`;
    });
    const lines = rows.map((r) => [r.kind === 'head' ? `${r.label} — ${suite}` : r.label, ...r.cells.map((c) => c.text)].join('\t'));
    const html = `<table style="border-collapse:collapse;font-family:Calibre,Calibri,Arial,sans-serif;font-size:10pt">${rowsH.join('')}</table>`;
    const ok = await copyHtml(html, lines.join('\n'));
    setCopied(ok ? suite : null);
    setTimeout(() => setCopied(null), 1800);
  };

  // Slide-sized PNG on the clipboard: laid out at 12.3in wide (fits a 16:9 slide with the
  // usual margins), rasterised at 2× and stamped 192 DPI so PowerPoint pastes it at exactly
  // that width. Falls back to downloading the PNG where the browser cannot copy images.
  const SLIDE_WIDTH_IN = 12.3;
  const copySlide = async (suite) => {
    const d = data[suite];
    if (!d) return;
    const cols = visibleCols(d);
    const rows = buildBlockRows(d, cols, false);   // enforced table only — the "Not enforced" table is left out
    const title = `Mandate Range Check — ${suite}`;
    const foot = 'Every row is an enforced pre-trade compliance rule; the band is the rule\'s range. Manual and standing checks are not shown.';
    const W = Math.round(SLIDE_WIDTH_IN * 96);
    const font = "font-family:'Calibre',Calibri,Arial,sans-serif;";
    const cell = 'padding:4px 8px;border:1px solid #D9DDE3;text-align:center;';
    const th = `${cell}background:#012341;color:#fff;font-weight:600;font-size:11pt;white-space:normal;`;
    const tdL = `${cell}text-align:left;font-weight:600;background:#F2F3F2;color:#012341;white-space:nowrap;`;
    const tdR = `${cell}text-align:left;color:#3F4A59;background:#fff;font-size:9.5pt;`;
    const tdRange = `${cell}color:#3F4A59;background:#fff;font-size:9.5pt;`;
    const rowsH = rows.map((r) => {
      if (r.kind === 'head') {
        return `<tr><th style="${th}text-align:left">${escHtml(r.label)}</th>${r.cells.map((c) => `<th style="${th}">${escHtml(c.text)}</th>`).join('')}</tr>`;
      }
      if (r.kind === 'section') {
        return `<tr><td colspan="${cols.length + 1}" style="${tdR}background:#EDF5F4;font-weight:600;font-size:9.5pt">${escHtml(r.label)}</td></tr>`;
      }
      if (r.kind === 'range') {
        return `<tr><td style="${tdR}">${escHtml(r.label)}</td>${r.cells.map((c) => `<td style="${tdRange}">${escHtml(c.text)}</td>`).join('')}</tr>`;
      }
      return `<tr><td style="${tdL}">${escHtml(r.label)}</td>${r.cells.map((c) => `<td style="${cell}${verdictCss(c.verdict)}">${escHtml(c.text)}</td>`).join('')}</tr>`;
    });
    const today = new Date().toISOString().slice(0, 10);
    const html = `<div style="width:${W}px;box-sizing:border-box;padding:12px 14px;background:#fff;${font}color:#231F20">`
      + `<div style="font-weight:300;font-size:20pt;color:#012341;margin:0 0 4px">${escHtml(title)}</div>`
      + `<div style="font-size:9pt;color:#7F7F7F;margin:0 0 10px">${escHtml(basisLabel())} · Drummond Portal, Portfolio Trading · ${today}</div>`
      + `<table style="border-collapse:collapse;width:100%;font-size:10.5pt">${rowsH.join('')}</table>`
      + `<div style="font-size:8pt;color:#7F7F7F;margin-top:8px">${escHtml(foot)}</div>`
      + '</div>';
    setMsg(null);
    try {
      const { blob } = await htmlToPngBlob(html, { scale: 2 });
      const stamped = await withPngDpi(blob, 192);
      const fname = `mandate-check-${suite.replace(/[^\w-]+/g, '_')}-${today}.png`;
      const how = await copyPngToClipboard(stamped, fname);
      setCopied(`${suite}::slide::${how}`);
      setTimeout(() => setCopied(null), 2200);
    } catch (e) {
      setMsg({ ok: false, text: e?.message || 'Could not build the slide image' });
    }
  };

  const renderBlock = (suite) => {
    const d = data[suite];
    if (!d) return null;
    const cols = visibleCols(d);
    const { ruled, unruled } = splitRows(d, cols);
    const hasRules = ruled.length > 0;
    // The PowerPoint copy is held until every manual check (Max Issuer 35%) is ticked for
    // every portfolio that has a target set to tick against.
    const unconfirmed = cols.filter((c) => c.target_set_id
      && (d.manual_checks || []).some((mc) => !((d.attestations?.[c.portfolio_code] || {})[mc.key])));
    const exportBlocked = unconfirmed.length > 0;
    return (
      <div className="mc-block" key={suite}>
        <div className="mc-block-head">
          <h3>{suite}</h3>
          <span className="trading-muted">
            {basis === 'approved' ? 'Approved targets' : 'Proposed — live draft / pending where one exists, else approved'}
          </span>
          <button className="trading-btn sm" onClick={() => copyBlock(suite)}
            title="Copy this block as a table — pastes into Excel, Word or Outlook">
            {copied === suite ? '✓ Copied' : '⧉ Copy table'}
          </button>
          <button className="trading-btn sm" disabled={exportBlocked} onClick={() => copySlide(suite)}
            title={exportBlocked
              ? 'Tick Max Issuer (35%) for every portfolio first — the PowerPoint copy is held until then'
              : `Copy this block as a picture sized for a 16:9 slide (${SLIDE_WIDTH_IN}in wide, 192 dpi) — paste straight into PowerPoint`}>
            {copied === `${suite}::slide::clipboard` ? '✓ Copied as picture'
              : copied === `${suite}::slide::download` ? '✓ Downloaded PNG' : '▣ Copy for PowerPoint'}
          </button>
          {exportBlocked && (
            <span className="mc-export-block"
              title="Confirm the Max Issuer (35%) check (tick the cell) for each portfolio listed; the export unlocks when all are ticked">
              ⚠ Confirm Max Issuer (35%) before exporting — not yet ticked: {unconfirmed.map((c) => c.display_name).join(', ')}
            </span>
          )}
        </div>
        {!hasRules && (
          <div className="mc-banner">No asset-class bucket rules are configured for this suite — the eight asset-class rows show
            exposure only. Add sub-asset-class bucket rules in ⚖ Compliance rules.</div>
        )}
        {!cols.length ? <div className="trading-muted">No portfolios with targets to show.</div> : (
          <div className="mc-scroll">
            <table className="mc-table">
              <thead>
                <tr>
                  <th className="mc-label">Mandate Range Check</th>
                  {cols.map((c) => (
                    <th key={c.portfolio_code}
                      title={`${c.blender_code} · ${c.target_source || 'no target'}${c.as_at ? ` · ${c.as_at}` : ''}`}>
                      {c.display_name}
                      <div className="mc-sub">{c.target_source || 'no target'}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ruled.map((r) => (
                  <React.Fragment key={r.key}>
                    <tr className="mc-row-label">
                      <td className="mc-label" title={r.sacs.join(', ')}>{r.label}</td>
                      {cols.map((c) => {
                        const cell = r.cells[c.portfolio_code];
                        return (
                          <td key={c.portfolio_code}>
                            <McVerdict cell={cell} /><span className="mc-val">{fmt(cell?.value, 1)}%</span>
                          </td>
                        );
                      })}
                    </tr>
                    <tr className="mc-row-range">
                      <td className="mc-label">Mandate Range</td>
                      {cols.map((c) => <td key={c.portfolio_code}>{mcBand(r.cells[c.portfolio_code])}</td>)}
                    </tr>
                  </React.Fragment>
                ))}
                {(d.other_rows || []).map((r) => (
                  <React.Fragment key={r.key}>
                    <tr className="mc-row-label">
                      <td className="mc-label" title="Enforced rule on these portfolios that is not one of the sheet's asset-class rows">{r.label}</td>
                      {cols.map((c) => {
                        const cell = r.cells[c.portfolio_code];
                        return (
                          <td key={c.portfolio_code}>
                            {cell ? (
                              <>
                                <McVerdict cell={cell} />
                                {cell.value != null && (
                                  <span className="mc-val">{fmt(cell.value, 1)}{r.code === 'max_holdings' ? '' : '%'}</span>
                                )}
                              </>
                            ) : <span className="mc-norule" title="Rule not scoped to this portfolio">—</span>}
                          </td>
                        );
                      })}
                    </tr>
                    <tr className="mc-row-range">
                      <td className="mc-label">Mandate Range</td>
                      {cols.map((c) => <td key={c.portfolio_code}>{mcBand(r.cells[c.portfolio_code], r.code)}</td>)}
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {cols.length > 0 && (
          <div className="mc-additional">
            <h4 className="mc-additional-head"
              title="Nothing here is enforced by a compliance rule: the manual Max Issuer tick, the standing Illiquid OK, and any sheet row with no rule in this suite (exposure only)">
              Not enforced in the portal <span className="trading-muted">— manual and standing checks, and sheet rows with no rule (exposure only)</span>
            </h4>
            <div className="mc-scroll">
              <table className="mc-table">
                <thead>
                  <tr><th className="mc-label">Check</th>{cols.map((c) => <th key={c.portfolio_code}>{c.display_name}</th>)}</tr>
                </thead>
                <tbody>
                  {unruled.map((r) => (
                    <tr className="mc-row-plain" key={r.key}>
                      <td className="mc-label" title={`Sheet row with no rule on any portfolio in this suite — exposure only. SACs: ${r.sacs.join(', ')}`}>{r.label}</td>
                      {cols.map((c) => <td key={c.portfolio_code}>{fmt(r.cells[c.portfolio_code]?.value, 1)}%</td>)}
                    </tr>
                  ))}
                  {(d.manual_checks || []).map((mc) => (
                    <tr className="mc-row-label mc-row-manual" key={mc.key}>
                      <td className="mc-label"
                        title="Manual check — no issuer data exists on fund records. The tick is keyed to the target set shown and clears when a newer set is approved.">
                        {mc.label} <span className="mc-standing">manual tick</span>
                      </td>
                      {cols.map((c) => {
                        const a = (d.attestations?.[c.portfolio_code] || {})[mc.key];
                        const k = `${suite}::${c.portfolio_code}::${mc.key}`;
                        if (!c.target_set_id) {
                          return <td key={c.portfolio_code}><span className="mc-norule" title="No target set to tick against">—</span></td>;
                        }
                        return (
                          <td key={c.portfolio_code}>
                            <label className="mc-tick"
                              title={a ? `Ticked on ${a.at} against ${c.target_source} — untick to clear` : `Tick to confirm for ${c.target_source}`}>
                              <input type="checkbox" checked={!!a} disabled={busy === k}
                                onChange={() => toggleTick(suite, c, mc.key, !!a)} />
                              {a ? <><span className="compliance-badge pass">OK</span><span className="mc-tick-by">{a.at}</span></>
                                : <span className="trading-muted">confirm</span>}
                            </label>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  <tr className="mc-row-label">
                    <td className="mc-label" title={d.illiquid.note}>{d.illiquid.label} <span className="mc-standing">standing</span></td>
                    {cols.map((c) => (
                      <td key={c.portfolio_code}>
                        <span className="compliance-badge pass" title={d.illiquid.note}>OK</span>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <header className="trading-header"><div><h2>Mandate checks</h2>
        <div className="trading-header-sub"><span className="trading-muted">
          The Portfolio Viewer "Mandate Check" sheet, live per suite — evaluated by the same rules and engine as the
          Series tab's pre-trade gate, so a CHECK here is a block there
        </span></div></div></header>
      {msg && <div className={`trading-save-msg ${msg.ok ? 'ok' : 'err'}`}>{msg.text}</div>}
      <div className="mc-controls">
        <select value={suiteSel} onChange={(e) => setSuiteSel(e.target.value)} title="Suite to check — or every suite stacked">
          {names.map((n) => <option key={n} value={n}>{n}</option>)}
          <option value="ALL">All suites</option>
        </select>
        <div className="rules-scope-tabs"
          title="Approved = the standing instruction. Proposed = the live draft / pending set where one exists, else approved.">
          <button className={basis === 'approved' ? 'active' : ''} onClick={() => setBasis('approved')}>Approved</button>
          <button className={basis === 'proposed' ? 'active' : ''} onClick={() => setBasis('proposed')}>Proposed</button>
        </div>
        <label className="mc-toggle">
          <input type="checkbox" checked={showUnseeded} onChange={(e) => setShowUnseeded(e.target.checked)} />
          show portfolios without targets
        </label>
        {loading && <span className="trading-muted">Loading…</span>}
      </div>
      {err && <div className="trading-save-msg err">{err}</div>}
      {targets.map((s) => renderBlock(s))}
    </>
  );
}

/* ═══════════════ App shell: suites sidebar → suite view / strategy drill ═══════════════ */
export default function TradingApp() {
  const [suites, setSuites] = useState([]);
  const [loadingSuites, setLoadingSuites] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedSuite, setSelectedSuite] = useState(null);
  const [drill, setDrill] = useState(null);   // { blenderCode, displayName }
  const [showApprovals, setShowApprovals] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [showMandate, setShowMandate] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [showCascade, setShowCascade] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showSheets, setShowSheets] = useState(false);
  const [showInstruments, setShowInstruments] = useState(false);
  const [showCosts, setShowCosts] = useState(false);
  const [showUnitMap, setShowUnitMap] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingSuites, setPendingSuites] = useState(() => new Set());
  const [auditInit, setAuditInit] = useState(null);     // {tab, bc} deep-link into audit
  const [cascadeInit, setCascadeInit] = useState(null); // bc deep-link into cascade map
  const dirtyRef = React.useRef(false);                 // SuiteView reports unsaved edits here
  const guardNav = () => !dirtyRef.current
    || window.confirm('You have unsaved proposed changes — discard them and navigate away?');

  // ── In-app navigation is pushed into browser history so Back walks drill → suite →
  //    panel steps instead of leaving the app. viewRef mirrors the applied view.
  const viewRef = React.useRef({ suite: null, drill: null, panel: null });
  const applyView = useCallback((v) => {
    setShowApprovals(v.panel === 'approvals');
    setShowRules(v.panel === 'rules');
    setShowMandate(v.panel === 'mandate');
    setShowAudit(v.panel === 'audit');
    setShowCascade(v.panel === 'cascade');
    setShowUpload(v.panel === 'upload');
    setShowSheets(v.panel === 'sheets');
    setShowInstruments(v.panel === 'instruments');
    setShowCosts(v.panel === 'costs');
    setShowUnitMap(v.panel === 'unitMap');
    setShowReport(v.panel === 'report');
    setShowGuide(v.panel === 'guide');
    setAuditInit(v.auditInit || null);
    setCascadeInit(v.cascadeInit || null);
    setSelectedSuite(v.suite || null);
    setDrill(v.drill || null);
    viewRef.current = v;
  }, []);
  const navTo = (v) => {
    if (!guardNav()) return;
    applyView(v);
    try { window.history.pushState({ tradingView: v }, ''); } catch (e) { /* ignore */ }
  };
  const togglePanel = (name) => navTo(
    viewRef.current.panel === name
      ? { suite: selectedSuite, drill: null, panel: null }
      : { suite: selectedSuite, drill: null, panel: name });

  useEffect(() => {
    try { window.history.replaceState({ tradingView: viewRef.current }, ''); } catch (e) { /* ignore */ }
    const h = (e) => {
      const v = e.state && e.state.tradingView;
      if (!v) return;   // leaving the app — let the router take it
      if (dirtyRef.current && !window.confirm('You have unsaved proposed changes — discard them and navigate away?')) {
        try { window.history.pushState({ tradingView: viewRef.current }, ''); } catch (e2) { /* ignore */ }
        return;
      }
      applyView(v);
    };
    window.addEventListener('popstate', h);
    return () => window.removeEventListener('popstate', h);
  }, [applyView]);

  const refreshPending = useCallback(() => {
    tradingAPI.listPendingApprovals().then((d) => {
      const pend = d.pending || [];
      setPendingCount(pend.length);
      // Suites with at least one portfolio awaiting approval — flagged with a star
      // in the sidebar. `suite` is null for registry-suiteless portfolios ("Other").
      setPendingSuites(new Set(pend.map((p) => p.suite || 'Other')));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    tradingAPI.listSuites().then((d) => setSuites(d.suites || [])).catch(() => setSuites([])).finally(() => setLoadingSuites(false));
    refreshPending();
    const t = setInterval(refreshPending, 60000);   // keep the badge trustworthy
    return () => clearInterval(t);
  }, [refreshPending]);

  const openEvolution = (bc) => navTo({ suite: selectedSuite, drill: null, panel: 'audit', auditInit: { tab: 'evolution', bc } });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return suites;
    return suites.filter((s) => (s.name || '').toLowerCase().includes(q)
      || (s.clients || []).some((c) => c.toLowerCase().includes(q)));
  }, [suites, search]);

  // Multi-suite side-by-side: the suite key is a comma-joined list of suite names (the
  // backend concatenates their portfolios). Plain click = just that suite; Ctrl/Cmd-click
  // or the checkbox toggles the suite in/out of the combination, in click order.
  const selSuites = useMemo(() => (selectedSuite || '').split(',').filter(Boolean), [selectedSuite]);
  const toggleSuiteInSel = (name) => {
    const next = selSuites.includes(name) ? selSuites.filter((n) => n !== name) : [...selSuites, name];
    navTo({ suite: next.join(',') || null, drill: null, panel: null });
  };

  return (
    <div className="trading-app">
      <aside className="trading-sidebar">
        <button className={`trading-approvals-nav${showApprovals ? ' active' : ''}`}
          title="Submissions awaiting a checker — review compliance and approve/reject"
          onClick={() => togglePanel('approvals')}>
          ⧉ Pending approvals{pendingCount ? <span className="approvals-count">{pendingCount}</span> : null}
        </button>
        <button className={`trading-approvals-nav${showRules ? ' active' : ''}`}
          title="Create and scope pre-trade compliance rules (global / platform / master / sub-portfolio)"
          onClick={() => togglePanel('rules')}>
          ⚖ Compliance rules
        </button>
        <button className={`trading-approvals-nav${showMandate ? ' active' : ''}`}
          title="Mandate range check per suite — the Portfolio Viewer sheet, live from the compliance rules"
          onClick={() => togglePanel('mandate')}>
          ☑ Mandate checks
        </button>
        <button className={`trading-approvals-nav${showAudit ? ' active' : ''}`}
          title="Every version ever created — timeline, evolution, comparisons"
          onClick={() => togglePanel('audit')}>
          🕘 Audit trail
        </button>
        <button className={`trading-approvals-nav${showCascade ? ' active' : ''}`}
          title="Master → sub-portfolio tree with recorded exceptions highlighted"
          onClick={() => togglePanel('cascade')}>
          ⛓ Cascade map
        </button>
        <button className={`trading-approvals-nav${showUpload ? ' active' : ''}`}
          title="Create draft targets for many strategies from one spreadsheet"
          onClick={() => togglePanel('upload')}>
          ⇪ Bulk upload
        </button>
        <button className={`trading-approvals-nav${showSheets ? ' active' : ''}`}
          title="Per-platform order files from approved targets, resolved per sub-portfolio"
          onClick={() => togglePanel('sheets')}>
          🧾 Generate trade sheets
        </button>
        <button className={`trading-approvals-nav${showInstruments ? ' active' : ''}`}
          title="Codes registered by hand (sub-SMAs, cash tokens, ETFs) that the platform lists do not carry"
          onClick={() => togglePanel('instruments')}>
          ⚙ Registered instruments
        </button>
        <button className={`trading-approvals-nav${showCosts ? ' active' : ''}`}
          title="Management Fees & Costs / rebate values plus the rebate agreement register, one row per fund"
          onClick={() => togglePanel('costs')}>
          % Management costs and rebates
        </button>
        <button className={`trading-approvals-nav${showUnitMap ? ' active' : ''}`}
          title="Per-platform default fund swaps: subs on a platform trade its own unit class automatically"
          onClick={() => togglePanel('unitMap')}>
          ⇄ Unit class mapping
        </button>
        <button className={`trading-approvals-nav${showReport ? ' active' : ''}`}
          title="Pasteable trade-changes table + post-change SAA positioning dots"
          onClick={() => togglePanel('report')}>
          📊 Charts &amp; tables
        </button>
        <button className={`trading-approvals-nav${showGuide ? ' active' : ''}`}
          title="How the trading app works — concepts, workflows and reference"
          onClick={() => togglePanel('guide')}>
          📖 User guide
        </button>
        <h3>Suites</h3>
        <input className="trading-search" placeholder="Search suite…" value={search} onChange={(e) => setSearch(e.target.value)} />
        {loadingSuites ? <div className="trading-muted">Loading…</div> : (
          <ul className="trading-strat-list">
            {filtered.map((s) => (
              <li key={s.name} className={`trading-strat-item${selSuites.includes(s.name) && !showApprovals ? ' active' : ''}`}
                  title="Click to open — Ctrl-click or tick to line suites up side by side"
                  onClick={(e) => (e.ctrlKey || e.metaKey)
                    ? toggleSuiteInSel(s.name)
                    : navTo({ suite: s.name, drill: null, panel: null })}>
                <div className="trading-strat-name">
                  <input type="checkbox" className="suite-check" checked={selSuites.includes(s.name)}
                    title="Show this suite alongside the others ticked"
                    onClick={(e) => e.stopPropagation()} onChange={() => toggleSuiteInSel(s.name)} />
                  {s.name}
                  {pendingSuites.has(s.name) && (
                    <span className="suite-pending-star"
                      title="A portfolio in this suite has changes awaiting approval">★</span>
                  )}
                </div>
                <div className="trading-strat-sub">
                  <span className="trading-badge cascade">{s.portfolio_count} portfolios</span>
                  {s.clients?.length > 0 && (
                    <span className="suite-client" title="Client / brand this tailored suite is granted to">
                      {s.clients.join(', ')}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </aside>

      <main className="trading-main">
        {showGuide ? (
          <UserGuide />
        ) : showReport ? (
          <ChangesReport suites={suites} />
        ) : showInstruments ? (
          <FundDataManager tab="instruments" />
        ) : showCosts ? (
          <FundDataManager tab="costs" />
        ) : showUnitMap ? (
          <UnitClassMapManager />
        ) : showSheets ? (
          <TradeSheets />
        ) : showUpload ? (
          <BulkUpload />
        ) : showCascade ? (
          <CascadeMap initialBc={cascadeInit} />
        ) : showAudit ? (
          <AuditTrail initial={auditInit} />
        ) : showRules ? (
          <ComplianceRulesManager />
        ) : showMandate ? (
          <MandateChecks suites={suites} initialSuite={selSuites[0] || null} />
        ) : showApprovals ? (
          <PendingApprovals onCount={setPendingCount}
            onOpenSuite={(suite) => navTo({ suite, drill: null, panel: null })} />
        ) : drill ? (
          <StrategyView blenderCode={drill.blenderCode} displayName={drill.displayName}
            onBack={() => navTo({ suite: selectedSuite, drill: null, panel: null })} />
        ) : selectedSuite ? (
          <SuiteView suite={selectedSuite}
            onDrill={(blenderCode, displayName) => navTo({ suite: selectedSuite, drill: { blenderCode, displayName }, panel: null })}
            onDirtyChange={(d) => { dirtyRef.current = d; }} onPendingChanged={refreshPending}
            onEvolution={openEvolution} />
        ) : (
          <div className="trading-placeholder">
            <h2>Portfolio Trading</h2>
            <p>Select a suite to list its portfolios across the top. Ctrl-click (or tick) suites to line
               several up side by side. Edit each portfolio's target weights, add funds, and click a
               portfolio header to drill into its platform vehicles &amp; overrides.</p>
          </div>
        )}
      </main>
    </div>
  );
}
